/* Warrior — external header links open in a new tab.
 *
 * Horizon renders menu links from the Shopify menu and does NOT add
 * target="_blank" for off-site URLs, so the Support link (an external Zendesk
 * help centre) replaced the storefront in the same tab. Losing the tab mid-session
 * costs the cart.
 *
 * Done in JS rather than Liquid because the menu markup is built by a private
 * vendor block, and this avoids a fourth hotspot on it. Scoped to the header so it
 * cannot alter links in page content, where an author's choice should stand.
 */
(function () {
  var header = document.querySelector('#header-component');
  if (!header) return;

  // Hosts that are OURS on a different subdomain. These are part of the session,
  // not destinations away from it, so they must stay in the same tab.
  //
  // Sign-In is the case that matters: it points at
  // account.teamwarrior.com/authentication/login, and opening a LOGIN flow in a new
  // tab is actively wrong — the visitor authenticates in tab B while tab A still
  // shows them signed out, and returning to the storefront means closing a tab and
  // reloading. Support is genuinely elsewhere (Zendesk) and should open away.
  var SAME_SESSION_HOSTS = ['account.teamwarrior.com', 'shop.app'];

  var links = header.querySelectorAll('a[href^="http"]');
  Array.prototype.forEach.call(links, function (a) {
    // Same-origin absolute URLs are internal — leave them alone.
    if (a.hostname === window.location.hostname) return;
    if (SAME_SESSION_HOSTS.indexOf(a.hostname) !== -1) return;
    a.target = '_blank';
    a.rel = a.rel ? a.rel + ' noopener' : 'noopener';
  });
})();

/* ---------------------------------------------------------------------------
   MEGA MENU — THE EMPTY HEADER BAR AROUND AN OPEN DROPDOWN IS PART OF IT

   The bug (screen recording 2026-09-30): the Shop panel snapped shut while the
   pointer was travelling from the panel back up towards "Shop".

   CAUSE. Horizon closes on `pointerleave` of the top-level <li>. Measured with
   elementFromPoint while open: everything BELOW the header row belongs to the <li>
   (the submenu is its child), but the empty header bar to the LEFT of and ABOVE
   "Shop" does not. A diagonal move from the range tiles up to the label clips that
   strip, and one pixel of it closes the menu instantly. The wider the window, the
   wider the strip, which is why it felt intermittent.

   FIX. While a dropdown is open, a leave INTO that dead space is held instead of
   acted on. The close happens when the pointer leaves the header altogether,
   lands on a control (another nav item, account, cart), or leaves the window.

   Done here rather than in `header-menu.js` because that is a vendor file
   (Rule 1). Horizon routes `on:pointerleave` through one capture listener on
   `document` (`assets/component.js`); a capture listener on `window` runs before it
   and can stop the event reaching it. Releasing re-dispatches a synthetic
   `pointerleave`, so the actual close is still Horizon's own code path.
   --------------------------------------------------------------------------- */
(function () {
  var header = document.querySelector('#header-component');
  if (!header) return;

  /** The open <li> whose close is being held, if any. */
  var held = null;

  function isOpenDropdown(li) {
    if (!(li instanceof Element) || !li.matches('header-menu .menu-list__list-item')) return false;
    // The More / overflow panel has its own leave handling in header-menu.js.
    if (li.slot === 'more' || li.slot === 'overflow') return false;
    var submenu = li.querySelector(':scope > [ref="submenu[]"]');
    return Boolean(submenu) && !submenu.inert;
  }

  // Dead space = inside the header but not on anything the pointer means to use.
  function isDeadSpace(el) {
    return (
      el instanceof Element &&
      header.contains(el) &&
      !el.closest('.menu-list__list-item, a, button, [role="button"], input, summary')
    );
  }

  function release(related) {
    var li = held;
    held = null;
    if (li && isOpenDropdown(li)) {
      li.dispatchEvent(new PointerEvent('pointerleave', { relatedTarget: related || null }));
    }
  }

  window.addEventListener(
    'pointerleave',
    function (event) {
      if (!event.isTrusted || event.pointerType === 'touch') return; // our own release, or touch
      var li = event.target;
      if (!isOpenDropdown(li) || !isDeadSpace(event.relatedTarget)) return;
      event.stopImmediatePropagation();
      held = li;
    },
    true
  );

  // `pointerover` bubbles, so one listener sees every element the pointer reaches.
  document.addEventListener('pointerover', function (event) {
    if (!held) return;
    var el = event.target;
    if (held.contains(el)) {
      held = null; // back in the item or its panel: it never closed, nothing to do
    } else if (!isDeadSpace(el)) {
      release(el);
    }
  });

  document.documentElement.addEventListener('pointerleave', function () {
    if (held) release(null);
  });
})();

/* ---------------------------------------------------------------------------
   TRUSTPILOT SKELETON

   Hides the loading placeholder once the real widget lands.

   WHY A MutationObserver AND NOT `script.onload`: the bootstrap script loading is
   not the same event as the widget being ready. `onload` fires when the JS file
   arrives; the widget then makes its own network call and injects an <iframe>
   some time later. Hiding the skeleton on `onload` would expose an empty box for
   the remainder of that second request — the exact gap the skeleton exists to
   cover.

   Observing for the <iframe> is the only signal that means "there is now
   something to look at".

   THE TIMEOUT IS NOT BELT-AND-BRACES, IT IS THE AD-BLOCKER PATH. Trustpilot is
   blocked by most tracker blockers, so for a real slice of visitors the iframe
   NEVER arrives. Without a timeout those users would stare at a shimmering
   skeleton forever, which reads as a broken page. After the cutoff we drop the
   skeleton and let Trustpilot's own <a> fallback show — a plain link to the
   profile, which is also what their terms require.
   --------------------------------------------------------------------------- */
(function () {
  var FALLBACK_MS = 8000;

  function init(frame) {
    var widget = frame.querySelector('[data-tp-widget]');
    var skeleton = frame.querySelector('[data-tp-skeleton]');
    var status = frame.querySelector('[data-tp-status]');
    if (!widget || !skeleton) return;

    var done = false;

    function reveal(loaded) {
      if (done) return;
      done = true;
      frame.setAttribute('data-tp-state', loaded ? 'loaded' : 'fallback');
      if (status) {
        // Emptying the live region rather than removing it: a removed node
        // cannot announce, and screen readers ignore an unchanged one.
        status.textContent = loaded ? '' : (status.getAttribute('data-fallback-text') || '');
      }
    }

    // Already present — the script can win the race on a warm cache.
    if (widget.querySelector('iframe')) {
      reveal(true);
      return;
    }

    var observer = new MutationObserver(function () {
      if (widget.querySelector('iframe')) {
        observer.disconnect();
        reveal(true);
      }
    });
    observer.observe(widget, { childList: true, subtree: true });

    window.setTimeout(function () {
      observer.disconnect();
      reveal(false);
    }, FALLBACK_MS);
  }

  function boot() {
    var frames = document.querySelectorAll('.custom-tp__frame');
    Array.prototype.forEach.call(frames, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init when a section is re-rendered, or the skeleton would
  // sit visible forever over a widget that already loaded.
  document.addEventListener('shopify:section:load', boot);
})();


/* ---------------------------------------------------------------------------
   FOOTER ACCORDION

   The columns are `<details open>` at every breakpoint, and collapse is done with a
   `data-collapsed` attribute rather than by removing `open`.

   WHY: a closed `<details>` hides its content in the browser's internal slot, and no CSS on
   the child can override that — `display: block` is ignored. So "force it open on desktop"
   is not achievable in CSS, which is why the desktop columns rendered empty while mobile
   worked. Horizon hits the same wall and solves it the same way (blocks/menu.liquid renders
   two headings and never closes the details).

   Keeping `open` also means the links are in the DOM for search and for a no-JS visitor. The
   previous version left three of four columns hidden to both.

   Below 750px the summary toggles the attribute. Above it, nothing happens — CSS ignores
   `data-collapsed` at desktop widths, so the columns are simply always open.
   --------------------------------------------------------------------------- */
(function () {
  var accs = document.querySelectorAll('.custom-wf__acc');
  if (!accs.length) return;

  Array.prototype.forEach.call(accs, function (acc) {
    var summary = acc.querySelector('.custom-wf__accbtn');
    if (!summary) return;

    summary.addEventListener('click', function (e) {
      // Only intercept on mobile. Above 750px the columns are always open and the summary
      // is inert (pointer-events: none), so this never fires there.
      if (window.innerWidth >= 750) {
        e.preventDefault();
        return;
      }
      // Prevent the native toggle: removing `open` would put the content back in the hidden
      // slot, which is the behaviour being avoided.
      e.preventDefault();
      if (acc.hasAttribute('data-collapsed')) {
        acc.removeAttribute('data-collapsed');
      } else {
        acc.setAttribute('data-collapsed', '');
      }
      summary.setAttribute('aria-expanded', acc.hasAttribute('data-collapsed') ? 'false' : 'true');
    });

    // Initial state for assistive tech. The visual state comes from the attribute.
    summary.setAttribute('aria-expanded', acc.hasAttribute('data-collapsed') ? 'false' : 'true');
  });
})();


/* ---------------------------------------------------------------------------
   LEGAL STUFF PANEL — click-away and close button

   A <details> does not close when you click elsewhere; that is dialog behaviour, not
   disclosure behaviour, and it has to be added. Without it the panel stays open behind
   whatever the visitor does next, which reads as a stuck menu.

   Three ways out, because a panel with only one is a trap:
     - the close button (top right)
     - clicking anywhere outside
     - Escape

   Escape is native to <details> in most engines but NOT all, so it is bound explicitly rather
   than assumed.
   --------------------------------------------------------------------------- */
(function () {
  var dd = document.querySelector('.custom-wf__legaldd');
  if (!dd) return;

  var close = function () { dd.removeAttribute('open'); };

  var btn = dd.querySelector('[data-legal-close]');
  if (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      close();
    });
  }

  // Click-away. Bound on `document` in the BUBBLE phase and guarded by `contains`, so a click
  // inside the panel (including on a link) is never treated as a click-away.
  document.addEventListener('click', function (e) {
    if (!dd.hasAttribute('open')) return;
    if (dd.contains(e.target)) return;
    close();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && dd.hasAttribute('open')) close();
  });
})();

/* ==========================================================================
   PDP — selected option value in the fieldset legend
   ==========================================================================
   The design labels each option row "FLAVOUR — MIXED BERRY". Horizon renders
   the selected value in the legend ONLY in `swatch` mode
   (`variant-main-picker.liquid:98`); in `buttons` mode the legend is the option
   name alone. That snippet is vendor code, so it cannot be edited (Rule 1) —
   the value is appended here instead.

   Progressive enhancement: with JS off the legend still reads "FLAVOUR", which
   is correct, just less informative. Nothing depends on this running.

   WHY THERE IS NO MutationObserver HERE.
   The first version of this observed the picker subtree and re-applied on
   childList changes. That is an infinite loop: appending the value span IS a
   childList mutation inside the observed subtree, so the observer retriggers
   itself forever. The page never reached its `load` event and every product URL
   hung. A `change` listener plus Horizon's own variant-change event covers the
   same ground with no feedback path.
   ---------------------------------------------------------------------- */
(function () {
  var SCOPE = "main[data-template^='product']";

  function syncLegend(fieldset) {
    if (!fieldset) return;
    var legend = fieldset.querySelector('legend');
    if (!legend) return;

    var checked = fieldset.querySelector("input[type='radio']:checked");
    if (!checked) return;

    // RAW's values carry the pack size ("Cherry Bakewell - 12 Bars"); the size
    // buttons show that, so the legend shows the flavour only.
    var value = (checked.value || '').split(' -')[0].trim();
    if (!value) return;

    var slot = legend.querySelector('.custom-pdp__option-value');
    if (slot && slot.getAttribute('data-value') === value) return; // already correct

    if (!slot) {
      slot = document.createElement('span');
      slot.className = 'custom-pdp__option-value';
      legend.appendChild(slot);
    }
    slot.setAttribute('data-value', value);

    // Built with DOM nodes, not innerHTML: `value` is option data and must never
    // be parsed as markup. The em dash is decorative punctuation between two
    // labels, so it is hidden from assistive tech; the value stays announced.
    slot.textContent = '';
    var dash = document.createElement('span');
    dash.setAttribute('aria-hidden', 'true');
    dash.textContent = ' \u2014 ';
    slot.appendChild(dash);
    slot.appendChild(document.createTextNode(value));
  }

  function syncAll() {
    var scope = document.querySelector(SCOPE);
    if (!scope) return;
    var sets = scope.querySelectorAll('.variant-option');
    for (var i = 0; i < sets.length; i++) syncLegend(sets[i]);
  }

  // Delegated from the document so it survives Horizon replacing the picker's
  // DOM on variant change — a listener bound to the inputs would be lost.
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.type !== 'radio') return;
    if (!input.closest) return;
    var fieldset = input.closest('.variant-option');
    if (!fieldset || !fieldset.closest(SCOPE)) return;

    // Apply immediately for the case where the DOM is not replaced, then again
    // after Horizon's re-render has landed. Without the second pass the suffix
    // vanishes on every chip click: the picker swaps in fresh markup that has no
    // appended value, and the `change` event fires BEFORE that swap. Re-querying
    // the fieldset by nothing but syncAll() is deliberate — the old node
    // reference is detached by then.
    syncLegend(fieldset);
    setTimeout(syncAll, 120);
    setTimeout(syncAll, 500);
  });

  // Horizon re-renders the picker after a variant change and drops the appended
  // value. These are its own signals, so re-applying on them cannot feed back
  // into a mutation the way an observer did.
  document.addEventListener('variant:update', syncAll);
  document.addEventListener('variantChange', syncAll);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }
})();

/* ==========================================================================
   PDP — price on the Add to bag button
   ==========================================================================
   The design labels the CTA "Add to bag — £14.99". Horizon builds that label
   from a locale string with no per-block override, and `add-to-cart-button.liquid`
   is vendor code (Rule 1), so the price is appended here.

   The price is read from `.custom-pdp__now` — our own price row — rather than
   from a variant JSON blob. That element is already the single source of the
   displayed price, so the button can never disagree with the figure above it,
   which is the failure mode that matters commercially.

   Quantity is deliberately NOT multiplied in. The button says what one costs;
   the total is the cart's job, and a line total on the button reads as a bug
   when someone sets quantity to 3.
   ---------------------------------------------------------------------- */
(function () {
  var SCOPE = "main[data-template^='product']";

  function priceText() {
    var el = document.querySelector(SCOPE + ' .custom-pdp__now');
    if (!el) return '';
    // The visually-hidden "Sale price" prefix must not end up on the button.
    var clone = el.cloneNode(true);
    var hidden = clone.querySelectorAll('.visually-hidden');
    for (var i = 0; i < hidden.length; i++) hidden[i].remove();
    return clone.textContent.replace(/\s+/g, ' ').trim();
  }

  function sync() {
    var price = priceText();
    if (!price) return;

    // There are TWO add-to-cart buttons on a product page: the buy column's and
    // the sticky bar's, and the sticky one comes FIRST in the DOM. A plain
    // querySelector returns the sticky button, so the price was being appended to
    // a bar that is off-screen until you scroll — which is why it looked like the
    // script was not running at all. Both get the price; the sticky bar showing it
    // is correct, it just must not be the ONLY one.
    var buttons = document.querySelectorAll(SCOPE + ' .add-to-cart-button');
    for (var b = 0; b < buttons.length; b++) applyTo(buttons[b], price);
  }

  function applyTo(btn, price) {
    if (!btn) return;

    var slot = btn.querySelector('.custom-pdp__atc-price');
    if (slot && slot.getAttribute('data-price') === price) return; // idempotent

    if (!slot) {
      slot = document.createElement('span');
      slot.className = 'custom-pdp__atc-price';
      // Appended after the label text. `add-to-cart__added` is the success state
      // Horizon swaps in, so the price must sit before it in the DOM or it shows
      // up inside the tick animation.
      var added = btn.querySelector('.add-to-cart__added');
      if (added) btn.insertBefore(slot, added);
      else btn.appendChild(slot);
    }
    slot.setAttribute('data-price', price);

    // DOM nodes, not innerHTML. The em dash is decorative, so it is hidden from
    // assistive tech — a screen reader already reads the price from the row above.
    slot.textContent = '';
    var dash = document.createElement('span');
    dash.setAttribute('aria-hidden', 'true');
    dash.textContent = ' — ';
    slot.appendChild(dash);
    slot.appendChild(document.createTextNode(price));
  }

  // No MutationObserver: appending the slot is itself a mutation inside the
  // button, so observing it would retrigger forever — the same trap that hung
  // every product page earlier in this build.
  document.addEventListener('change', function (e) {
    if (!e.target || e.target.type !== 'radio') return;
    // Horizon re-renders the button along with the price on a variant change, so
    // a single quick pass loses the appended price. Same reason the legend needs
    // two passes: the `change` event fires before the swap lands.
    setTimeout(sync, 120);
    setTimeout(sync, 500);
    setTimeout(sync, 1000);
  });
  document.addEventListener('variant:update', function () { setTimeout(sync, 60); });
  document.addEventListener('variantChange', function () { setTimeout(sync, 60); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();

/* ==========================================================================
   PDP — align the accordion to the gallery column
   ==========================================================================
   The accordion is a SECTION-level block, so it spans the full content width
   while the design has it sitting under the GALLERY column only.

   A hardcoded max-width cannot do this. Measured across viewports, `786px` was
   right at exactly 1440px and wrong everywhere else: it bled up to 424px into the
   buy column at 900-1280px, and fell 81px short at 1600px and above. The buy
   column is a fixed 549.73px track while the gallery takes the remainder, so the
   correct width changes with every viewport.

   So it is measured from the gallery itself and written to a custom property.
   That also survives a change to the grid, the gap, or the page width setting —
   none of which this code needs to know about.

   Below the breakpoint where the columns stop sharing a row, the width is
   released back to `none` so the accordion is full width like everything else.
   ---------------------------------------------------------------------- */
(function () {
  var SCOPE = "main[data-template^='product']";

  function align() {
    var acc = document.querySelector(SCOPE + ' .custom-pdp-acc');
    if (!acc) return;

    var gallery = document.querySelector(SCOPE + ' .product-media-container');
    var details = document.querySelector(SCOPE + ' .product-details');
    if (!gallery || !details) return;

    var g = gallery.getBoundingClientRect();
    var d = details.getBoundingClientRect();

    // Stacked layout: the buy column starts below the gallery rather than beside
    // it, so there is no column to align to and full width is correct.
    var stacked = d.top > g.bottom - 1;
    if (stacked || g.width < 1) {
      acc.style.removeProperty('--custom-pdp-acc-width');
      return;
    }

    acc.style.setProperty('--custom-pdp-acc-width', Math.round(g.width) + 'px');
  }

  var raf = null;
  function schedule() {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(function () { raf = null; align(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', align);
  } else {
    align();
  }
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  // Fonts landing changes the gallery's height, not its width, but a late layout
  // shift can still move it. Cheap to re-check once.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(align);
  }
})();

/* ==========================================================================
   PDP editorial — scroll reveals and the saturation bar
   ==========================================================================
   Two separate jobs, deliberately kept in one IIFE because they share the same
   scope-and-bail guard.

   NOTE ON THE OBSERVER. Unlike the legend/ATC code earlier in this file, an
   IntersectionObserver IS correct here: it observes elements and only toggles a
   CLASS on them. A class change is not a childList mutation, so there is no
   feedback path — the trap that hung every product page was a MutationObserver
   whose callback appended a node inside the subtree it was watching.

   `.page-wrapper` is the scroll container on this theme (html and body are
   overflow:hidden), so the observer's root must be that element, not the
   viewport. With root:null the callbacks fire against a container that never
   scrolls and everything reveals at load.
   ---------------------------------------------------------------------- */
(function () {
  var root = document.querySelector('[data-custom-ed]');
  if (!root) return;

  /* --- 1. Reveal on scroll ------------------------------------------- */
  var targets = root.querySelectorAll('.custom-reveal, .custom-stagger');

  function revealAll() {
    for (var i = 0; i < targets.length; i++) targets[i].classList.add('is-in');
  }

  if (!('IntersectionObserver' in window) ||
      (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
    // No observer, or the visitor asked for less motion: show everything now.
    revealAll();
  } else {
    var scroller = document.querySelector('.page-wrapper');
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          entries[i].target.classList.add('is-in');
          // Reveal ONCE. Re-animating on every scroll-back is what makes a
          // motion layer feel cheap.
          io.unobserve(entries[i].target);
        }
      }
    }, {
      root: scroller || null,
      // Fire slightly before the element is fully in view so the motion reads as
      // the page arriving, not as a delayed reaction to it.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.08
    });

    for (var i = 0; i < targets.length; i++) io.observe(targets[i]);

    // A fast scroll or a deep link can carry an element past between callbacks
    // and leave it stuck invisible. Sweep once anything already past the fold.
    setTimeout(function () {
      var vh = window.innerHeight || 800;
      for (var j = 0; j < targets.length; j++) {
        var b = targets[j].getBoundingClientRect();
        if (b.top < vh && b.bottom > 0) targets[j].classList.add('is-in');
      }
    }, 120);
  }

  /* --- 2. Saturation bar -------------------------------------------- */
  var fill = root.querySelector('[data-sat-fill]');
  var pct = root.querySelector('[data-sat-pct]');
  var weeks = root.querySelectorAll('.custom-ed__week input');

  function syncSat() {
    if (!fill) return;
    var checked = null;
    for (var i = 0; i < weeks.length; i++) {
      if (weeks[i].checked) { checked = weeks[i]; break; }
    }
    if (!checked) return;
    var v = parseInt(checked.value, 10);
    if (isNaN(v)) return;
    fill.style.width = v + '%';
    if (pct) {
      // The design shows "~25%" for the intermediate weeks and a flat "100%" at
      // full saturation — an approximation sign on an exact figure reads wrong.
      pct.textContent = (v >= 100 ? '' : '~') + v + '%';
    }
  }

  root.addEventListener('change', function (e) {
    if (e.target && e.target.type === 'radio') syncSat();
  });
  syncSat();
})();

/* ==========================================================================
   PDP purchase selector — Subscribe / Bulk Buy / One-time
   ==========================================================================
   The subscription tier is rendered server-side from Shopify selling plans, so
   this file only has to handle two things Liquid cannot:

     1. Kaching's bundle ladder, which is client-side data
     2. Switching the selected tier, and keeping the form in step

   WHY WE READ JSON, NOT DOM (Ian's option C, and it is sturdier than it sounds).
   Kaching emits its deal config as a server-rendered
   `<script class="kaching-bundles-deal-block-settings" type="application/json">`.
   That is structured data the app publishes, so this breaks only if Kaching
   changes its config SCHEMA — not when it restyles its own UI, which is what
   scraping rendered markup would have been hostage to.

   Kaching's own block stays on the page and keeps working underneath (option B):
   its `body:has(.kaching-bundles__block)` price-hiding rule targets
   `.price__container` / `.price-per-servings`, and ours are `custom-pdp__*`, so
   the two coexist. Verified live.
   ---------------------------------------------------------------------- */
(function () {
  var root = document.querySelector('[data-custom-buy]');
  if (!root) return;

  var productId = root.getAttribute('data-product-id');
  var variantPrice = parseInt(root.getAttribute('data-variant-price'), 10) || 0;

  function money(pence) {
    // Match the storefront's own formatting rather than inventing one: a bundle
    // price shown as "10.6" next to a price row showing "£10.60" reads as a bug.
    var v = (pence / 100).toFixed(2);
    return '£' + v;
  }

  /* --- Read Kaching's deal ladder ---------------------------------------- */
  function readLadder() {
    var nodes = document.querySelectorAll('.kaching-bundles-deal-block-settings');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      // Several products can publish config on one page (recommendations etc),
      // so match on the product id rather than taking the first one.
      var pid = n.getAttribute('data-product-id');
      if (productId && pid && pid !== productId) continue;
      try {
        var cfg = JSON.parse(n.textContent);
        if (cfg && cfg.dealBars && cfg.dealBars.length) return cfg;
      } catch (e) {
        // A malformed config is the app's problem, not ours: fall through and
        // leave the bundle tier hidden rather than rendering a broken price.
      }
    }
    return null;
  }

  /* Per-unit price for one deal bar, in pence.
     `discountType: 'specific'` means discountValue IS the total for that
     quantity — not a percentage and not a per-unit figure. Treating it as either
     produces prices that look plausible and are wrong. */
  function barUnitPence(bar) {
    var qty = bar.quantity || 1;
    if (bar.discountType === 'specific' && bar.discountValue) {
      return Math.round((bar.discountValue * 100) / qty);
    }
    /* `percentage`: discountValue is % off each unit (Creatine Monohydrate:
       2 tubs 10, 3 tubs 20, 4 tubs 25). The discount is rounded DOWN to the
       penny, which is what reaches the cart: 500g £11.99 x 3 at 20% added as
       £28.80, i.e. £9.60 each (verified 2026-10-02). Without this branch the
       card showed £11.99 each, under-selling a real 20% saving. */
    if (bar.discountType === 'percentage' && bar.discountValue) {
      return variantPrice - Math.floor(variantPrice * bar.discountValue / 100);
    }
    return Math.round(variantPrice / 1); // no discount: full unit price
  }

  /* If the bundle tier is pre-checked in the Liquid (which it is on any product
     with no subscription) but Kaching has no usable ladder, the tier stays
     `hidden` and NOTHING visible is selected. Measured on Milk Thistle: bundle
     checked, bundle hidden, one-time unchecked. Move the selection to one-time
     so the card always has a visible answer. */
  function fallbackFromBundle() {
    var bundle = root.querySelector('[data-tier="bundle"] input');
    if (!bundle || !bundle.checked) return;
    var once = root.querySelector('[data-tier="once"] input');
    if (once) {
      once.checked = true;
      syncTier();
    }
  }

  /* --- Free-gift strip (gift option 3, Ian 2026-10-02) --------------------
     Under the deal rows: the gifts of the first deal that carries any, greyed
     with "With N boxes" until that deal (or a bigger one with gifts) is chosen,
     then "Unlocked". A product gift links to its product page; its title, URL,
     image and price come from Kaching's own `kaching-bundles-product` JSON, which
     already carries the gift products, so nothing extra is fetched. */
  var TRUCK = '<svg width="20" height="20" viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 9 H19 V21 H3 Z"></path><path d="M19 13 H25 L29 17 V21 H19"></path><circle cx="9" cy="23" r="2.5"></circle><circle cx="24" cy="23" r="2.5"></circle></svg>';
  var kachingProducts = null;
  function giftProduct(gid) {
    var id = parseInt(String(gid || '').split('/').pop(), 10);
    if (!id) return null;
    if (!kachingProducts) {
      kachingProducts = [];
      document.querySelectorAll('script.kaching-bundles-product').forEach(function (s) {
        try { kachingProducts.push(JSON.parse(s.textContent)); } catch (e) {}
      });
    }
    var found = null;
    (function walk(o) {
      if (found || !o || typeof o !== 'object') return;
      if (o.id === id && o.handle) { found = o; return; }
      for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) walk(o[k]);
    })(kachingProducts);
    return found;
  }
  function giftLabel(text) {
    return String(text || '').replace(/^\s*\+\s*/, '').replace(/^free\s+/i, '').trim();
  }
  function buildGiftStrip(slot, bars) {
    var giftBar = null;
    for (var i = 0; i < bars.length; i++) if (bars[i].freeGifts && bars[i].freeGifts.length) { giftBar = bars[i]; break; }
    var old = slot.querySelector('.custom-buy__gifts');
    if (old) old.remove();
    if (!giftBar) return;
    var unit = (/\b(tubs?|boxes|box|packs?|bags?|bottles?|bars?)\b/i.exec(giftBar.title || '') || [, 'boxes'])[1].toLowerCase();
    var strip = document.createElement('div');
    strip.className = 'custom-buy__gifts';
    strip.setAttribute('data-gift-qty', String(giftBar.quantity || 1));
    var head = document.createElement('div');
    head.className = 'custom-buy__gifts-head';
    head.innerHTML = '<span>Free gifts</span><span data-gifts-state></span>';
    head.querySelector('[data-gifts-state]').setAttribute('data-locked-text', 'With ' + (giftBar.quantity || 1) + ' ' + unit);
    strip.appendChild(head);
    giftBar.freeGifts.forEach(function (g) {
      var p = g.giftType === 'product' ? giftProduct(g.productGID) : null;
      var row = document.createElement(p ? 'a' : 'div');
      row.className = 'custom-buy__giftrow';
      if (p) row.href = p.url;
      var media = document.createElement('span');
      media.className = 'custom-buy__giftimg';
      if (p && p.image) {
        var img = document.createElement('img');
        img.src = p.image.replace(/([?&])height=\d+/, '$1width=120');
        img.alt = 'Free gift: ' + p.title;
        img.loading = 'lazy';
        media.appendChild(img);
      } else {
        media.innerHTML = TRUCK;
      }
      row.appendChild(media);
      var txt = document.createElement('span');
      txt.className = 'custom-buy__gifttext';
      var name = document.createElement('span');
      name.className = 'custom-buy__giftname';
      name.textContent = g.giftType === 'shipping' ? 'UK delivery' : (giftLabel(g.text) || (p ? p.title : 'Free gift'));
      txt.appendChild(name);
      if (p) {
        var sub = document.createElement('span');
        sub.className = 'custom-buy__giftsub';
        sub.textContent = String(p.title).replace(/^Warrior\s+/, '').split(/\s+[–-]\s+/)[0].replace(' Plus ', ' + ');
        txt.appendChild(sub);
      }
      row.appendChild(txt);
      var pr = document.createElement('span');
      pr.className = 'custom-buy__giftprice';
      var free = document.createElement('span');
      free.className = 'custom-buy__giftfree';
      free.textContent = 'Free';
      pr.appendChild(free);
      var v = p && p.variants && p.variants[0];
      if (v && g.showPrice !== false && v.price) {
        var was = document.createElement('s');
        was.textContent = money(v.price * (g.quantity || 1));
        pr.appendChild(was);
      }
      row.appendChild(pr);
      strip.appendChild(row);
    });
    slot.appendChild(strip);
    paintGifts();
  }
  function paintGifts() {
    var strip = root.querySelector('.custom-buy__gifts');
    if (!strip) return;
    var on = root.querySelector('.custom-buy__deal.is-on');
    var unlocked = !!(on && on.classList.contains('has-gifts') &&
      parseInt(on.getAttribute('data-deal-qty'), 10) >= parseInt(strip.getAttribute('data-gift-qty'), 10));
    strip.classList.toggle('is-unlocked', unlocked);
    var state = strip.querySelector('[data-gifts-state]');
    if (state) state.textContent = unlocked ? '\u2713 Unlocked' : state.getAttribute('data-locked-text');
    paintAtcGifts();
  }

  /* "+ 2 free gifts" on Add to bag and the sticky bar's button whenever the
     selection carries gifts (Ian, 2026-10-02): Bulk Buy with a gift deal
     chosen. Text only, no images. The words come from the deal's own edge tag,
     so the count can never disagree with the row the shopper picked. */
  function paintAtcGifts() {
    var on = root.getAttribute('data-selected') === 'bundle' && root.querySelector('.custom-buy__deal.is-on.has-gifts');
    var tag = on && on.querySelector('.custom-buy__deal-gift');
    var text = tag ? tag.textContent.trim() : '';
    var btns = document.querySelectorAll("main[data-template^='product'] .add-to-cart-button");
    for (var i = 0; i < btns.length; i++) {
      var g = btns[i].querySelector('.custom-pdp__atc-gifts');
      if (!text) { if (g) g.remove(); continue; }
      if (!g) {
        g = document.createElement('span');
        g.className = 'custom-pdp__atc-gifts';
        var added = btns[i].querySelector('.add-to-cart__added');
        if (added) btns[i].insertBefore(g, added); else btns[i].appendChild(g);
      }
      if (g.textContent !== text) g.textContent = text;
    }
  }

  function fillBundle(cfg) {
    var tier = root.querySelector('[data-tier="bundle"]');
    if (!tier) { fallbackFromBundle(); return; }

    var bars = cfg.dealBars.filter(function (b) { return (b.quantity || 1) > 1; });
    if (!bars.length) { fallbackFromBundle(); return; } // a single-quantity "ladder" is not a bundle

    // The "from" figure is the CHEAPEST per-unit price across the ladder,
    // computed rather than typed. The design shows £11.24, which is the 4-tub
    // tier — at 5 tubs it is £10.60, so a hardcoded number goes stale the moment
    // merchandising adds or reprices a tier.
    var cheapest = null;
    for (var i = 0; i < bars.length; i++) {
      var u = barUnitPence(bars[i]);
      if (cheapest === null || u < cheapest) cheapest = u;
    }

    var fromEl = tier.querySelector('[data-bundle-from]');
    if (fromEl && cheapest !== null) {
      // The unit word comes from the DEAL TITLES, not Kaching's `unitLabel`.
      // CPE's unitLabel is "Per Box" while every title says "Tubs" — so trusting
      // the label printed "£10.60 / box" for a product sold in tubs. The titles
      // are what merchandising actually writes and what the customer reads.
      var unit = 'unit';
      var m = /\b(tubs?|boxes|box|packs?|bags?|bottles?|sachets?|bars?|tins?)\b/i
                .exec(bars[0].title || '');
      if (m) {
        unit = m[1].toLowerCase().replace(/(s|es)$/, '');
        if (unit === 'box' || unit === 'boxe') unit = 'box';
      } else if (cfg.unitLabel) {
        unit = cfg.unitLabel.replace(/^per\s+/i, '').toLowerCase();
      }
      fromEl.textContent = money(cheapest) + ' / ' + unit;
      var unitEl = tier.querySelector('[data-bundle-unit]');
      if (unitEl) unitEl.textContent = unit;
    }

    var slot = tier.querySelector('[data-bundle-slot]');
    if (slot) {
      slot.innerHTML = '';
      for (var j = 0; j < bars.length; j++) {
        var b = bars[j];
        var opt = document.createElement('button');
        opt.type = 'button';
        opt.className = 'custom-buy__deal';
        opt.setAttribute('data-deal-id', b.id || '');
        opt.setAttribute('data-deal-qty', String(b.quantity || 1));
        // The exact deal total for fixed-price ("specific") deals, so the button
        // shows £35.99, not 4 x a rounded £9.00 = £36.00.
        if (b.discountType === 'specific' && b.discountValue) opt.setAttribute('data-deal-total', String(Math.round(b.discountValue * 100)));

        var t = document.createElement('span');
        t.className = 'custom-buy__deal-title';
        // Kaching titles carry a supply note after a pipe ("2 Tubs | 5 Month Supply");
        // the row shows the quantity only (design A2, 2026-10-02).
        t.textContent = (b.title || ((b.quantity || 1) + ' tubs')).split('|')[0].trim();
        opt.appendChild(t);

        if (b.freeGifts && b.freeGifts.length) {
          // Edge tag on the row's top border (gift option 3, Ian 2026-10-02).
          var gift = document.createElement('span');
          gift.className = 'custom-buy__deal-gift';
          gift.textContent = b.freeGifts.length > 1 ? '+ ' + b.freeGifts.length + ' free gifts' : '+ free gift';
          opt.appendChild(gift);
          opt.classList.add('has-gifts');
        }

        var pr = document.createElement('span');
        pr.className = 'custom-buy__deal-price';
        pr.textContent = money(barUnitPence(b)) + ' each';
        opt.appendChild(pr);

        if (b.badgeText) {
          var badge = document.createElement('span');
          badge.className = 'custom-buy__deal-badge';
          badge.textContent = b.badgeText;
          opt.appendChild(badge);
        }
        slot.appendChild(opt);
      }
    }

    if (slot) buildGiftStrip(slot, bars);

    // Only now reveal it. `hidden` was on the markup so the tier was inert to
    // assistive tech too until it was known to exist.
    tier.removeAttribute('hidden');

    /* PRE-SELECT THE MOST POPULAR DEAL when bulk buy is the active tier.
       Ian, 2026-08-21: on a product with no subscription, bulk buy is selected
       and its most popular option should already be chosen.

       "Most popular" is Kaching's own badge text — it is the merchandiser's
       choice of which tier to push, so it is the right signal rather than
       guessing at quantity or price. Falls back to the first deal when no bar
       carries a badge, so something is always selected. */
    var bundleChecked = tier.querySelector('input') && tier.querySelector('input').checked;
    var already = slot && slot.querySelector('.custom-buy__deal.is-on');
    if (slot && bundleChecked && !already) {
      var all = slot.querySelectorAll('.custom-buy__deal');
      var pick = null;
      for (var k = 0; k < all.length; k++) {
        var bt = all[k].querySelector('.custom-buy__deal-badge');
        if (bt && /most\s*popular/i.test(bt.textContent || '')) { pick = all[k]; break; }
      }
      if (!pick && all.length) pick = all[0];
      if (pick) {
        pick.classList.add('is-on');
        // syncTier pushes the chosen deal's quantity into the product form; the
        // class alone would look selected and add ONE unit to the cart.
        syncTier();
      }
    }

    paintPrices();
  }

  /* --- Push the selection into the product form -------------------------
     THE BUG THIS FIXES. Horizon's product form has no `selling_plan` input at
     all — checked, it emits only `id` and `quantity`. So without this, selecting
     Subscribe & Save changed the UI and then added a ONE-OFF to the cart. The
     customer sees a subscription confirmed and gets a single purchase.

     Same for the bundle tier: the deal quantity has to reach `quantity` or a
     "3 Tubs" selection adds one tub at the 3-tub unit price.
  --------------------------------------------------------------------- */
  function form() {
    return document.querySelector('product-form-component form')
        || document.querySelector('form[action*="/cart/add"]');
  }

  function setField(name, value) {
    var f = form();
    if (!f) return;
    var input = f.querySelector('input[name="' + name + '"]');
    if (value === null || value === '') {
      // Remove rather than blank it: an empty `selling_plan` is not the same as
      // no selling plan, and Shopify treats the former as an error.
      if (input && input.getAttribute('data-custom-buy-owned')) input.remove();
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.setAttribute('data-custom-buy-owned', 'true');
      f.appendChild(input);
    }
    input.value = value;

    // NO event dispatch on quantity. Measured: setting .value gives "4", and
    // dispatching `change` immediately reverts it to "1" — Horizon's
    // quantity-selector component resets from its own state on that event. The
    // dispatch was CAUSING the reset, not fixing it.
  }

  /* KNOWN BUG, NOT YET FIXED — bundle quantity does not reach the form.
     Verified behaviour: selecting "4 Tubs" leaves quantity at 1, so the cart
     receives ONE tub. selling_plan works correctly (plan ids 712544682361 /
     715129 / 747897 land in the form and follow the frequency buttons), so the
     subscription path is sound; only the bundle quantity is broken.

     What has been ruled out: the field exists and is found, and dispatching
     input+change on it did not make Horizon's quantity-selector component adopt
     the value.

     Most likely cause, to check next: the stepper's input may sit OUTSIDE the
     `product-form-component form` element, in which case `setField` is finding
     the right-named input but not the one the form serialises. Diagnose by
     comparing `document.querySelectorAll('input[name=quantity]').length` against
     `form.querySelectorAll(...)`, then either target the form's own input or add
     an owned hidden field and let the stepper be display-only for bundles.

     Until then a bundle selection under-charges the customer's basket rather
     than over-charging, which is the safer direction, but it is still wrong. */

  /* --- Intercept the cart POST ------------------------------------------
     THE REAL MECHANISM, found by watching the network rather than the DOM.

     Horizon does not submit the form; it POSTs JSON to /cart/add.js:

       {"items":[{"id":…,"quantity":1,"properties":{…},"selling_plan":…}]}

     `quantity` lives in that PAYLOAD, not in the form fields. Which is why every
     attempt to set the form's quantity input failed — the value was never read.
     Confirmed end to end: picking the 4-tub deal added ONE tub at £14.99.

     Kaching's own bar behaves identically, so the app is not doing anything
     special either; the bundle price comes from a cart-level discount keyed on the
     `__kaching_bundles` property, and the quantity has to be right for that
     discount to apply to the right number of units.

     So the fix is a fetch wrapper that rewrites the outgoing payload. It touches
     only /cart/add.js, only when a bundle tier is selected, and leaves everything
     else exactly as Horizon built it.
  --------------------------------------------------------------------- */
  /* FREE GIFT LINES (Ian, 2026-10-02). Measured on the LIVE theme, where
     Kaching's own widget adds to cart: choosing RAW 4 boxes adds the bars
     AND a second line, the Creatine + Electrolytes sachet pack at £0, tagged
     `__kaching_bundles: {"id":<main id>,"deal":<deal id>,"v":<v>,"gift":<gift id>}`
     (Kaching's discount then prices it at £0). Through this theme's form only
     the bars were added: the gift never reached the cart. So for the chosen
     deal, each PRODUCT gift is appended in exactly that format, unless Kaching
     already added it. Shipping gifts need no line. */
  /* Which lines in a cart payload are THIS product's own line. Kaching / UpCart
     build their own payload (/cart/add.js?upcart=1): {id, quantity, properties}
     with no `product-id`, plus free-gift lines carrying `parent_id` (Ian,
     2026-10-12: One-time on Creatine Plus Collagen added 3 tubs + a shaker, and
     Bulk Buy "2 Tubs" added 3). So the line is also recognised by variant id;
     a line with parent_id is a gift, never ours. */
  var ourIdsCache = null;
  function ourIds() {
    if (ourIdsCache) return ourIdsCache;
    try {
      ourIdsCache = JSON.parse(root.querySelector('[data-custom-buy-variants]').textContent)
        .variants.map(function (v) { return String(v.id); });
    } catch (e) { ourIdsCache = []; }
    return ourIdsCache;
  }
  function isOurLine(item) {
    if (!item) return false;
    if (String(item['product-id'] || '') === String(productId)) return true;
    return !item.parent_id && ourIds().indexOf(String(item.id)) !== -1;
  }
  // Kaching gift lines hung off our variant. keepGiftIds: gift ids to keep.
  function dropKachingGifts(payload, keepGiftIds) {
    if (!payload || !payload.items) return;
    payload.items = payload.items.filter(function (it) {
      if (!it.parent_id || ourIds().indexOf(String(it.parent_id)) === -1) return true;
      var r = (it.properties && it.properties.__kaching_bundles) || '';
      var m = r.match(/"gift":"([^"]+)"/);
      return !m || (keepGiftIds || []).indexOf(m[1]) !== -1;
    });
  }

  function addGiftLines(payload, dealEl) {
    try {
      if (!dealEl || !payload || !payload.items) return;
      var cfg = readLadder();
      var dealId = dealEl.getAttribute('data-deal-id');
      var bar = cfg && cfg.dealBars.filter(function (b) { return b.id === dealId; })[0];
      // Gifts Kaching attached for a DIFFERENT deal (its default) go; the chosen
      // deal's gifts stay or are added below.
      dropKachingGifts(payload, ((bar && bar.freeGifts) || []).map(function (g) { return g.id; }));
      if (!bar || !bar.freeGifts || !bar.freeGifts.length) return;
      var main = null, mainProp = null;
      for (var i = 0; i < payload.items.length; i++) {
        var it = payload.items[i];
        if (!isOurLine(it)) continue;
        var raw = (it.properties && it.properties.__kaching_bundles) || it['properties[__kaching_bundles]'];
        if (raw) { main = it; try { mainProp = JSON.parse(raw); } catch (e) {} break; }
      }
      if (!main || !mainProp) return;
      bar.freeGifts.forEach(function (g) {
        if (g.giftType !== 'product') return;
        var already = payload.items.some(function (it) {
          var r = (it.properties && it.properties.__kaching_bundles) || it['properties[__kaching_bundles]'] || '';
          return r.indexOf('"gift":"' + g.id + '"') !== -1;
        });
        if (already) return;
        var p = giftProduct(g.productGID);
        var vid = (g.variantGIDs && g.variantGIDs.length) ? String(g.variantGIDs[0]).split('/').pop()
          : (p && (p.selectedVariantId || (p.variants && p.variants[0] && p.variants[0].id)));
        if (!vid) return;
        payload.items.push({
          id: Number(vid),
          quantity: g.quantity || 1,
          properties: { __kaching_bundles: JSON.stringify({ id: mainProp.id, deal: mainProp.deal, v: mainProp.v, gift: g.id }) }
        });
      });
    } catch (e) {
      // Never break the add over a gift: the bars still go in.
    }
  }

  (function interceptCartAdd() {
    if (window.__customBuyPatched) return;
    window.__customBuyPatched = true;

    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var isAdd = url.indexOf('/cart/add') !== -1;
        var selected = root.getAttribute('data-selected');

        /* Pin the variant the shopper actually picked. Horizon writes the form's
           id only after its own fetch lands, so a fast tap on Add to bag right
           after choosing a size could add the PREVIOUS size. The cards know the
           picked variant immediately (data-variant-id), so the request uses it. */
        /* ONLY this product's own form submission is touched: it carries
           `product-id` = this product. Cart-drawer upsells (UpCart adds a
           Shaker, a Magnesium...) also hit /cart/add and must pass untouched. */
        var ours = isOurLine;
        var pinned = root.getAttribute('data-variant-id');
        if (isAdd && pinned && init && init.body) {
          if (typeof init.body === 'string') {
            var pj = JSON.parse(init.body);
            var pitems = pj && (pj.items || [pj]);
            for (var pi = 0; pitems && pi < pitems.length; pi++) {
              if (ours(pitems[pi])) pitems[pi].id = pinned;
            }
            init = Object.assign({}, init, { body: JSON.stringify(pj) });
          } else if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
            if (String(init.body.get('product-id') || '') === String(productId)) init.body.set('id', pinned);
          }
        }

        if (isAdd && selected === 'bundle' && init && init.body &&
            typeof init.body === 'string') {
          var deal = root.querySelector('.custom-buy__deal.is-on');
          var qty = deal ? parseInt(deal.getAttribute('data-deal-qty'), 10) : 0;

          if (qty > 1) {
            var payload = JSON.parse(init.body);
            if (payload && payload.items && payload.items.length) {
              for (var i = 0; i < payload.items.length; i++) {
                if (!ours(payload.items[i])) continue;
                payload.items[i].quantity = qty;
                // A bundle is a one-off. Leaving a selling_plan on it would send a
                // subscription for the bundle quantity, which is not the offer.
                delete payload.items[i].selling_plan;
              }
              addGiftLines(payload, deal);
              init = Object.assign({}, init, { body: JSON.stringify(payload) });
            }
          }
        }
        /* Subscribe and Save / One-Time: the request must carry exactly what the
           shopper chose, whatever Kaching's own widget did meanwhile. Measured
           2026-10-02: after a size change Kaching re-applied its default
           "2 Tubs" deal AFTER our refresh, so Subscribe added qty 2 with a
           __kaching_bundles property (£19.44 instead of £10.79). The quantity
           stepper is hidden on the PDP, so the shopper cannot choose more than
           one here; quantity is forced to 1, the bundle property dropped, and on
           subscribe the selected plan is set. */
        if (isAdd && (selected === 'subscribe' || selected === 'once') && init && init.body) {
          var plan = selected === 'subscribe' ? selectedPlanId() : null;
          if (typeof init.body === 'string') {
            var p2 = JSON.parse(init.body);
            var items = p2 && (p2.items || [p2]);
            for (var k = 0; items && k < items.length; k++) {
              if (!ours(items[k])) continue;
              items[k].quantity = 1;
              if (items[k].properties) delete items[k].properties.__kaching_bundles;
              delete items[k]['properties[__kaching_bundles]'];
              if (plan) items[k].selling_plan = plan; else delete items[k].selling_plan;
            }
            // Kaching's bulk-buy gift lines belong to a deal, not to a single tub.
            dropKachingGifts(p2, []);
            init = Object.assign({}, init, { body: JSON.stringify(p2) });
          } else if (typeof FormData !== 'undefined' && init.body instanceof FormData &&
              String(init.body.get('product-id') || '') === String(productId)) {
            init.body.set('quantity', '1');
            init.body.delete('properties[__kaching_bundles]');
            if (plan) init.body.set('selling_plan', plan); else init.body.delete('selling_plan');
          }
        }
      } catch (e) {
        // Never break add-to-cart over this. On any parse or shape surprise the
        // original request goes through untouched.
      }
      return origFetch.call(this, input, init);
    };
  })();

  /* Quantity, set through the STEPPER rather than the input.
     The component owns the input and reverts a direct assignment on `change`, so
     the only reliable route is its own plus/minus buttons — which is what a human
     uses and what the component is built to respond to. */
  function setQuantity(target) {
    var input = document.querySelector(
      'quantity-selector-component input[name="quantity"]');
    if (!input) { setField('quantity', String(target)); return; }
    var current = parseInt(input.value, 10) || 1;
    if (current === target) return;

    var wrap = input.closest('quantity-selector-component');
    var plus = wrap && wrap.querySelector('[name="plus"]');
    var minus = wrap && wrap.querySelector('[name="minus"]');
    // Bounded loop: a runaway click loop on a component we do not own would be
    // far worse than a wrong quantity.
    var guard = 0;
    while (current < target && plus && guard++ < 50) { plus.click(); current++; }
    while (current > target && minus && guard++ < 50) { minus.click(); current--; }
  }

  /* Kaching's own add-to-cart mechanism, discovered by serialising the form:
     it injects `properties[__kaching_bundles]` carrying the selected deal id.
     THAT is how a bundle reaches the cart — not by multiplying quantity, which is
     why forcing the quantity field was the wrong approach entirely.

     We set the deal id on Kaching's own hidden input where it exists, and fall
     back to quantity only if the app has not injected one. */
  /* Drive KACHING'S OWN BAR, rather than trying to reproduce what it does.

     Established by testing the LIVE site, where Kaching is visible: selecting its
     4-tub bar makes the site post `quantity: 4` on its own. The app manages
     quantity correctly — the reason our page posted 1 was that the app never knew
     the selection had changed.

     Two things had to be true for that to work:
       - the block must keep a real hit area (see the CSS note; display:none and a
         1px clip both broke it)
       - the bar needs REAL pointer events. A plain `.click()` does not move
         Kaching's selection — verified on the live site too, so this is the app's
         behaviour and not something our theme causes.

     So we dispatch pointerdown/mousedown/mouseup/click at the bar's own centre
     coordinates, which is what a genuine tap produces. */
  function proxyToKaching(qty) {
    var bars = document.querySelectorAll('.kaching-bundles__bar');
    if (!bars.length) return false;

    var target = null;
    for (var i = 0; i < bars.length; i++) {
      // Match on the leading quantity in the bar's own title, e.g. "4 Tubs | …".
      var m = /^\s*(\d+)\s/.exec(bars[i].textContent || '');
      if (m && parseInt(m[1], 10) === qty) { target = bars[i]; break; }
    }
    if (!target) return false;

    var r = target.getBoundingClientRect();
    var x = r.left + r.width / 2;
    var y = r.top + Math.min(24, r.height / 2);
    var opts = { bubbles: true, cancelable: true, composed: true,
                 clientX: x, clientY: y, button: 0, isPrimary: true };

    try {
      target.dispatchEvent(new PointerEvent('pointerdown', opts));
      target.dispatchEvent(new MouseEvent('mousedown', opts));
      target.dispatchEvent(new PointerEvent('pointerup', opts));
      target.dispatchEvent(new MouseEvent('mouseup', opts));
      target.dispatchEvent(new MouseEvent('click', opts));
    } catch (e) {
      target.click(); // last resort
    }
    return true;
  }

  function setBundle(deal) {
    var qty = parseInt(deal.getAttribute('data-deal-qty'), 10) || 1;
    // Let Kaching own the state. If its bars are not there for any reason, the
    // fetch interceptor below is the safety net.
    proxyToKaching(qty);
  }

  function clearBundle() {
    setQuantity(1);
  }

  function selectedPlanId() {
    var on = root.querySelector('.custom-buy__freq-btn.is-on');
    return on ? on.getAttribute('data-selling-plan') : null;
  }

  function selectedDealQty() {
    var on = root.querySelector('.custom-buy__deal.is-on');
    return on ? on.getAttribute('data-deal-qty') : null;
  }

  /* --- Price shown on the CTA and the sticky bar ------------------------
     Both must follow the selected tier. Left alone they keep showing the one-off
     price, so a customer on Subscribe & Save sees "Add to bag — £14.99" under a
     card that says £13.49 — the page contradicting itself at the moment of
     commitment.

     The sticky bar is a separate element with its own priceContainer, and it is
     the one that is visible when the CTA is scrolled past, so it matters more
     than it looks.
  --------------------------------------------------------------------- */
  function tierPricePence() {
    var value = root.getAttribute('data-selected');

    if (value === 'subscribe') {
      var el = root.querySelector('[data-sub-price]');
      if (el) {
        var n = parseFloat((el.textContent || '').replace(/[^0-9.]/g, ''));
        if (!isNaN(n)) return Math.round(n * 100);
      }
      return variantPrice;
    }

    if (value === 'bundle') {
      // The TOTAL for the chosen deal, not the per-unit price: the button adds
      // the whole bundle, so showing the unit price under-states what is charged.
      var on = root.querySelector('.custom-buy__deal.is-on')
            || root.querySelector('.custom-buy__deal');
      if (on && on.getAttribute('data-deal-total')) return parseInt(on.getAttribute('data-deal-total'), 10);
      if (on) {
        var qty = parseInt(on.getAttribute('data-deal-qty'), 10) || 1;
        var unit = parseFloat(
          (on.querySelector('.custom-buy__deal-price') || {}).textContent
            ? on.querySelector('.custom-buy__deal-price').textContent.replace(/[^0-9.]/g, '')
            : '');
        if (!isNaN(unit)) return Math.round(unit * 100) * qty;
      }
    }

    return variantPrice;
  }

  function paintPrices() {
    var pence = tierPricePence();
    var text = money(pence);

    // The CTA's price span was appended by the earlier ATC-price block, which
    // reads `.custom-pdp__now`. The A2 top section has no such price row, so
    // that block never creates the span and the button showed no price. Create
    // it here when missing (same placement: before Horizon's "added" state).
    var btns = document.querySelectorAll("main[data-template^='product'] .add-to-cart-button");
    for (var bi = 0; bi < btns.length; bi++) {
      if (btns[bi].querySelector('.custom-pdp__atc-price')) continue;
      var made = document.createElement('span');
      made.className = 'custom-pdp__atc-price';
      var added = btns[bi].querySelector('.add-to-cart__added');
      if (added) btns[bi].insertBefore(made, added); else btns[bi].appendChild(made);
    }
    paintAtcGifts();
    var slots = document.querySelectorAll(
      "main[data-template^='product'] .custom-pdp__atc-price");
    for (var i = 0; i < slots.length; i++) {
      slots[i].setAttribute('data-price', text);
      slots[i].textContent = '';
      var dash = document.createElement('span');
      dash.setAttribute('aria-hidden', 'true');
      dash.textContent = ' \u2014 ';
      slots[i].appendChild(dash);
      slots[i].appendChild(document.createTextNode(text));
    }

    // Sticky bar. Replace only the money text so the visually-hidden "Sale price"
    // label and the compare-at markup Horizon renders are left intact.
    var sticky = document.querySelector('.sticky-add-to-cart__price');
    if (sticky) {
      var moneyEls = sticky.querySelectorAll('.money, .price-item__group .money');
      if (moneyEls.length) {
        moneyEls[0].textContent = text;
        for (var j = 1; j < moneyEls.length; j++) moneyEls[j].textContent = '';
      }
    }
  }

  /* --- Tier + frequency selection --------------------------------------- */
  function syncTier() {
    var checked = root.querySelector('input[type="radio"]:checked');
    var value = checked ? checked.value : 'once';
    root.setAttribute('data-selected', value);

    if (value === 'subscribe') {
      clearBundle();
      setField('selling_plan', selectedPlanId());
      setQuantity(1);
    } else if (value === 'bundle') {
      setField('selling_plan', null);

      var deal = root.querySelector('.custom-buy__deal.is-on');
      if (!deal) {
        deal = root.querySelector('.custom-buy__deal');
        if (deal) deal.classList.add('is-on');
      }
      if (deal) setBundle(deal);
    } else {
      clearBundle();
      setField('selling_plan', null);
      setQuantity(1);
    }

    paintPrices();
    paintGifts();
  }

  root.addEventListener('change', function (e) {
    if (e.target && e.target.type === 'radio') syncTier();
  });

  // A flavour or size change makes Horizon re-render the Add to bag button,
  // which drops the price span; repaint once the swap has landed.
  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.closest || !e.target.closest('variant-picker')) return;
    setTimeout(paintPrices, 600);
    setTimeout(paintPrices, 1400);
  });

  root.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('.custom-buy__freq-btn') : null;
    if (btn && typeof VJ !== 'undefined' && VJ) setTimeout(function () { applyVariant(pickedVariant()); }, 0);
    if (btn) {
      var all = root.querySelectorAll('.custom-buy__freq-btn');
      for (var i = 0; i < all.length; i++) {
        all[i].classList.toggle('is-on', all[i] === btn);
        all[i].setAttribute('aria-pressed', all[i] === btn ? 'true' : 'false');
      }
      // Selecting a frequency implies the subscription tier.
      var subRadio = root.querySelector('[data-tier="subscribe"] input');
      if (subRadio && !subRadio.checked) subRadio.checked = true;
      syncTier(); // always: the plan id changed even if the tier did not
      return;
    }

    var deal = e.target.closest ? e.target.closest('.custom-buy__deal') : null;
    if (deal) {
      var deals = root.querySelectorAll('.custom-buy__deal');
      for (var j = 0; j < deals.length; j++) {
        deals[j].classList.toggle('is-on', deals[j] === deal);
      }
      var bundleRadio = root.querySelector('[data-tier="bundle"] input');
      if (bundleRadio && !bundleRadio.checked) bundleRadio.checked = true;
      syncTier(); // always: the deal quantity changed even if the tier did not
    }
  });

  syncTier();

  /* --- Refresh on variant change (Ian, 2026-10-02) -----------------------
     The cards are rendered by Liquid for the variant the page loaded with,
     and Horizon does not re-render this custom-liquid block when the variant
     changes. Measured on Creatine Monohydrate: load 1KG, pick 500g, and the
     form's id became the 500g variant while all three cards and the button
     still showed 1KG's £17.99 / £19.99. So when the form's variant id moves,
     the section is fetched for the new variant (Section Rendering API) and the
     block's contents swapped in place. The root element and its delegated
     listeners stay; the chosen tier, delivery frequency and bulk row are
     carried over by position. */
  function currentFormVariant() {
    var input = document.querySelector("main[data-template^='product'] .product-details form input[name='id']") ||
      document.querySelector("main[data-template^='product'] input[name='id']");
    return input ? input.value : '';
  }
  var refreshing = null;
  function refreshFor(id) {
    if (!id || id === root.getAttribute('data-variant-id') || refreshing === id) return;
    var section = root.closest('[id^="shopify-section-"]');
    if (!section) return;
    refreshing = id;
    var keep = {
      tier: (root.querySelector('input[type="radio"]:checked') || {}).value,
      freq: Array.prototype.indexOf.call(root.querySelectorAll('.custom-buy__freq-btn'), root.querySelector('.custom-buy__freq-btn.is-on')),
      deal: Array.prototype.indexOf.call(root.querySelectorAll('.custom-buy__deal'), root.querySelector('.custom-buy__deal.is-on'))
    };
    var url = location.pathname + '?variant=' + encodeURIComponent(id) +
      '&section_id=' + encodeURIComponent(section.id.replace('shopify-section-', ''));
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        refreshing = null;
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-custom-buy]');
        if (!fresh || currentFormVariant() !== id) return; // superseded by a newer choice
        root.innerHTML = fresh.innerHTML;
        root.setAttribute('data-variant-id', fresh.getAttribute('data-variant-id') || id);
        root.setAttribute('data-variant-price', fresh.getAttribute('data-variant-price') || '0');
        variantPrice = parseInt(root.getAttribute('data-variant-price'), 10) || 0;
        if (keep.tier) {
          var radio = root.querySelector('input[type="radio"][value="' + keep.tier + '"]');
          if (radio) radio.checked = true;
        }
        var fb = root.querySelectorAll('.custom-buy__freq-btn');
        if (keep.freq >= 0 && fb[keep.freq]) {
          for (var i = 0; i < fb.length; i++) {
            fb[i].classList.toggle('is-on', i === keep.freq);
            fb[i].setAttribute('aria-pressed', i === keep.freq ? 'true' : 'false');
          }
        }
        var cfg = readLadder();
        if (cfg) fillBundle(cfg);
        var deals = root.querySelectorAll('.custom-buy__deal');
        if (keep.deal >= 0 && deals[keep.deal]) {
          for (var j = 0; j < deals.length; j++) deals[j].classList.toggle('is-on', j === keep.deal);
        }
        if (keep.tier === 'bundle' && root.querySelector('[data-tier="bundle"]').hidden) {
          var once = root.querySelector('[data-tier="once"] input');
          if (once) once.checked = true;
        }
        syncTier();
      })
      .catch(function () { refreshing = null; });
  }
  /* --- Instant update from the pre-rendered variant JSON ------------------
     The snippet prints every variant's one-off, compare-at, servings and
     per-plan subscription prices into `[data-custom-buy-variants]`. On a
     flavour/size tap the chosen variant is worked out from the picker's own
     checked radios (not from the form's id, which Horizon only updates after
     its fetch lands) and the cards are repainted in the same frame. The
     section fetch above remains only as a fallback for a page without the JSON. */
  var VJ = null;
  try {
    var vjEl = root.querySelector('[data-custom-buy-variants]');
    if (vjEl) VJ = JSON.parse(vjEl.textContent);
  } catch (e) { VJ = null; }

  function pickedVariant() {
    if (!VJ) return null;
    var picker = document.querySelector("main[data-template^='product'] variant-picker");
    if (!picker) return null;
    var want = [];
    for (var i = 0; i < VJ.options.length; i++) {
      var name = VJ.options[i];
      var inputs = picker.querySelectorAll("input[type='radio']:checked, select");
      var val = null;
      for (var k = 0; k < inputs.length; k++) {
        if ((inputs[k].getAttribute('data-option-name') || inputs[k].name || '').indexOf(name) === 0) { val = inputs[k].value; break; }
      }
      want.push(val);
    }
    for (var j = 0; j < VJ.variants.length; j++) {
      var o = VJ.variants[j].o, ok = true;
      for (var m = 0; m < o.length; m++) { if (want[m] !== null && o[m] !== want[m]) { ok = false; break; } }
      if (ok) return VJ.variants[j];
    }
    return null;
  }

  function setText(sel, text, show) {
    var el = root.querySelector(sel);
    if (!el) return;
    el.textContent = text;
    if (show) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
  }

  function applyVariant(v) {
    if (!v) return;
    root.setAttribute('data-variant-id', String(v.id));
    root.setAttribute('data-variant-price', String(v.p));
    variantPrice = v.p;
    var plan = selectedPlanId();
    var pl = (plan && v.plans[plan]) || v.plans[Object.keys(v.plans)[0]];
    if (pl) {
      setText('[data-sub-price]', money(pl[0]), true);
      // Was price = the higher of the plan's compare-at and the variant's
      // compare-at (2026-10-12), matching the One-time card's struck price.
      var subWas = Math.max(pl[1] > pl[0] ? pl[1] : v.p, v.c || 0);
      setText('[data-sub-was]', money(subWas), subWas > pl[0]);
      setText('[data-sub-per-serving]', money(v.s ? Math.floor(pl[0] / v.s) : 0) + ' / serving', !!v.s);
    }
    setText('[data-once-price]', money(v.p), true);
    setText('[data-once-was]', money(v.c), v.c > v.p);
    setText('[data-once-per]', money(v.s ? Math.floor(v.p / v.s) : 0) + ' / serving', !!v.s);
    var keepDeal = Array.prototype.indexOf.call(root.querySelectorAll('.custom-buy__deal'), root.querySelector('.custom-buy__deal.is-on'));
    var cfg = readLadder();
    if (cfg) fillBundle(cfg);
    var deals = root.querySelectorAll('.custom-buy__deal');
    if (keepDeal >= 0 && deals[keepDeal]) {
      for (var d = 0; d < deals.length; d++) deals[d].classList.toggle('is-on', d === keepDeal);
    }
    syncTier();
  }

  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.closest || !e.target.closest('variant-picker')) return;
    if (VJ) {
      applyVariant(pickedVariant());
      // Horizon re-renders the button after its fetch, dropping the price span.
      setTimeout(function () { paintPrices(); }, 700);
      return;
    }
    // Fallback without the JSON: fetch the section once the form's id has moved.
    setTimeout(function () { refreshFor(currentFormVariant()); }, 700);
    setTimeout(function () { refreshFor(currentFormVariant()); }, 1600);
  });

  /* Kaching's script may not have parsed its own config yet at DOMContentLoaded.
     The config script itself is server-rendered though, so it is readable
     immediately — no need to wait on the app's JS. Retry briefly only in case
     the block is injected late. */
  var tries = 0;
  (function attempt() {
    var cfg = readLadder();
    if (cfg) { fillBundle(cfg); return; }
    if (++tries < 10) { setTimeout(attempt, 250); return; }
    // Retries exhausted: this product has no Kaching ladder at all, so the
    // pre-checked bundle tier will never reveal. Hand the selection to one-time.
    fallbackFromBundle();
  })();
})();

/* ----------------------------------------------------------------------
   PDP REVIEWS — move the Loox widget into the Warrior band
   ----------------------------------------------------------------------
   REPLACES a Judge.me mover that is now dead code. That version pulled the
   app's histogram, review list and pagination out of its inline DOM and into
   our rail and grid, restyling them in place. Loox cannot be treated that way:
   its widget is a CROSS-ORIGIN IFRAME on loox.io, so nothing inside it is
   reachable from CSS or JS — no cards, no stars, no filters.

   What is still ours: the torn edges, the black band, the eyebrow, the display
   heading and the score rail, all rendered in Liquid from the review
   metafields. What Loox owns: everything inside the frame.

   So this does the one thing that IS possible — relocates the whole app block
   into the band's grid slot, so the widget sits inside the black band between
   our masthead and the bottom tear rather than in a white section below it.

   Watched rather than run once: the app injects its block asynchronously, and
   on a slow connection the node does not exist at DOMContentLoaded. The
   observer disconnects the moment the move succeeds, so it cannot sit live
   watching the document for the rest of the session.
   ---------------------------------------------------------------------- */
(function () {
  var band = document.querySelector('.custom-reviews');
  if (!band) return;

  var grid = band.querySelector('[data-reviews-grid]');
  if (!grid) return;

  function move() {
    // The app BLOCK wrapper, not the iframe: moving the iframe alone would
    // strip the container and script Loox needs around it.
    var widget = document.querySelector('#looxReviews');
    if (!widget) return false;

    var block = widget.closest('.shopify-app-block') || widget;
    if (grid.contains(block)) return true;

    // Remember where it came from so the now-empty section can be hidden —
    // an empty <section> still prints its own padding as a gap under the band.
    var host = block.closest('.shopify-section');

    grid.appendChild(block);
    band.setAttribute('data-reviews-app', 'loox');
    if (host && !host.querySelector('.custom-reviews') && !host.children.length) {
      host.setAttribute('data-reviews-emptied', 'true');
    }
    return true;
  }

  if (move()) return;

  var observer = new MutationObserver(function () {
    if (move()) observer.disconnect();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Never watch forever — Loox may be absent on a product with no reviews.
  setTimeout(function () { observer.disconnect(); }, 15000);
})();

/* ==========================================================================
   COLLECTION — move the quick-add button out of the media stage.

   WHY JS AT ALL. The design puts a small round add button at the foot of the
   card, beside the price. Horizon renders its quick-add as a child of
   `.card-gallery`, absolutely positioned over the pack shot. Two CSS-only routes
   were tried and measured, and both fail:

     - Pinned absolutely to the card instead of the gallery, it floats over
       whatever sits at that spot: a geometric test against every text leaf found
       it overlapping the product title on 14 of 20 cards at 390px and 16 at
       1440px. Padding the text cannot fix it, because an out-of-flow element
       ignores the space reserved for it.
     - `order` cannot move it either: it is not a child of the card, so it is not
       one of the card's flex items.

   Relocating the ELEMENT is therefore the only way to get it into normal flow,
   where an overlap is impossible by construction.

   The whole node is moved, so the quick-add component keeps its own markup,
   dataset and event bindings — nothing here re-implements add-to-basket. Verified
   after the move: cart count 0 -> 1 with no console errors.
   ========================================================================== */
(function () {
  var SCOPE = '.custom-collection-page';

  function moveOne(card) {
    var q = card.querySelector('.quick-add');
    if (!q) return;
    // Already moved: it is a direct child of the card.
    if (q.parentElement === card) return;
    card.appendChild(q);
  }

  function moveAll(root) {
    var scope = (root || document).querySelector
      ? (root.closest && root.closest(SCOPE) ? root : (root || document))
      : document;
    var cards = (scope.querySelectorAll ? scope : document).querySelectorAll(
      SCOPE + ' .product-grid__card, ' + SCOPE + ' .product-grid__item .product-grid__card'
    );
    for (var i = 0; i < cards.length; i++) moveOne(cards[i]);
  }

  function init() {
    /* "You may also like" (product-recommendations) fetches its cards after
       load, so on a PDP the scope may not exist yet at init. It is watched below
       alongside the collection grid rather than bailing here. */
    var recs = document.querySelectorAll('product-recommendations');
    if (!document.querySelector(SCOPE) && !recs.length) return;
    moveAll(document);

    /* The grid is re-rendered by filtering (a morph diff against a fresh server
       render) and extended by infinite scroll, both of which bring back cards with
       the button in its original place. A MutationObserver re-runs the move for
       any card that appears.

       It observes the GRID, not the card, and the move appends a node the observer
       would see — so `moved` guards against re-entry: a card whose button is
       already its last child is skipped, which terminates the loop. */
    var grid = document.querySelector(SCOPE + ' .product-grid');
    var targets = Array.prototype.slice.call(recs);
    if (grid) targets.push(grid);
    if (!targets.length || typeof MutationObserver === 'undefined') return;

    var scheduled = false;
    var mo = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      /* Coalesce a burst of mutations into one pass on the next frame, so a page
         of 24 appended cards costs one sweep rather than 24. */
      requestAnimationFrame(function () {
        scheduled = false;
        moveAll(document);
      });
    });
    targets.forEach(function (t) { mo.observe(t, { childList: true, subtree: true }); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();


/* ==========================================================================
   COLLECTION — the "Filter" and "View" labels the design puts in the bar.

   Horizon renders its "Filters" heading ONLY in the vertical filter layout
   (blocks/filters.liquid:107 gates it on `filter_style == 'vertical'`), and this
   page uses the horizontal bar. Switching layouts to gain a label would change
   the whole control arrangement, so the two labels are inserted instead.

   They are decorative captions for controls that already have accessible names —
   each facet is a <summary> with its own text, and the density inputs carry
   aria-labels — so they are marked aria-hidden rather than adding a second,
   competing label to the same controls.
   ========================================================================== */
(function () {
  var SCOPE = '.custom-collection-page';

  function label(text, cls) {
    var el = document.createElement('span');
    el.className = cls;
    el.setAttribute('aria-hidden', 'true');
    el.textContent = text;
    return el;
  }

  function place() {
    var bar = document.querySelector(SCOPE + ' .facets--horizontal');
    if (!bar) return;

    /* The "Filter" caption that led the bar is gone: the "All filters" button
       (blocks/_custom-filters.liquid) leads it now (design option C, 2026-09-30). */

    /* "View" captioned the grid-density rail. That control is off (Ian: not enough
       products to justify a dense view), so the caption is only inserted if the rail
       is actually present — turning `enable_grid_density` back on restores both. */
    var rail = bar.querySelector('.column-options-wrapper, .column-options');
    if (rail && rail.getBoundingClientRect().width > 0 && !bar.querySelector('.custom-facets__view')) {
      var host = rail.parentElement || bar;
      host.insertBefore(label('View', 'custom-facets__view custom-t-meta'), rail);
    }
  }

  function init() {
    if (!document.querySelector(SCOPE)) return;
    place();

    /* Filtering morph-diffs the bar against a fresh server render, which removes
       anything not in that response — so the labels are re-placed afterwards.
       `place()` is idempotent: it returns early once each label exists. */
    var bar = document.querySelector(SCOPE + ' .facets--horizontal');
    if (!bar || typeof MutationObserver === 'undefined') return;
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        place();
      });
    }).observe(bar, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/* ==========================================================================
   Bundle builder — drive the vendor picker from OUTSIDE it
   ==========================================================================
   THE BUG THIS FIXES. An earlier version MOVED the builder inside
   <variant-picker> so the component would resolve its radios directly. It did —
   but `variant-picker.js` re-renders by MORPHING itself against a fresh server
   render (morph.js), and the builder is not in that server HTML, so morph
   deleted it as a foreign node. Measured on the first click: steps 4 -> 0,
   pills 22 -> 0, the whole builder gone. `morph`'s `reject` hook only filters
   INCOMING nodes, so it cannot preserve ours either.

   So the builder STAYS OUTSIDE the picker and forwards each selection onto the
   matching vendor radio. Vendor owns resolution, price and cart; the builder is
   purely an interface. Nothing of ours is inside the morph target, so a
   re-render cannot touch it.
   ========================================================================== */
(function () {
  var root = document.querySelector('[data-custom-bundle]');
  if (!root) return;

  /* Vendor's own form is hidden by CSS (structurally, see custom.css) but stays
     rendered — `hidden` and `display: none` both make the component inert. */

  function vendorInput(optionName, value) {
    var picker = document.querySelector('variant-picker');
    if (!picker) return null;
    var all = picker.querySelectorAll('.variant-picker__form input[type="radio"]');
    for (var i = 0; i < all.length; i++) {
      if (all[i].dataset.optionName === optionName && all[i].value === value) return all[i];
    }
    return null;
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Summary "Your selections" is read from the checked inputs, so it cannot
     drift from what the form will submit. */
  function paintPicks() {
    var out = root.querySelector('[data-bundle-picks]');
    if (!out) return;
    var rows = root.querySelectorAll('.custom-bundle__step[data-row]');
    var html = '';
    for (var i = 0; i < rows.length; i++) {
      var on = rows[i].querySelector('input:checked');
      if (!on) continue;
      var t = rows[i].querySelector('.custom-bundle__step-title');
      var label = t ? t.childNodes[0].textContent.trim() : '';
      html += '<span class="custom-bundle__pick">'
            + '<span class="custom-bundle__pick-dot"></span>'
            + '<span class="custom-bundle__pick-line">'
            + esc(label) + ' \u2014 ' + esc(on.value) + '</span>'
            + '<span class="custom-bundle__pick-tick">&#10003;</span></span>';
    }
    out.innerHTML = html;
  }

  /* Sold out must not be selectable. `aria-disabled` still lets a click through
     and `disabled` would drop the input from the map, so cancel the click. */
  root.addEventListener('click', function (e) {
    var out = e.target.closest && e.target.closest('.custom-bundle__pill--out');
    if (out) { e.preventDefault(); e.stopPropagation(); }
  }, true);

  root.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.type !== 'radio') return;
    var proxy = vendorInput(input.dataset.optionName || '', input.value);
    if (proxy && !proxy.checked) {
      proxy.checked = true;
      // Dispatch from the VENDOR input: the component reads
      // `data-option-value-id` off the element the event came from, and only
      // its own radios carry it.
      proxy.dispatchEvent(new Event('change', { bubbles: true }));
    }
    paintPicks();
  });

  paintPicks();

  /* Mirror the resolved price back into the summary. */
  document.addEventListener('variant:update', function () {
    var el = root.querySelector('[data-bundle-price]');
    var live = document.querySelector('product-price .price, .price__current');
    if (el && live) el.textContent = live.textContent.trim();
    paintPicks();
  });
})();

/* ==========================================================================
   Sticky add-to-cart on a bundle: repeat the product name
   ==========================================================================
   Horizon prints the VARIANT title on the second line. On a bundle that is the
   concatenated flavour list — measured "Mixed Berry / Chocolate Orange /
   Chocolate Brownie - 12 bars", which wrapped to six lines and pushed the price
   and button out of alignment. Ian wants the product name repeated there.

   Done in script rather than by forking `sections/product-information.liquid`:
   that is a vendor section and forking it for one line costs the upgrade path.
   The component re-renders the bar on variant change, so this re-applies on its
   own update event as well as on load.
   ========================================================================== */
(function () {
  if (!document.querySelector('[data-custom-bundle]')) return;

  function nameLine() {
    var bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar) return;
    var title = bar.querySelector('.sticky-add-to-cart__title');
    var line = bar.querySelector('.sticky-add-to-cart__variant');
    if (!title || !line) return;
    var name = title.textContent.trim();
    if (name && line.textContent.trim() !== name) {
      line.textContent = name;
      // The vendor rule hides this line on single-variant products; a bundle is
      // multi-variant so it is visible, but clear the inline style in case a
      // re-render reinstated it.
      line.style.removeProperty('display');
    }
  }

  nameLine();
  document.addEventListener('variant:update', nameLine);
  // The bar itself mounts late; watch for it rather than guessing a delay.
  var mo = new MutationObserver(nameLine);
  mo.observe(document.body, { childList: true, subtree: true });
})();

/* ==========================================================================
   Cart drawer appearance — carry the theme setting into UpCart's shadow root
   ==========================================================================
   The drawer is UpCart's, rendered inside #upCart's shadow root ("Render cart
   in shadow DOM" is on). Theme CSS cannot cross that boundary — verified on
   live: 48 UpCart elements inside the root, none reachable from the document.

   Two things DO cross, and this uses both:
     - the root is OPEN, so script can add a class inside it
     - CSS custom properties inherit through a shadow boundary

   So this block does two things inside the root: INJECTS the stylesheet
   (assets/custom.upcart.css, served by the theme) and writes the theme
   setting as a class on `.upcart-cart`.

   WHY INJECT RATHER THAN PASTE INTO THE APP: a document-level <link> cannot
   style a shadow root, but an OPEN root accepts a <style> node appended by
   script. Injecting keeps the drawer CSS in version control and previewable on
   an unpublished theme, instead of living only in the UpCart admin where it
   cannot be diffed, reviewed, or rolled back with the theme.

   Pasting into UpCart's own Custom CSS still works as a fallback. Do not do
   both: duplicate rules at equal specificity make the winner depend on load
   order.
   ========================================================================== */
(function () {
  /* Fallback matches the schema default (light). If the two disagree, a page
     that fails to emit the global renders the opposite palette to the one the
     merchant chose. */
  var MODE = (window.WarriorCartMode || 'light').toLowerCase();
  var CLASS = 'w-light';
  var HREF = window.WarriorCartCssUrl || '';
  var MARK = 'data-warrior-cart-css';
  var cssText = null;

  /* Fetched once as TEXT, not linked: an @import inside the shadow root would
     re-request per drawer mount and flash unstyled on a slow connection. */
  function loadCss() {
    if (cssText !== null || !HREF) return Promise.resolve(cssText);
    return fetch(HREF, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (t) { cssText = t; return t; })
      .catch(function () { cssText = ''; return ''; });
  }

  /* @font-face does NOT cross a shadow boundary. Measured: JetBrains Mono is
     loaded in the document (document.fonts.check passes) yet inside #upCart's
     root it rendered identically to generic monospace — 28.81px for both — so
     every price fell back to the system mono.

     The faces are declared in snippets/custom.fonts.liquid with `asset_url`,
     which only Liquid can resolve, so they cannot simply be pasted into a static
     stylesheet. Instead the document's already-resolved @font-face rules are
     copied in, CDN URLs and all. */
  function fontFaces() {
    var out = '';
    for (var i = 0; i < document.styleSheets.length; i++) {
      var rules;
      try {
        rules = document.styleSheets[i].cssRules;
      } catch (e) {
        continue; // cross-origin sheet, not ours
      }
      if (!rules) continue;
      for (var j = 0; j < rules.length; j++) {
        if (rules[j].constructor && rules[j].constructor.name === 'CSSFontFaceRule') {
          out += rules[j].cssText + '\n';
        } else if (rules[j].type === 5) {
          out += rules[j].cssText + '\n';
        }
      }
    }
    return out;
  }

  function inject(root) {
    if (!cssText || root.querySelector('[' + MARK + ']')) return;
    var el = document.createElement('style');
    el.setAttribute(MARK, '');
    el.textContent = fontFaces() + cssText;
    /* Prepended so UpCart's own Custom CSS, if any is configured, still wins —
       whoever is editing the app admin should not be silently overridden. */
    root.insertBefore(el, root.firstChild);
  }

  /* UpCart renders the checkout label as the literal text "Checkout • £44.99".
     The bullet is part of a TEXT NODE, not an element, so no CSS selector can
     reach it — it has to be rewritten in the DOM. Only the text node is
     touched; the <span class="money"> sibling that carries the price is left
     alone so UpCart keeps updating it. */
  function stripBullet(root) {
    var btn = root.querySelector('.upcart-checkout-button');
    if (!btn) return;
    for (var i = 0; i < btn.childNodes.length; i++) {
      var n = btn.childNodes[i];
      /* Test-before-write matters here: this runs inside a MutationObserver on
         the same subtree, so an unconditional assignment would re-trigger the
         observer and loop. The regex guard makes the second pass a no-op. */
      if (n.nodeType === 3 && /[•·]/.test(n.nodeValue)) {
        n.nodeValue = n.nodeValue.replace(/\s*[•·]\s*/g, ' ');
      }
    }
  }

  /* Copy changes that live in UpCart's own text, not in CSS. Each is idempotent
     and re-run on every paint, because UpCart re-renders its subtree on any cart
     change and would otherwise restore the original wording. */
  function relabel(root) {
    // "Add to cart" -> "Add to bag". These are <input type="submit">, so the
    // label is the VALUE attribute, not textContent.
    /* The EMPTY-state recommendation carousel uses different classes from the
       in-cart upsells, so a single selector missed half the buttons. Matching on
       the label instead covers both without guessing at class names. */
    var btns = root.querySelectorAll(
      '.upcart-upsells-button, .upcart-recommendations-button,'
      + ' [class*="recommendations"] input[type="submit"],'
      + ' [class*="recommendations"] button,'
      + ' [class*="PreviewCarousel"] input[type="submit"]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (b.value && /add to cart/i.test(b.value)) b.value = 'Add to bag';
      else if (!b.value && /add to cart/i.test(b.textContent)) b.textContent = 'Add to bag';
    }

    // Upsell heading.
    var titles = root.querySelectorAll(
      '.upcart-upsells-title, .upcart-recommendations-title');
    for (var j = 0; j < titles.length; j++) {
      if (/you'?ll love these/i.test(titles[j].textContent)) {
        titles[j].textContent = 'Customers also bought';
      }
    }

    // Discount field wording (theme settings via window.WarriorCartCode). The
    // label rides on a data attribute that the stylesheet prints with ::before,
    // so no element is added inside UpCart's React tree. Write-only-on-change,
    // as everywhere in this observer.
    var code = window.WarriorCartCode || {};
    var mod = root.querySelector('.upcart-discount-code-module');
    if (mod) {
      var label = (code.label || '').trim();
      if (label) {
        if (mod.getAttribute('data-w-label') !== label) mod.setAttribute('data-w-label', label);
      } else if (mod.hasAttribute('data-w-label')) {
        mod.removeAttribute('data-w-label');
      }
      // Note under the field (gift cards go in at checkout), printed by ::after.
      var note = (code.hint || '').trim();
      if (note) {
        if (mod.getAttribute('data-w-hint') !== note) mod.setAttribute('data-w-hint', note);
      } else if (mod.hasAttribute('data-w-hint')) {
        mod.removeAttribute('data-w-hint');
      }
      var input = mod.querySelector('.upcart-discount-code-input');
      var hint = (code.placeholder || '').trim();
      if (input && hint && input.getAttribute('placeholder') !== hint) input.setAttribute('placeholder', hint);
      // The placeholder is not a label; give the field a real accessible name.
      if (input && input.getAttribute('aria-label') !== 'Discount code') input.setAttribute('aria-label', 'Discount code');
      var apply = mod.querySelector('.upcart-discount-code-button');
      if (apply && /^\s*apply\s*$/i.test(apply.textContent) && apply.textContent !== 'Apply') apply.textContent = 'Apply';
      collapseCode(mod, input, code);
    }
  }

  /* Collapsed discount code (canvas option D1, 2026-10-20). The field folds into
     one "Have a discount code?" line so Customers also bought gets the room.
     The toggle is our one element inside the module; re-added if UpCart
     re-renders it away. Open state lives here, not in the DOM, so a cart change
     does not snap it shut. A typed code keeps it open. */
  var codeOpen = false;
  function collapseCode(mod, input, code) {
    var on = code.collapse !== false;
    var btn = mod.querySelector(':scope > .w-code-toggle');
    if (!on) {
      if (mod.hasAttribute('data-w-collapse')) mod.removeAttribute('data-w-collapse');
      if (btn) btn.remove();
      return;
    }
    if (input && input.value) codeOpen = true;
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'w-code-toggle';
      btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/><circle cx="7.5" cy="7.5" r="1.5"/></svg>'
        + '<span class="w-code-toggle__text"></span>'
        + '<svg class="w-code-toggle__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';
      btn.addEventListener('click', function () {
        codeOpen = !codeOpen;
        mod.setAttribute('data-w-collapse', codeOpen ? 'open' : 'closed');
        btn.setAttribute('aria-expanded', codeOpen ? 'true' : 'false');
        var f = codeOpen && mod.querySelector('.upcart-discount-code-input');
        if (f) f.focus();
      });
      mod.insertBefore(btn, mod.firstChild);
    }
    var text = (code.toggle || 'Have a discount code?').trim();
    var t = btn.querySelector('.w-code-toggle__text');
    if (t.textContent !== text) t.textContent = text;
    var state = codeOpen ? 'open' : 'closed';
    if (mod.getAttribute('data-w-collapse') !== state) mod.setAttribute('data-w-collapse', state);
    if (btn.getAttribute('aria-expanded') !== String(codeOpen)) btn.setAttribute('aria-expanded', String(codeOpen));
  }

  /* Free-shipping milestone: locked vs unlocked.

     UpCart's achieved state has no stable class we can target — the guessed
     `[class*="achieved"]` / `:has()` selectors never matched, so the globe stayed
     grey after the threshold was met. The MESSAGE text is deterministic though
     ("...away from free shipping" vs "unlocked"), so the state is derived from it
     and stamped as our own attribute for CSS to hook. */
  function shippingState(root) {
    var icon = root.querySelector('[class*="milestone-icon"]');
    if (!icon) return;
    var msg = root.querySelector('[class*="rewards"] [class*="message"]');
    var text = msg ? msg.textContent : '';
    // "away from" is the locked phrasing in every locale UpCart ships with a
    // progress bar; "unlocked"/"achieved" is the met state.
    var unlocked = /unlocked|achieved|congrat/i.test(text) ||
                   (!!text && !/away/i.test(text));
    var want = unlocked ? 'on' : 'off';
    // Same loop guard as above — only write when the value actually changes.
    if (icon.getAttribute('data-warrior-ship') !== want) {
      icon.setAttribute('data-warrior-ship', want);
    }
  }

  /* Subtotal + total. UpCart renders only a savings row, so the stack is built
     from the cart's own figures and kept in sync by re-reading on each paint.
     Inserted before the footer's checkout button so it reads bottom-up:
     subtotal, savings, total, then the CTA. */
  function totals(root) {
    var footer = root.querySelector('.upcart-footer');
    var btn = root.querySelector('.upcart-checkout-button');
    if (!footer || !btn) return;

    var total = (btn.textContent.match(/[^\s]*\d[\d.,]*/) || [''])[0];
    var savedEl = root.querySelector('.upcart-savings-amount');
    var saved = savedEl ? (savedEl.textContent.match(/[^\s]*\d[\d.,]*/) || [''])[0] : '';

    var box = root.querySelector('[data-warrior-totals]');
    if (!box) {
      box = document.createElement('div');
      box.setAttribute('data-warrior-totals', '');
      btn.parentNode.insertBefore(box, btn);
    }

    /* Subtotal = the sum of every line's WAS price (its struck-through compare
       figure, or its price when it has none); savings = subtotal \u2212 total.

       It used to be total + UpCart's footer savings, but that figure only counts
       DISCOUNT savings (multibuy codes), not compare-at ones, so the summary
       disagreed with the lines: \u2212\u00a326.95 in the footer against \u00a366.51 of "Save"
       on the lines (2026-10-02). Reading the lines makes the stack add up. */
    var num = function (v) { return parseFloat(String(v).replace(/[^\d.]/g, '')) || 0; };
    var cur = (total.match(/^[^\d]+/) || ['\u00a3'])[0];
    var sub = 0;
    var items = root.querySelectorAll('.upcart-product-item');
    for (var li = 0; li < items.length; li++) {
      var was = items[li].querySelector('.upcart-item-compare-price .money');
      var now = items[li].querySelector('.upcart-item-price .money');
      sub += num((was || now || { textContent: '0' }).textContent);
    }
    if (!items.length || sub < num(total)) sub = num(total) + num(saved);
    saved = (sub - num(total)).toFixed(2);

    var rows = '<span class="w-tot__row"><span>Subtotal</span><b>'
      + cur + sub.toFixed(2) + '</b></span>';
    if (num(saved) > 0) {
      rows += '<span class="w-tot__row w-tot__row--save"><span>Savings</span><b>\u2212'
        + cur + num(saved).toFixed(2) + '</b></span>';
    }
    // Mobile prints the stack as ONE line (canvas option C): the struck
    // subtotal and the saving ride inside the Total label, shown by CSS only
    // under 750px, where the subtotal and savings rows are hidden.
    var inline = num(saved) > 0
      ? '<span class="w-tot__inline"> <s>' + cur + sub.toFixed(2) + '</s> <em>save '
        + cur + num(saved).toFixed(2) + '</em></span>'
      : '';
    rows += '<span class="w-tot__row w-tot__row--total"><span>Total' + inline + '</span><b>'
      + total + '</b></span>';

    if (box.getAttribute('data-sig') !== rows) {
      box.innerHTML = rows;
      box.setAttribute('data-sig', rows);
    }
  }

  /* RECEIPT LAYER (Ian, 2026-10-02) — design 1 of docs/experiments/side-cart.html.
     custom.upcart.css styles it under `.w-receipt`; this supplies what CSS
     cannot: the two-line header, the delivery wording, design 4's subscription
     chip, the short display titles and per-variant sizes (from
     /products/<handle>?view=custom-drawer, templates/product.custom-drawer.liquid)
     and "FREE" on a zero-price line. Every write is guarded so the root observer
     that calls this cannot loop. */
  var drawerData = {};
  function drawerInfo(handle) {
    if (!drawerData[handle]) {
      drawerData[handle] = fetch('/products/' + handle + '?view=custom-drawer', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return drawerData[handle];
  }
  function setText(el, text) { if (el && el.textContent !== text) el.textContent = text; }
  function setHTML(el, html) {
    if (el && el.getAttribute('data-w-sig') !== html) { el.innerHTML = html; el.setAttribute('data-w-sig', html); }
  }
  /* NEW-GALLERY PHOTOS (Ian, 2026-10-03). When a product's "use new images"
     toggle is on, the feed's `i` map gives each variant's first
     custom.gallery_images_2 image; the drawer swaps it in so line and upsell
     photos match the collection cards. `data-w-src` guards the observer loop;
     srcset is dropped or the browser keeps picking UpCart's old candidates. */
  function heroOf(d, variant) {
    if (!d || !d.i) return null;
    if (variant && d.i[variant]) return d.i[variant];
    var keys = Object.keys(d.i);
    return keys.length === 1 ? d.i[keys[0]] : null;
  }
  function swapImg(img, url) {
    if (!img || !url) return;
    if (img.getAttribute('data-w-src') === url) return;
    img.setAttribute('data-w-src', url);
    img.removeAttribute('srcset');
    img.src = url.indexOf('//') === 0 ? 'https:' + url : url;
  }
  /* Recharge "Contents" text -> [{qty, label, bars, img}]. Entries are split on
     new lines, else on "<n>x " (no space before the x, so "12 x 75g" stays put).
     Each entry's product is the predictive-search hit whose title ends the
     entry; the rest is the variant title ("Chocolate Brownie - 12 bars"). An
     entry that can't be matched still shows, with its text before " - " and no
     picture. Results are cached per entry text. */
  var rbCache = {};
  function rbNorm(t) { return String(t || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
  function rbEsc(t) { return String(t).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  // One product lookup per entry text: the predictive-search hit whose title
  // ends the entry, as its /products/<h>.js JSON (null when nothing matched).
  function rbProduct(rest) {
    if (rbCache[rest]) return rbCache[rest];
    rbCache[rest] = fetch('/search/suggest.json?q=' + encodeURIComponent(rest) +
        '&resources[type]=product&resources[limit]=6', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var hits = (j && j.resources && j.resources.results && j.resources.results.products) || [];
        var n = rbNorm(rest);
        var hit = hits.filter(function (h) { var t = rbNorm(h.title); return t && n.slice(-t.length) === t; })[0];
        return hit ? fetch('/products/' + hit.handle + '.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; }) : null;
      })
      .catch(function () { return null; });
    return rbCache[rest];
  }
  function rbContents(raw) {
    var parts = raw.split(/\n+/).map(function (s) { return s.trim(); }).filter(Boolean);
    if (parts.length < 2) parts = raw.split(/\s+(?=\d+x\s)/i).map(function (s) { return s.trim(); }).filter(Boolean);
    var entries = parts.map(function (t) {
      var m = /^\s*(\d+)\s*x\s+(.+?)\s*$/i.exec(t);
      return { qty: m ? parseInt(m[1], 10) : 1, rest: m ? m[2] : t };
    });
    return Promise.all(entries.map(function (e) { return rbProduct(e.rest); })).then(function (found) {
      var known = found.filter(Boolean);
      return entries.map(function (e, i) {
        var n = rbNorm(e.rest);
        // An entry the search missed reuses a product a sibling entry found.
        var p = found[i] || known.filter(function (k) { var t = rbNorm(k.title); return n.slice(-t.length) === t; })[0];
        var vn = p ? n.slice(0, n.length - rbNorm(p.title).length) : '';
        var v = p && p.variants.filter(function (x) { return rbNorm(x.title) === vn; })[0];
        var title = v ? v.title : e.rest;
        var b = /(\d+)\s*bars?\b/i.exec(title);
        var img = v && v.featured_image ? v.featured_image.src : null;
        if (img) img = (img.indexOf('//') === 0 ? 'https:' + img : img) + (img.indexOf('?') === -1 ? '?' : '&') + 'width=120';
        return {
          qty: e.qty,
          label: title.split(/\s+-\s*/)[0].replace(/\s+and\s+/i, ' & ').trim(),
          bars: b ? parseInt(b[1], 10) : 0,
          img: img
        };
      });
    });
  }
  function linkOf(scope) {
    var a = scope.querySelector('a[href*="/products/"]');
    var m = a && a.getAttribute('href').match(/\/products\/([^/?#]+)(?:[^#]*[?&]variant=(\d+))?/);
    return m ? { handle: m[1], variant: m[2] } : null;
  }

  /* READY GATE (Ian, 2026-10-28: "a flash of unstyled content" on add and
     delete). UpCart redraws a line in its own raw form, then this layer
     restyles it; anything it has to fetch first (the drawer feed, a Recharge
     box's flavours) left UpCart's version on screen meanwhile. A line now stays
     hidden behind a skeleton (custom.upcart.css) until every lookup it needs
     has settled. Cached lookups settle in the same microtask checkpoint as the
     observer, so a line already seen never shows the skeleton at all. A 1.5s
     failsafe means a failed fetch can never hide a line for good. */
  var gatePending = new WeakMap();
  function gate(el, p) {
    if (!el || el.hasAttribute('data-w-ready')) return p;
    var list = gatePending.get(el) || [];
    list.push(p);
    gatePending.set(el, list);
    return p;
  }
  function gateSettle(root) {
    var els = root.querySelectorAll('.upcart-product-item:not([data-w-ready]), .upcart-upsell-item-card:not([data-w-ready])');
    Array.prototype.forEach.call(els, function (el) {
      var list = gatePending.get(el) || [];
      gatePending.delete(el);
      var done = function () { if (!el.hasAttribute('data-w-ready')) el.setAttribute('data-w-ready', ''); };
      if (!list.length) { done(); return; }
      Promise.all(list.map(function (p) { return Promise.resolve(p).catch(function () {}); })).then(done);
      if (!el.hasAttribute('data-w-gate')) { el.setAttribute('data-w-gate', ''); setTimeout(done, 1500); }
    });
  }

  function receipt(root) {
    var cart = root.querySelector('.upcart-cart');
    if (!cart) return;
    if (!cart.classList.contains('w-receipt')) cart.classList.add('w-receipt');

    // Header: "Cart • 7 Items" -> eyebrow + title.
    var head = root.querySelector('.upcart-header-text');
    if (head) {
      var n = (head.getAttribute('data-w-count') || (head.textContent.match(/\d+/) || [''])[0]);
      var live = (head.textContent.match(/(\d+)\s*item/i) || [])[1];
      if (live) n = live;
      if (n) head.setAttribute('data-w-count', n);
      setHTML(head, '<span class="w-rx-eyebrow">Your order' + (n ? ' · ' + n + (n === '1' ? ' item' : ' items') : '') +
        '</span><span class="w-rx-title">Your bag</span>');
    }

    // Empty state: heading only (Ian, 2026-10-06: the "Every rep starts
    // somewhere" subtitle is gone; it sat beside the heading on mobile).
    var empty = root.querySelector('[class*="CartPreview__empty"]');
    if (empty) {
      var eh = empty.querySelector('h3, h2');
      setText(eh, "Your bag's empty");
      var es = empty.querySelector('.w-rx-empty-sub');
      if (es) es.remove();
    }

    // Free delivery wording.
    var msg = root.querySelector('.upcart-internal-rewards__message');
    if (msg) {
      var raw = msg.getAttribute('data-w-raw');
      var cur = msg.textContent;
      if (!raw || (cur.indexOf('to free delivery') === -1 && cur.indexOf('Free delivery unlocked') === -1)) {
        raw = cur;
        msg.setAttribute('data-w-raw', raw);
      }
      var away = raw.match(/([£$€]\s?[\d.,]+)/);
      var unlocked = /unlocked|achieved|congrat/i.test(raw) || !away;
      setText(msg, unlocked ? '✓ Free delivery unlocked' : away[1].replace(/\s/g, '') + ' to free delivery');
      if (msg.getAttribute('data-w-ship') !== (unlocked ? 'on' : 'off')) msg.setAttribute('data-w-ship', unlocked ? 'on' : 'off');
    }

    // Lines.
    var lines = root.querySelectorAll('.upcart-product-item');
    Array.prototype.forEach.call(lines, function (line) {
      // Subscription: "1 month subscription with 10% discount" -> design 4 chip.
      var sub = line.querySelector('.upcart-item-property-subscription');
      if (sub) {
        var sraw = sub.getAttribute('data-w-raw') || sub.textContent;
        if (!sub.getAttribute('data-w-raw')) sub.setAttribute('data-w-raw', sraw);
        var every = sraw.match(/(\d+)\s*(day|week|month)s?/i);
        var pct = sraw.match(/(\d+(?:\.\d+)?)\s*%/);
        if (every) {
          setHTML(sub, '↻ Every ' + every[1] + ' ' + every[2].toLowerCase() + (every[1] === '1' ? '' : 's') +
            (pct ? ' <span class="w-rx-sub-off">−' + pct[1] + '%</span>' : ''));
        }
      }
      // Zero-price lines (gifts) read FREE.
      var price = line.querySelector('.upcart-item-price .money');
      if (price && /^\D*0[.,]00$/.test(price.textContent.trim())) setText(price, 'FREE');

      var ref = linkOf(line);
      if (!ref) return;
      gate(line, drawerInfo(ref.handle)).then(function (d) {
        if (!d) return;
        swapImg(line.querySelector('img'), heroOf(d, ref.variant));
        var title = line.querySelector('.upcart-product-title-link') || line.querySelector('.upcart-product-title');
        if (d.t) setText(title, d.t);
        var size = ref.variant && d.s ? d.s[ref.variant] : '';
        // Two-option variants ("Size: 300g", "Flavour: Blue Razz") render two
        // option rows that the line grid stacks in one cell, so they overprint.
        // Merge into the first row as "Blue Razz · 300g" and hide the rest.
        var vals = line.querySelectorAll('.upcart-item-option-value');
        if (vals.length > 1) {
          var raws = Array.prototype.map.call(vals, function (v) {
            if (!v.getAttribute('data-w-raw')) v.setAttribute('data-w-raw', v.textContent.trim());
            return v.getAttribute('data-w-raw');
          });
          var rest = raws.filter(function (t) { return !size || t.toLowerCase() !== size.toLowerCase(); });
          setText(vals[0], rest.concat(size ? [size] : []).join(' · '));
          Array.prototype.forEach.call(line.querySelectorAll('.upcart-item-option'), function (o, i) {
            if (i > 0 && !o.classList.contains('w-rx-hide')) o.classList.add('w-rx-hide');
          });
          return;
        }
        var opt = line.querySelector('.upcart-item-option');
        var val = opt && opt.querySelector('.upcart-item-option-value');
        if (size) {
          if (val) {
            // Only when the option line is not already the size itself.
            var flav = val.getAttribute('data-w-raw') || val.textContent;
            if (!val.getAttribute('data-w-raw')) val.setAttribute('data-w-raw', flav);
            if (flav.toLowerCase() !== size.toLowerCase()) setText(val, flav + ' · ' + size);
          } else {
            var meta = line.querySelector('.w-rx-meta');
            if (!meta) {
              meta = document.createElement('div');
              meta.className = 'w-rx-meta';
              var t = line.querySelector('.upcart-product-title');
              if (t && t.parentNode) t.parentNode.insertBefore(meta, t.nextSibling);
            }
            setText(meta, size);
          }
        }
      });
    });

    // Bundle contents as tub thumbnails (design option B, Ian 2026-10-07).
    // UpCart only renders a bundle's component rows while its "Show N items"
    // toggle is open, so open it once per toggle (the CSS hides the toggle).
    // Each row then gets data-w-qty (the "2" of "2 × …") and data-w-label (the
    // short flavour: last part after " / ", without "Milkshake"), which the
    // stylesheet prints as a count badge and a caption. UpCart's own text is
    // left untouched and stays readable to screen readers.
    Array.prototype.forEach.call(root.querySelectorAll('.upcart-product-item'), function (line) {
      var tog = line.querySelector('[class*="BundleProperties__toggleButton"]');
      if (!tog) return;
      if (!line.classList.contains('w-bundle')) line.classList.add('w-bundle');
      if (!line.querySelector('.upcart-bundle-detail-toggle') && !tog.getAttribute('data-w-opened')) {
        tog.setAttribute('data-w-opened', '1');
        tog.click();
      }
      Array.prototype.forEach.call(line.querySelectorAll('[class*="BundleProperties__ItemComponentRow"]'), function (row) {
        var name = row.querySelector('.upcart-bundle-item-product-title');
        var vt = row.querySelector('.upcart-bundle-item-variant-title');
        var m = name ? /^\s*(\d+)\s*[×x]/.exec(name.textContent) : null;
        var qty = m ? m[1] : '1';
        var label = (vt ? vt.textContent : (name ? name.textContent : '')).split(' / ').pop().replace(/\s*milkshake\s*$/i, '').trim();
        var wrap = row.querySelector('.upcart-bundle-item-image-wrapper');
        if (wrap && wrap.getAttribute('data-w-qty') !== qty) wrap.setAttribute('data-w-qty', qty);
        var content = row.querySelector('[class*="BundleProperties__ItemComponentContent"]');
        if (content && content.getAttribute('data-w-label') !== label) content.setAttribute('data-w-label', label);
      });
    });

    // Recharge bundle contents as tub thumbnails (option A, Ian 2026-10-17;
    // canvas claude.ai/artifact/WUCvZANzj3vRKEVmXcTaSU). Recharge sends the
    // selection only as a "Contents" property, one "1x <variant> <product>" per
    // line, so it rendered as one long string with no image. Each entry is
    // matched to its product (predictive search, then /products/<h>.js) for the
    // flavour and pack shot; the line then gets the same .w-bundle tubs as a
    // native bundle, a "3 boxes · 36 bars" meta line and a fanned main image.
    // The raw property is hidden only once the tubs are built.
    Array.prototype.forEach.call(root.querySelectorAll('.upcart-product-item'), function (line) {
      var prop = null;
      Array.prototype.forEach.call(line.querySelectorAll('.upcart-item-property'), function (p) {
        var k = p.querySelector('strong');
        if (k && /^\s*contents\s*:?\s*$/i.test(k.textContent)) prop = p;
      });
      if (!prop) return;
      var raw = (prop.querySelector('span') || prop).textContent || '';
      // Rebuild if UpCart re-rendered the line and dropped the tubs. When the
      // tubs survive, still restore the classes: React rewrites className on a
      // re-render (it does so while a line is being deleted), and without
      // w-bundle/w-rb the built line fell back to UpCart's layout for ~430ms.
      if (line.getAttribute('data-w-rb') === raw && line.querySelector('.w-rb-box')) {
        if (!line.classList.contains('w-rb')) line.classList.add('w-bundle', 'w-rb');
        if (!prop.classList.contains('w-rx-hide')) prop.classList.add('w-rx-hide');
        return;
      }
      line.setAttribute('data-w-rb', raw);
      gate(line, rbContents(raw)).then(function (items) {
        if (!items.length || line.getAttribute('data-w-rb') !== raw) return;
        if (!line.classList.contains('w-bundle')) line.classList.add('w-bundle', 'w-rb');
        var total = 0, bars = 0;
        items.forEach(function (it) { total += it.qty; bars += it.qty * (it.bars || 0); });
        var meta = total + (total === 1 ? ' item' : (bars ? ' boxes' : ' items')) + (bars ? ' · ' + bars + ' bars' : '');
        var tubs = items.map(function (it) {
          return '<div class="w-rb-tub"><div class="upcart-bundle-item-image-wrapper" data-w-qty="' + it.qty + '">' +
            (it.img ? '<img src="' + it.img + '" alt="" loading="lazy">' : '') + '</div><span>' + rbEsc(it.label) + '</span></div>';
        }).join('');
        var box = line.querySelector('.w-rb-box');
        if (!box) {
          box = document.createElement('div');
          box.className = 'w-rb-box';
          prop.parentNode.insertBefore(box, prop);
        }
        setHTML(box, '<div class="w-rb-tubs" aria-label="Bundle contents">' + tubs + '</div>');
        // The "3 boxes · 36 bars" line leads the properties, above the
        // subscription chip, where a normal line shows its flavour and size.
        var props = prop.parentNode, mEl = props.querySelector('.w-rb-meta');
        if (!mEl) { mEl = document.createElement('p'); mEl.className = 'w-rb-meta'; props.insertBefore(mEl, props.firstChild); }
        setText(mEl, meta);
        prop.classList.add('w-rx-hide');
        var wrap = line.querySelector('.upcart-product-image-wrapper');
        var imgs = items.filter(function (it) { return it.img; }).slice(0, 3);
        if (wrap && imgs.length) {
          var fan = '<span class="w-rb-fan">' + imgs.map(function (it) { return '<img src="' + it.img + '" alt="">'; }).join('') + '</span>';
          var a = wrap.querySelector('a') || wrap;
          setHTML(a, fan);
        }
      });
    });

    // Upsell titles: the short display title, not the SEO one.
    Array.prototype.forEach.call(root.querySelectorAll('.upcart-upsell-item-card'), function (card) {
      var ref = linkOf(card);
      if (!ref) return;
      gate(card, drawerInfo(ref.handle)).then(function (d) {
        if (d && d.t) setText(card.querySelector('.upcart-upsell-item-title'), d.t);
        var sel = card.querySelector('.upcart-upsell-item-variant-selector');
        var img = card.querySelector('.upcart-upsell-item-image');
        swapImg(img, heroOf(d, (sel && sel.value) || ref.variant));
        // Picking another flavour changes no DOM UpCart owns, so the observer
        // never fires: follow the select directly.
        if (sel && !sel.getAttribute('data-w-hero')) {
          sel.setAttribute('data-w-hero', '1');
          sel.addEventListener('change', function () {
            swapImg(card.querySelector('.upcart-upsell-item-image'), heroOf(d, sel.value));
          });
        }
      });
    });

    gateSettle(root);
  }

  /* Warm-up hook for code that is about to add something: the RAW box builder
     (custom.raw-box.js) calls this with the Contents it is adding, so the
     flavour look-ups are cached before the drawer opens and the new line
     paints styled on its first frame. */
  window.WarriorBag = {
    warm: function (contents, handle) {
      if (contents) rbContents(contents);
      if (handle) drawerInfo(handle);
    }
  };

  function paint() {
    var host = document.getElementById('upCart');
    if (!host || !host.shadowRoot) return false;
    inject(host.shadowRoot);
    stripBullet(host.shadowRoot);
    relabel(host.shadowRoot);
    shippingState(host.shadowRoot);
    totals(host.shadowRoot);
    receipt(host.shadowRoot);
    var cart = host.shadowRoot.querySelector('.upcart-cart');
    if (!cart) return false;
    cart.classList.toggle(CLASS, MODE === 'light');
    // Also expose it as an attribute so the CSS can hook either way.
    cart.setAttribute('data-warrior-mode', MODE);
    return true;
  }

  loadCss().then(function () { paint(); });

  /* WATCH THE SHADOW ROOT ITSELF, not just the document.

     The previous version did `if (paint()) return;` — which stopped observing
     the moment the drawer first existed — and re-applied on a 350ms timer after
     any click. UpCart re-renders the upsell button on click and restores its own
     "Add to cart", so that timer WAS the visible flash: the original label sat
     on screen until the timeout fired.

     Observing the root means our labels are reapplied in the same frame as
     UpCart's re-render, before paint. */
  var rootObserver = null;

  function watchRoot() {
    var host = document.getElementById('upCart');
    if (!host || !host.shadowRoot || rootObserver) return false;
    rootObserver = new MutationObserver(function () {
      // Only the cheap text/class work — no refetch, no re-injection.
      relabel(host.shadowRoot);
      shippingState(host.shadowRoot);
      stripBullet(host.shadowRoot);
      totals(host.shadowRoot);
      receipt(host.shadowRoot);
    });
    rootObserver.observe(host.shadowRoot, {
      childList: true,
      subtree: true,
      characterData: true,
      /* `value` is the label on these buttons — they are <input type="submit">,
         so the text is an ATTRIBUTE, not a child node. Without watching
         attributes the observer never fired when UpCart reset the label, which
         is why "Add to Cart" flashed back on click. */
      attributes: true,
      attributeFilter: ['value'],
    });
    return true;
  }

  /* The observer alone is not enough: UpCart REPLACES the upsell button on
     click rather than mutating it, so there is a frame where its own
     "Add to Cart" is painted before our callback runs. Measured with a
     requestAnimationFrame sampler: bag -> Cart -> bag.

     Relabelling synchronously on pointerdown — in the same task as the click,
     before UpCart's handler swaps the node — closes that window. The observer
     still catches everything else (qty changes, line removal, re-renders). */
  document.addEventListener('pointerdown', function () {
    var host = document.getElementById('upCart');
    if (host && host.shadowRoot) {
      relabel(host.shadowRoot);
      // and again on the next frames, to catch the replacement itself
      requestAnimationFrame(function () { relabel(host.shadowRoot); });
      setTimeout(function () { relabel(host.shadowRoot); }, 0);
      setTimeout(function () { relabel(host.shadowRoot); }, 60);
    }
  }, true);

  /* Basket page: custom.cart-line renders a Recharge bundle's pills from its
     Contents property; this adds the avatar pack shots and, when the bundle product
     has no image, the three packs fanned in the line's media slot. */
  function rbCartPage() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-rb-contents]:not([data-rb-done])'), function (box) {
      box.setAttribute('data-rb-done', '1');
      rbContents(box.getAttribute('data-rb-contents') || '').then(function (items) {
        // Design C2c: overlapped flavour avatars before the pills (up to 4).
        var av = box.querySelector('[data-rb-av]');
        if (av && !av.querySelector('img')) {
          items.filter(function (it) { return it.img; }).slice(0, 4).forEach(function (it) {
            var im = document.createElement('img');
            im.src = it.img; im.alt = ''; im.loading = 'lazy';
            av.appendChild(im);
          });
        }
        var line = box.closest('.custom-cartline');
        var media = line && line.querySelector('.custom-cartline__media');
        var imgs = items.filter(function (it) { return it.img; }).slice(0, 3);
        if (media && imgs.length && !media.querySelector('img')) {
          var fan = document.createElement('span');
          fan.className = 'custom-cartline__fan';
          imgs.forEach(function (it) { var im = document.createElement('img'); im.src = it.img; im.alt = ''; fan.appendChild(im); });
          media.appendChild(fan);
        }
      });
    });
  }
  rbCartPage();

  paint();
  watchRoot();

  /* The drawer mounts lazily — it is not in the DOM until the cart is first
     opened — so keep watching the document until the root appears, then hand
     over to the root observer above. */
  var mo = new MutationObserver(function () {
    paint();
    watchRoot();
    rbCartPage();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();

/* ==========================================================================
   Gift card page — chips drive the vendor picker from OUTSIDE it
   ==========================================================================
   Same contract as the bundle builder above, and for the same reason:
   `variant-picker.js` morphs its own subtree against a fresh server render, so
   anything of ours placed inside it is deleted on the first change. The chips
   therefore live outside and forward onto the matching vendor radio, which
   keeps owning variant resolution, price and add-to-cart.

   The chips are <button role="radio">, not real inputs, so there is no second
   form control competing to submit. That also means arrow-key roving focus is
   ours to implement — a native radiogroup would have given it for free.
   ========================================================================== */
(function () {
  var root = document.querySelector('[data-custom-gc]');
  if (!root) return;

  var chips = [].slice.call(root.querySelectorAll('[data-gc-chip]'));
  if (!chips.length) return;

  function vendorInput(value) {
    var picker = document.querySelector('variant-picker');
    if (!picker) return null;
    var all = picker.querySelectorAll('.variant-picker__form input[type="radio"]');
    for (var i = 0; i < all.length; i++) {
      if (all[i].value === value) return all[i];
    }
    return null;
  }

  function select(chip, moveFocus) {
    if (!chip || chip.disabled) return;
    chips.forEach(function (c) {
      var on = c === chip;
      c.setAttribute('aria-checked', on ? 'true' : 'false');
      c.tabIndex = on ? 0 : -1;
    });
    if (moveFocus) chip.focus();

    var proxy = vendorInput(chip.dataset.optionValue);
    if (proxy && !proxy.checked) {
      proxy.checked = true;
      // Dispatched from the VENDOR input: the component reads
      // `data-option-value-id` off the element the event came from.
      proxy.dispatchEvent(new Event('change', { bubbles: true }));
    }
    paint();
  }

  /* Amount on the card face and the label both mirror the RESOLVED price from
     the vendor picker rather than the chip text, so a currency or a
     market-specific price change cannot make the card disagree with the cart. */
  function paint() {
    var on = root.querySelector('[data-gc-chip][aria-checked="true"]');
    var label = root.querySelector('[data-gc-amount-label]');
    if (on && label) label.textContent = on.textContent.trim();

    var amount = root.querySelector('[data-gc-amount]');
    if (!amount || !on) return;
    /* The amount comes from the CHIP, not from a price node on the page. This
       template renders no price block, so there is nothing to mirror — an
       earlier version read `.price` and silently matched the recommended
       -products carousel instead, leaving the card stuck on the opening
       amount. For a gift card the variant title IS the denomination, so the
       chip is the authoritative source.

       Trailing ".00" is dropped to match `money_without_trailing_zeros`, which
       is what Liquid renders on first paint — without this the amount visibly
       changed format on the first click. */
    amount.textContent = on.textContent.trim().replace(/[.,]00$/, '');
  }

  root.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest('[data-gc-chip]');
    if (chip) select(chip, false);
  });

  /* Roving focus — Left/Up and Right/Down move between chips, skipping any that
     are sold out, and Home/End jump to the ends. */
  root.addEventListener('keydown', function (e) {
    var chip = e.target.closest && e.target.closest('[data-gc-chip]');
    if (!chip) return;
    var usable = chips.filter(function (c) { return !c.disabled; });
    var i = usable.indexOf(chip);
    if (i < 0) return;
    var next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = usable[(i + 1) % usable.length];
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = usable[(i - 1 + usable.length) % usable.length];
    else if (e.key === 'Home') next = usable[0];
    else if (e.key === 'End') next = usable[usable.length - 1];
    if (next) { e.preventDefault(); select(next, true); }
  });

  /* Move the vendor buy controls into the buy column, directly under the
     denomination chips. They are a SIBLING of this snippet's wrapper in
     Horizon's flex column, so by default Add to bag renders full-width below
     the information grid — far from the chips it acts on.

     The node is MOVED rather than cloned: it owns the product form, so a copy
     would submit nothing and would double the event listeners. appendChild on
     a live node relocates it and preserves both. */
  function seatCart() {
    var slot = root.querySelector('[data-gc-cart-slot]');
    var buy = document.querySelector('.buy-buttons-block');
    if (!slot || !buy || slot.contains(buy)) return !!slot && !!buy;
    slot.appendChild(buy);
    return true;
  }

  if (!seatCart()) {
    /* Kaching and the recipient form can mount the block late, so watch for it
       rather than polling on a guessed delay. */
    var mo = new MutationObserver(function () { if (seatCart()) mo.disconnect(); });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('variant:update', paint);
  paint();
})();

/* ---------------------------------------------------------------------------
   LOYALTYLION BOOTSTRAP (perf audit 2026-10-03)
   The LoyaltyLion app embed (331 KB on every page) is disabled. custom.globals
   sets window.WarriorLL = { token } only on /pages/rewards and the customer
   account templates, and this is the embed's own loader, verbatim, run there.
   Bundled fonts are off: the page is styled by custom.css.
   --------------------------------------------------------------------------- */
(function () {
  var cfg = window.WarriorLL;
  if (!cfg || !cfg.token || window.loyaltylion && window.loyaltylion.isLoyaltyLion) return;
  if (!document.querySelector('[data-lion-app-styles]')) {
    var st = document.createElement('style');
    st.setAttribute('data-lion-app-styles', 'main');
    st.setAttribute('data-lion-app-styles-settings', '{"disableBundledCSS":false,"disableBundledFonts":true,"useClassIsolator":false}');
    document.head.appendChild(st);
  }
  !function(t,n){function o(n){var o=t.getElementsByTagName("script")[0],i=t.createElement("script");i.src=n,i.crossOrigin="",o.parentNode.insertBefore(i,o)}if(!n.isLoyaltyLion){window.loyaltylion=n,void 0===window.lion&&(window.lion=n),n.version=2,n.isLoyaltyLion=!0;var i=new Date,e=i.getFullYear().toString()+i.getMonth().toString()+i.getDate().toString();o("https://sdk.loyaltylion.net/static/2/loader.js?t="+e);var r=!1;n.init=function(t){if(r)throw new Error("Cannot call lion.init more than once");r=!0;var a=n._token=t.token;if(!a)throw new Error("Token must be supplied to lion.init");for(var l=[],s="_push configure bootstrap shutdown on removeListener authenticateCustomer".split(" "),c=0;c<s.length;c+=1)!function(t,n){t[n]=function(){l.push([n,Array.prototype.slice.call(arguments,0)])}}(n,s[c]);o("https://sdk.loyaltylion.net/sdk/start/"+a+".js?t="+e+i.getHours().toString()),n._initData=t,n._buffer=l}}}(document,window.loyaltylion||[]);
  /* Signed-in customers: custom.globals signs { customer, auth } server-side
     (Liquid sha1 of id + date + email + SDK secret). Passing them to init is
     what the app embed did; without them every visitor is a guest. */
  var init = { token: cfg.token };
  if (cfg.customer && cfg.auth) { init.customer = cfg.customer; init.auth = cfg.auth; }
  else if (cfg.missingSecret) console.warn('[warrior] LoyaltyLion: customer is signed in but shop.metafields.warrior.loyaltylion_sdk_secret is empty, so the rewards page runs as a guest.');
  window.loyaltylion.init(init);
  window.loyaltylion.configure({ disableBundledCSS: false, disableBundledFonts: true, useClassIsolator: false });
})();

/* ---------------------------------------------------------------------------
   LOYALTY RESKIN — /pages/rewards
   Canvas: "Warrior Loyalty LL Reskin", states 1a (guest) / 1b (member).

   Two jobs, both additive. The design brief is explicit that structure stays
   LoyaltyLion's: this reads their DOM and adds classes, it never moves nodes.

     1. Mark <body> so `assets/custom.css` can scope the reskin to this page.
        Horizon emits no template class here, so the LoyaltyLion mount is the
        signal. It is injected asynchronously from their CDN, hence the observer.

     2. Stagger the earn / reward cards into view.

   ON HIDING CONTENT TO ANIMATE IT — the trap this originally fell into.
   The first version set `opacity: 0` on every card up front and cleared it from
   an IntersectionObserver callback. LoyaltyLion re-renders its sections after
   mount, which replaced the observed nodes with fresh copies; the observer was
   left watching detached elements, the callback never ran, and thirteen cards
   sat permanently invisible. Verified: 13 `.w-pre`, 0 `.w-in`, opacity 0 after
   scrolling past them.

   So the hide is now applied PER CARD, immediately before that same card is
   observed, and only when the card is currently OFF-SCREEN. A card already in
   view is never hidden, and if the observer never fires the worst case is a
   card that animates in late rather than one that never appears.
   --------------------------------------------------------------------------- */
(function () {
  var REVEAL = '.lion-rule-item, .lion-reward-item';
  var io = null;
  var seen = null;

  function motionOK() {
    return 'IntersectionObserver' in window &&
      !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function reveal(root) {
    if (!motionOK()) return;

    var items = root.querySelectorAll(REVEAL);
    if (!items.length) return;

    seen = seen || new WeakSet();
    io = io || new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        var el = entries[i].target;
        el.classList.add('w-in');
        io.unobserve(el);
        /* Drop the hook once it has played so a later re-render of the same
           node does not re-hide it. */
        window.setTimeout(function (n) {
          return function () { n.classList.remove('w-pre', 'w-in'); n.style.transitionDelay = ''; };
        }(el), 900);
      }
    }, { threshold: 0.15 });

    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      if (seen.has(el)) continue;
      seen.add(el);

      /* Only hide what cannot currently be seen. Anything on screen at mount
         stays painted — no flash, and nothing to un-hide if JS stalls. */
      var box = el.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) continue;

      el.style.transitionDelay = (i % 6) * 60 + 'ms';
      el.classList.add('w-pre');
      io.observe(el);
    }
  }

  /* --- v2 copy and small additions (canvas "Warrior Loyalty Page", approved by
     Ian 2026-10-03). Every write is idempotent and guarded, because
     LoyaltyLion re-renders sections and wipes them; the observer below
     re-applies. Structure stays LoyaltyLion's: text is replaced inside their
     nodes, and the few additions are appended inside the section they belong to. */
  var HEADS = {
    redeem: ['Rewards', 'Turn XP into money off.'],
    earn: ['Earn XP', 'Ways to earn.'],
    tiers_overview: ['Tiers', 'Spend more. Earn faster.'],
    faq: ['FAQ', 'Questions.'],
    tier_progress: ['Your tier', ''],
    available_rewards: ['Your activity', 'Claimed rewards'],
    account_history: ['', 'History']
  };
  var STEPS = [['Join free', 'Create an account and 250 XP lands straight away.'],
               ['Earn XP', 'Every £1 you spend earns 5 to 7 XP, depending on your tier.'],
               ['Spend it', '100 XP is £1 off. Use it at checkout or claim a voucher.']];
  var FAQ_SHOWN = 8;

  function text(el, t) { if (el && el.textContent !== t) el.textContent = t; }
  function sec(root, kind) { return root.querySelector('[data-lion-integrated-page-section-kind="' + kind + '"]'); }
  function add(parent, cls, tag, html) {
    if (!parent) return null;
    var el = parent.querySelector(':scope > .' + cls);
    if (!el) { el = document.createElement(tag || 'div'); el.className = cls; parent.appendChild(el); }
    if (html != null && el.innerHTML !== html) el.innerHTML = html;
    return el;
  }
  function num(s) { return parseInt(String(s || '').replace(/[^0-9]/g, ''), 10) || 0; }

  function decorate(root) {
    var page = root.querySelector('.lion-integrated-page');
    if (!page) return;
    var member = page.classList.contains('lion-integrated-page--authenticated');

    Object.keys(HEADS).forEach(function (k) {
      var h = root.querySelector('[data-lion-integrated-page-section-kind="' + k + '"] .lion-integrated-page-section__heading-text');
      if (!h) return;
      if (h.getAttribute('data-eyebrow') !== HEADS[k][0]) h.setAttribute('data-eyebrow', HEADS[k][0]);
      if (HEADS[k][1]) text(h, HEADS[k][1]);
    });

    // How it works (merchant HTML block).
    var hiw = root.querySelector('.hiw');
    if (hiw) {
      var hh = hiw.querySelector('.hiw_header');
      if (hh) { hh.setAttribute('data-eyebrow', 'How it works'); text(hh, 'Three steps. No catch.'); }
      hiw.querySelectorAll('.hiw_section').forEach(function (s, i) {
        if (!STEPS[i]) return;
        text(s.querySelector('.hiw_section-subheader'), STEPS[i][0]);
        text(s.querySelector('.hiw_section-content'), STEPS[i][1]);
      });
    }

    // Refer a friend (merchant HTML block).
    var ref = root.querySelector('.referral-ll__right');
    if (ref) {
      text(ref.querySelector('h3'), 'Refer a friend');
      text(ref.querySelector('h4'), 'Give £15, get £15.');
      text(ref.querySelector('p'), 'Your friend gets £15 off their first order over £55. You get 1,500 XP, worth £15, once their order is approved.');
      text(ref.querySelector('a'), 'Get your link');
    }

    // Checkout redemption tile.
    var co = root.querySelector('.lion-reward-item--checkout-redemption .lion-reward-item__content');
    if (co) {
      text(co.querySelector('.lion-reward-item__title'), 'Spend XP at checkout');
      add(co, 'custom-loyalty-co', 'p', '100 XP = £1 off. Choose how much to use when you pay.');
    }

    var header = sec(root, 'header');
    if (!member && header) {
      var j = header.querySelector('.lion-header__join-today');
      if (j && !j.querySelector('.custom-loyalty-lede')) {
        j.innerHTML = '<p class="custom-loyalty-eyebrow">Team Warrior rewards</p><h1>Every order<br>earns XP.</h1>' +
          '<p class="custom-loyalty-lede">Join free and bank 250 XP before your first order. Spend it as money off your next one.</p>';
      }
      var join = header.querySelector('.lion-action-button--primary');
      if (join) text(join, 'Join free');
      add(header, 'custom-loyalty-stats', 'div',
        '<div><p>250</p><span>XP for joining</span></div><div><p>5–7</p><span>XP per £1 spent</span></div><div><p>100</p><span>XP = £1 off</span></div>');
    }

    if (member && header) {
      var pts = num((header.querySelector('.lion-header__your-points strong') || {}).textContent);
      var pend = num((header.querySelector('.lion-header__pending-points') || {}).textContent);
      var worth = '£' + (pts / 100).toFixed(2);
      var nav = header.querySelector('.lion-header__nav');
      var w = add(header, 'custom-loyalty-worth', 'p', 'Worth ' + worth + ' off · ' + pend.toLocaleString('en-GB') + ' XP pending');
      var shop = add(header, 'custom-loyalty-shop', 'a', 'Shop and earn');
      if (shop.getAttribute('href') !== '/collections/all') shop.setAttribute('href', '/collections/all');
      // Keep the nav last so it stays the rail along the bottom of the hero.
      if (nav && nav !== header.lastElementChild) header.appendChild(nav);

      // Tier panel: current tier and its earn rate, read from the tiers overview.
      var tp = sec(root, 'tier_progress');
      if (tp) {
        var cur = tp.querySelector('.lion-tier-progress__bar-section__step-label--current .lion-tier-progress__tier-name');
        var name = cur ? cur.textContent.trim() : '';
        var rate = '';
        root.querySelectorAll('.lion-tier-box').forEach(function (b) {
          var t = b.querySelector('.lion-tier-box__title');
          if (t && t.textContent.trim() === name) rate = (b.querySelector('.lion-tier-box__purchase-rule-points') || {}).textContent || '';
        });
        var idx = ['Recruit', 'Elite', 'Champion'].indexOf(name);
        var row = add(tp, 'custom-loyalty-tier', 'div',
          '<span class="custom-loyalty-tier__icon custom-loyalty-tier__icon--' + Math.max(idx, 0) + '"></span><p>' + name + '</p><span class="custom-loyalty-tier__rate">' + rate.trim() + '</span>');
        if (row !== tp.children[1]) tp.insertBefore(row, tp.children[1] || null);
      }

      // Voucher progress: "0 / 500 XP · 500 to go".
      root.querySelectorAll('.lion-reward-item--cart-discount-voucher').forEach(function (r) {
        var cost = num((r.querySelector('.lion-reward-item__cost') || {}).textContent);
        var acts = r.querySelector('.lion-reward-item__actions');
        if (!cost || !acts) return;
        var line = Math.min(pts, cost).toLocaleString('en-GB') + ' / ' + cost.toLocaleString('en-GB') + ' XP' +
          (pts < cost ? ' · ' + (cost - pts).toLocaleString('en-GB') + ' to go' : ' · ready');
        add(r.querySelector('.lion-reward-item__content'), 'custom-loyalty-progress', 'p', line);
      });
    }

    // FAQ: first eight, then a toggle for the rest.
    var faq = root.querySelector('.lion-faq-list');
    if (faq) {
      var items = faq.querySelectorAll('.lion-faq-list__item');
      if (items.length > FAQ_SHOWN && !faq.parentNode.querySelector('.custom-loyalty-faq-more')) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'custom-loyalty-faq-more';
        b.textContent = 'Show all ' + items.length + ' questions';
        b.addEventListener('click', function () {
          var open = faq.classList.toggle('is-all');
          b.textContent = open ? 'Show fewer' : 'Show all ' + items.length + ' questions';
        });
        faq.parentNode.insertBefore(b, faq.nextSibling);
      }
    }
  }

  var busy = false;
  function mount() {
    var root = document.getElementById('loyaltylion') ||
               document.querySelector('[data-lion-integrated-page]');
    if (!root) return false;
    document.body.classList.add('custom-loyalty');
    reveal(root);
    if (!busy) { busy = true; try { decorate(root); } finally { busy = false; } }
    ready();
    return true;
  }

  /* Smooth hand-over (Ian, 2026-10-05: the page "janks out of its wrapper"). The CSS
     keeps the app's markup transparent over a placeholder hero until this marks the
     host ready, i.e. once the app has rendered AND decorate() has restyled it, so the
     unstyled SDK hero never paints. Signed-in visitors wait for the member view, as the
     SDK can render the guest view first. Backstop at 4 s; CSS has its own at 5 s. */
  var host = document.querySelector('[data-lion-integrated-page]');
  var t0 = Date.now();
  function ready() {
    if (!host || host.classList.contains('custom-ll-ready')) return;
    var page = host.querySelector('.lion-integrated-page');
    var wantMember = !!(window.WarriorLL && window.WarriorLL.customer);
    var late = Date.now() - t0 > 4000;
    if (!late) {
      if (!page) return;
      if (wantMember && !page.classList.contains('lion-integrated-page--authenticated')) return;
    }
    host.classList.add('custom-ll-ready');
    window.setTimeout(function () { host.classList.add('custom-ll-settled'); }, 360);
  }
  window.setTimeout(ready, 4100);

  mount();

  /* Keep watching: LoyaltyLion mounts late AND re-renders when member state
     resolves, so new cards keep arriving and our copy gets wiped. Mutations
     made by decorate() are skipped by the `busy` guard and its writes are
     no-ops on a second pass, so this cannot loop. */
  var queued = false;
  var mo = new MutationObserver(function () {
    if (busy || queued) return;
    queued = true;
    /* setTimeout, not requestAnimationFrame: rAF never fires in a background
       tab, so a page opened with cmd-click stayed undecorated until focused. */
    window.setTimeout(function () { queued = false; mount(); }, 60);
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
  window.setTimeout(function () {
    mo.disconnect();
    var root = document.getElementById('loyaltylion');
    if (root) mo.observe(root, { childList: true, subtree: true, characterData: true });
  }, 20000);

  /* Backstop: whatever happens with the observer, nothing stays hidden. */
  window.setTimeout(function () {
    var stuck = document.querySelectorAll('#loyaltylion .w-pre:not(.w-in)');
    for (var i = 0; i < stuck.length; i++) stuck[i].classList.remove('w-pre');
  }, 4000);
})();

/* ==========================================================================
   Product card — flavour swatches
   ==========================================================================
   Implements "Warrior Card Flavour States" 1c: clicking a dot selects it and
   swaps the pack shot with a rise animation; `+N` expands the row in place and
   becomes `–`, which collapses it again with the selection kept.

   DELEGATED from the document, because cards arrive three ways — the homepage
   row, the collection grid, and again after a filtered or paginated load.
   Binding per card would miss every card that appears later.

   The pack shot is found by walking UP to the card and taking its first <img>,
   not by a Horizon class name: those differ between the homepage scroller and
   the collection grid and would break silently on a theme update.
   ========================================================================== */
(function () {
  function cardOf(el) {
    return el.closest('.product-grid__card, .card, [class*="product-card"], li, article');
  }

  function packShot(card) {
    var imgs = card.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      /* Skip anything inside the meta block so a future icon there is never
         mistaken for the product image. */
      if (imgs[i].closest('[data-cardmeta-flavours]')) continue;
      return imgs[i];
    }
    return null;
  }

  /* Two alternating classes rather than one: re-adding the same animation name
     does not restart it, so a second click on a different dot would swap the
     image with no motion at all. */
  function replay(el, a, b) {
    if (!el) return;
    var next = el.classList.contains(a) ? b : a;
    el.classList.remove(a, b);
    /* Force a reflow so the browser sees the removal before the re-add. */
    void el.offsetWidth;
    el.classList.add(next);
  }

  function select(btn) {
    var wrap = btn.closest('[data-cardmeta-flavours]');
    var card = cardOf(btn);
    if (!wrap || !card) return;

    var dots = wrap.querySelectorAll('[data-cardmeta-swatch]');
    for (var i = 0; i < dots.length; i++) {
      var on = dots[i] === btn;
      dots[i].classList.toggle('is-on', on);
      dots[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    var img = packShot(card);
    var src = btn.getAttribute('data-image');
    if (img && src && img.getAttribute('src') !== src) {
      img.setAttribute('src', src);
      /* srcset/sizes keep winning over a new src at wider breakpoints — the
         swap then appears to do nothing on desktop only. */
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      replay(img, 'custom-cardmeta-img-a', 'custom-cardmeta-img-b');
    }
    /* The card hover (custom.card-shader.js) plays into THIS flavour's second
       gallery image, not the default variant's. Empty = this flavour has none,
       so the hover does nothing. The shader reads both images on every hover. */
    if (img && btn.hasAttribute('data-image2')) {
      img.setAttribute('data-custom-hover-src', btn.getAttribute('data-image2') || '');
    }

    /* Price per serving follows the picked variant (data-pps from Liquid). */
    var serving = card.querySelector('[data-cardmeta-serving]');
    if (serving) {
      var ppsEl = serving.querySelector('[data-cardmeta-pps]');
      var pps = btn.getAttribute('data-pps');
      if (ppsEl && pps) ppsEl.textContent = pps + ' ' + (serving.getAttribute('data-suffix') || '');
      var saveEl = serving.querySelector('[data-cardmeta-save]');
      var save = btn.getAttribute('data-save');
      if (saveEl) {
        saveEl.hidden = !save;
        if (save) saveEl.textContent = (serving.getAttribute('data-save-label') || 'Save') + ' ' + save;
      }
    }

    /* Price and was-price follow the flavour too (Ian, 2026-09-30). Horizon's
       price snippet renders `.price .money` and `.compare-at-price .money`
       inside `[ref="priceContainer"]` on both the homepage and grid cards. */
    var priceBox = card.querySelector('[ref="priceContainer"]');
    var price = btn.getAttribute('data-price');
    if (priceBox && price) {
      var nowEl = priceBox.querySelector('.price .money') || priceBox.querySelector('.price');
      if (nowEl) nowEl.textContent = price;
      var wasGroup = priceBox.querySelector('.compare-at-price');
      var compare = btn.getAttribute('data-compare');
      if (wasGroup) {
        wasGroup.hidden = !compare;
        var wasEl = wasGroup.querySelector('.money') || wasGroup;
        if (compare) wasEl.textContent = compare;
      }
      priceBox.classList.toggle('is-no-compare', !compare);
    }

    /* Servings tag in the top bar: swap the number, keep the wording. */
    var unit = card.querySelector('.custom-cardmeta__unit');
    var servings = btn.getAttribute('data-servings');
    if (unit && servings && /\d/.test(unit.textContent)) {
      unit.textContent = unit.textContent.replace(/\d[\d,]*/, servings);
    }

    /* Every link to this product now opens the picked flavour, so a click
       through lands on the variant the card is showing. */
    var vurl = btn.getAttribute('data-url');
    if (vurl) {
      var base = vurl.split('?')[0];
      var links = card.querySelectorAll('a[href]');
      for (var l = 0; l < links.length; l++) {
        if (links[l].pathname === base) links[l].setAttribute('href', vurl);
      }
    }

    /* The flavour line lives outside the swatch wrapper (it sits under the
       price), so it is looked up from the card. */
    var line = card.querySelector('[data-cardmeta-flavourline]');
    if (line) {
      var dot = line.querySelector('[data-flavourline-dot]');
      var text = line.querySelector('[data-flavourline-text]');
      var colour = btn.getAttribute('data-colour');
      if (dot && colour) dot.style.background = colour;
      /* Name only — the coloured dot beside it already says this is a flavour,
         so the label was repeating what the design communicates visually. */
      if (text) text.textContent = btn.getAttribute('data-name') || '';
      line.hidden = false;
      replay(line, 'is-swap-a', 'is-swap-b');
    }
  }

  function toggle(btn) {
    var wrap = btn.closest('[data-cardmeta-flavours]');
    if (!wrap) return;
    var expanded = btn.getAttribute('aria-expanded') === 'true';
    var opening = !expanded;

    /* THE TOGGLE STAYS PUT (Ian, 2026-09-30). The row lays out right to left
       (custom.css) with the toggle at order 0, so it keeps its spot and the
       extra dots open to the left of the visible ones. It used to be moved out
       of the list on expand, which shifted it, and it showed a dash. */

    /* The note ("Multiple flavours") hides ONLY if the opened dots would reach
       it. Predicted from the dot count rather than measured after the width
       animation, so it fades out as the dots grow instead of after they have
       already run over it. */
    var row = wrap.closest('.custom-cardmeta__eyerow');
    var note = row && row.querySelector('.custom-cardmeta__macros');
    var hideNote = false;
    /* Only when the dots share the note's line. If they have already wrapped
       onto their own line (a narrow card), opening cannot reach the note. */
    var sameLine = false;
    if (note) {
      var nb = note.getBoundingClientRect();
      var gb = wrap.getBoundingClientRect();
      sameLine = nb.width > 0 && Math.abs(nb.top - gb.top) < nb.height;
    }
    if (opening && row && note && sameLine && note.textContent.trim()) {
      /* Measured: the collapsed row's width plus one dot pitch per hidden
         flavour, plus 8px of slack. The open row measures ~4px wider than the
         pitch predicts (the toggle's close state), and the old "buttons × 24"
         estimate left the homepage card 1px short of hiding the note, so the
         dots wrapped and the title dropped 21px. */
      var shown = wrap.querySelectorAll('[data-cardmeta-swatch]:not([data-overflow="true"])');
      var extra = wrap.querySelectorAll('[data-overflow="true"]').length;
      var pitch = 26;
      if (shown.length > 1) {
        pitch = Math.abs(shown[1].getBoundingClientRect().left - shown[0].getBoundingClientRect().left) || pitch;
      }
      var needed = wrap.getBoundingClientRect().width + extra * pitch + 8;
      var rs = getComputedStyle(row);
      var room = row.clientWidth - parseFloat(rs.paddingLeft) - parseFloat(rs.paddingRight)
        - note.getBoundingClientRect().width - (parseFloat(rs.columnGap) || 6);
      hideNote = needed > room;
    }
    /* On CLOSE the note waits for the dots to finish collapsing (0.28s width
       transition, custom.css). Bringing it back at once put it beside dots
       that were still wide, the row wrapped for ~18 frames and the title
       jumped 21px: the "yank" on 1 Year Supply of Creatine. */
    if (row) {
      clearTimeout(row.__noteTimer);
      if (opening) {
        row.classList.toggle('is-note-hidden', hideNote);
      } else if (row.classList.contains('is-note-hidden')) {
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        row.__noteTimer = setTimeout(function () {
          if (!wrap.classList.contains('is-expanded')) row.classList.remove('is-note-hidden');
        }, reduce ? 0 : 300);
      }
    }

    wrap.classList.toggle('is-expanded', opening);
    btn.setAttribute('aria-expanded', opening ? 'true' : 'false');

    var label = btn.querySelector('[data-more-label]');
    if (label) {
      if (opening) {
        label.innerHTML = '<svg aria-hidden="true" width="9" height="9" viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="3.6" stroke-linecap="round"><path d="M8 8 L24 24"/><path d="M24 8 L8 24"/></svg>';
        btn.setAttribute('aria-label', 'Show fewer flavours');
      } else {
        label.textContent = '+' + (btn.getAttribute('data-extra') || wrap.querySelectorAll('[data-overflow="true"]').length);
        btn.setAttribute('aria-label', 'Show all flavours');
      }
    }
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var toggleBtn = e.target.closest('[data-cardmeta-toggle]');
    if (toggleBtn) {
      /* These sit inside the card's link region on some layouts, so without
         this the click navigates to the PDP before anything is visible. */
      e.preventDefault();
      e.stopPropagation();
      toggle(toggleBtn);
      return;
    }

    var dot = e.target.closest('[data-cardmeta-swatch]');
    if (dot) {
      e.preventDefault();
      e.stopPropagation();
      select(dot);
    }
  }, true);

  /* PRICE PER SERVING sits IN the price row, beside the price (Ian,
     2026-09-30). It renders in the card-meta block, and the price is Horizon's
     own <product-price> block, so it is moved across. A re-render (filtering,
     paging) brings a fresh one back into the block, and this moves it again. */
  function placeServing() {
    /* Both card kinds: the collection grid (Horizon's <product-price>) and the
       homepage best-sellers rail (the `price` snippet). Each renders its prices
       in a `[ref="priceContainer"]` div, so that is the target. */
    var items = document.querySelectorAll('.custom-collection-page [data-cardmeta-serving], .custom-bs [data-cardmeta-serving]');
    for (var i = 0; i < items.length; i++) {
      var card = cardOf(items[i]);
      var price = card && card.querySelector('[ref="priceContainer"]');
      if (!price || price.contains(items[i])) continue;
      var old = price.querySelector('[data-cardmeta-serving]');
      if (old) old.remove();
      price.appendChild(items[i]);
    }
  }

  /* Paint the default flavour line on load, so the card is correct before any
     interaction and matches what Liquid rendered into the image. */
  /* GALLERY IMAGES 2: put the default variant's new-design shot on the card.
     The collection card's photo is rendered by a vendor block that only knows
     product media, so the snippet hands the URL over in data-cardmeta-hero. */
  function placeHero() {
    /* Once per card: this runs again on every DOM mutation, and without the
       flag it would undo a swatch click by putting the default shot back. */
    var metas = document.querySelectorAll('[data-cardmeta-hero]:not([data-hero-done])');
    for (var i = 0; i < metas.length; i++) {
      metas[i].setAttribute('data-hero-done', '');
      var card = cardOf(metas[i]);
      var img = card && packShot(card);
      var src = metas[i].getAttribute('data-cardmeta-hero');
      // Already rendered server-side by custom.card-gallery: swapping would only
      // re-download the same shot (and drop its srcset).
      if (img && img.hasAttribute('data-hero-ssr')) continue;
      if (!img || !src || img.getAttribute('src') === src) continue;
      img.removeAttribute('srcset');
      img.setAttribute('src', src);
    }
  }

  function initLines() {
    placeServing();
    placeHero();
    var wraps = document.querySelectorAll('[data-cardmeta-flavours]');
    for (var i = 0; i < wraps.length; i++) {
      var first = wraps[i].querySelector('[data-cardmeta-swatch]');
      var card = cardOf(wraps[i]);
      if (!first || !card) continue;
      var line = card.querySelector('[data-cardmeta-flavourline]');
      if (!line || !line.hidden) continue;
      var dot = line.querySelector('[data-flavourline-dot]');
      var text = line.querySelector('[data-flavourline-text]');
      var colour = first.getAttribute('data-colour');
      if (dot && colour) dot.style.background = colour;
      if (text) text.textContent = first.getAttribute('data-name') || '';
      line.hidden = false;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLines);
  } else {
    initLines();
  }

  /* Cards also arrive from filtering and pagination, which replace the grid
     without a page load. */
  new MutationObserver(initLines).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();

/* ---------------------------------------------------------------------------
   PDP HERO VIDEO — move the overlay onto gallery slide 1 and manage playback.

   The snippet renders the video at the end of the gallery block; this puts it
   over the FIRST `.product-media-container` and keeps it there.

   Three jobs:
     1. Re-parent the overlay into slide 1 and mark that slide as the
        positioning context. The overlay stays hidden until this succeeds, so a
        JS failure degrades to the normal gallery rather than a stray video.
     2. Hide it as soon as the shopper moves to another slide — the overlay is
        absolutely positioned inside slide 1, so without this it would still be
        covering image 1 when they came back mid-swipe.
     3. Playback mode. Looping is the default and is driven by the `loop`
        attribute the snippet renders from the `hero_video_loop` setting.
        When looping is OFF we hold the last frame on `ended` rather than
        resetting to the poster, which would read as the video failing.

        This replaces an earlier play-once-only behaviour. That version bound
        an `ended` handler unconditionally, which is what made a looping video
        stall exactly once at its first wrap.

   Deliberately NOT done: pausing on tab blur. Browsers already throttle
   background tabs, and a muted 5s clip is cheap. Pausing when scrolled out of
   view is a separate decision — see the note raised with Ian.
   --------------------------------------------------------------------------- */
(function () {
  var HOST_CLASS = 'custom-pdp-herovid-host';

  function mount() {
    var wrap = document.querySelector('[data-custom-hero-video]');
    if (!wrap || wrap.dataset.mounted === '1') return false;

    var gallery = document.querySelector('media-gallery');
    if (!gallery) return false;

    var first = gallery.querySelector('.product-media-container');
    if (!first) return false;

    first.classList.add(HOST_CLASS);
    first.appendChild(wrap);
    wrap.dataset.mounted = '1';
    wrap.classList.add('is-on');

    var video = wrap.querySelector('video');
    if (video) {
      video.muted = true;            /* belt and braces: autoplay needs it */
      video.playsInline = true;
      /* Only hold the last frame when we are NOT looping. Binding this while
         `loop` is set would pause the clip at its first wrap — `ended` still
         fires there in some engines even though playback continues. */
      if (!video.hasAttribute('loop')) {
        video.addEventListener('ended', function () {
          try { video.pause(); } catch (e) {}
        });
      }
      var p = video.play();
      /* A rejected play() is normal — a data-saver or a background tab. The
         gallery image is underneath, so there is nothing to recover. */
      if (p && p.catch) p.catch(function () {});
    }

    markThumb(gallery, video);
    watchSlide(gallery, first, wrap);
    return true;
  }

  /* Slide 1 shows the video, so its thumbnail shows a FRAME FROM THE VIDEO plus
     a play badge (Ian, 2026-10-02).

     Not the poster: Shopify's poster is frame 0, which on the CPE clip is an
     almost empty white frame. Not a canvas grab: the file is on Shopify's video
     CDN without CORS, so drawImage would taint the canvas. Instead a paused,
     muted copy of the same file sits over the thumb image, seeked to near the
     end (the clip's settled hero frame). It is the file the hero is already
     loading, so it comes from cache. If it never loads, the gallery image
     underneath still shows. The thumb stays the vendor's button for slide 1. */
  function markThumb(gallery, video) {
    var thumb = gallery.querySelector('.slideshow-controls__thumbnail');
    if (!thumb || thumb.classList.contains('custom-pdp-thumb--video')) return;
    thumb.classList.add('custom-pdp-thumb--video');
    var source = video && (video.currentSrc || (video.querySelector('source') || {}).src);
    if (source) {
      var still = document.createElement('video');
      still.className = 'custom-pdp-thumb__frame';
      still.muted = true;
      still.playsInline = true;
      still.setAttribute('muted', '');
      still.setAttribute('playsinline', '');
      still.setAttribute('aria-hidden', 'true');
      still.preload = 'auto';
      still.addEventListener('loadedmetadata', function () {
        still.currentTime = Math.max(0, still.duration - 0.15);
      }, { once: true });
      still.src = source.split('#')[0];
      thumb.appendChild(still);
    }
    var badge = document.createElement('span');
    badge.className = 'custom-pdp-thumb__play';
    badge.setAttribute('aria-hidden', 'true');
    badge.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10"><path d="M2.5 1.5 L8.5 5 L2.5 8.5 Z" fill="currentColor"></path></svg>';
    thumb.appendChild(badge);
    thumb.setAttribute('aria-label', 'Product video');
  }

  /* The slideshow does not emit a public event, so observe which slide is
     current instead. IntersectionObserver against the gallery viewport is
     cheaper than listening to scroll on a horizontal snap container. */
  function watchSlide(gallery, first, wrap) {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          wrap.classList.toggle('is-on', entries[i].isIntersecting);
        }
      },
      { root: gallery, threshold: 0.6 }
    );
    io.observe(first);
  }

  if (!mount()) {
    /* The gallery is a web component and may upgrade after this runs. */
    var mo = new MutationObserver(function () { if (mount()) mo.disconnect(); });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(function () { mo.disconnect(); }, 10000);
  }
})();

/* ==========================================================================
   COOKIE CONSENT — "Manage cookies" row (design 1a)
   ==========================================================================
   The banner in design 1a carries a third control under the two buttons.
   Pandectes renders only two, so the row is drawn as
   `.cc-compliance::after` in custom.css and made clickable here.

   A pseudo-element cannot receive a listener, so the click is bound to the
   CONTAINER and the pointer position tested against it: anything below the
   buttons' bottom edge is the manage row. Bound on document with a capture
   listener rather than on the element itself, because Pandectes re-renders
   the banner on consent changes and language switches — a listener attached
   to the node would be dropped with it.

   The Preferences button is a real, focusable control doing the same job, so
   nothing here is the only path to preferences.
   -------------------------------------------------------------------------- */
(function () {
  document.addEventListener('click', function (e) {
    // A click that lands on the ::after pseudo-element reports the CONTAINER as
    // its target, but e.target can also be a non-Element (text node) depending
    // on the engine, so guard rather than assume `.closest` exists.
    var t = e.target;
    if (t && t.nodeType !== 1) t = t.parentElement;
    if (!t || !t.closest) return;
    var row = t.closest('#pandectes-banner .cc-compliance');
    if (!row) return;
    // A real button inside the row handles its own click.
    if (e.target.closest('.cc-btn')) return;

    var buttons = row.querySelectorAll('.cc-btn');
    if (!buttons.length) return;
    if (e.target.closest && e.target.closest('.cc-btn')) return;

    // The manage row sits below every button. Compare against the lowest edge
    // so this works in both the desktop row and the stacked mobile column.
    var lowest = 0;
    buttons.forEach(function (b) {
      var r = b.getBoundingClientRect();
      if (r.bottom > lowest) lowest = r.bottom;
    });
    if (e.clientY <= lowest) return;

    if (window.Pandectes && window.Pandectes.fn && window.Pandectes.fn.openPreferences) {
      e.preventDefault();
      window.Pandectes.fn.openPreferences();
    }
  }, true);
})();

/* ==========================================================================
   STICKY MINI BASKET — the options line (design 1c)
   ==========================================================================
   Horizon's <sticky-add-to-cart> renders the variant title in
   `.sticky-add-to-cart__variant`. Design 1c wants the SELECTED OPTIONS and
   the active purchase plan on that line:

       300g · Blue Razz · Subscribe & Save — every 1 month

   The variant title already carries size and flavour, so it is reformatted
   rather than recomputed: the vendor component stays the single source of
   truth for which variant is selected.

   NO MutationObserver. The first version observed the variant node and
   re-applied after the component rewrote it. `writing` guarded our own write,
   but the vendor's rewrite path cleared the cached source and called render()
   from inside the callback, which wrote again, which re-fired the observer —
   an unbounded loop that CRASHED the PDP outright. Verified: blocking
   custom.js loaded the page, restoring it crashed it every time.

   Instead the line is rebuilt on a debounced rAF tick after the events that
   can change it, and the write is skipped when the text already matches. A
   render that loses a race with the component is simply re-run on the next
   tick rather than fought with an observer.
   -------------------------------------------------------------------------- */
(function () {
  var bar = document.querySelector('.sticky-add-to-cart');
  if (!bar) return;

  var variantEl = bar.querySelector('.sticky-add-to-cart__variant');
  if (!variantEl) return;

  var productId = bar.getAttribute('data-product-id');
  var queued = false;

  /* The tier's own label is the display name — "Subscribe & Save", "Bulk Buy",
     "One-time purchase" — so the copy never drifts from the buy box. */
  function activeTier() {
    if (!productId) return null;
    var checked = document.querySelector('input[name="custom-buy-' + productId + '"]:checked');
    if (!checked) return null;

    var label = checked.closest('[data-tier]');
    if (!label) return null;

    var title = label.querySelector('.custom-buy__title');
    var name = title ? title.textContent.trim() : '';
    if (!name) return null;

    /* Delivery frequency is a row of BUTTONS marked `.is-on`, not radios —
       `input[name*="freq"]:checked` matched nothing and the frequency silently
       never appeared. Same pattern as the bulk deals below. */
    if (label.getAttribute('data-tier') === 'subscribe') {
      var freqBtn = label.querySelector('.custom-buy__freq-btn.is-on') ||
                    label.querySelector('.custom-buy__freq-btn');
      var freqText = freqBtn ? freqBtn.textContent.trim() : '';
      if (freqText) name += ' — every ' + freqText.toLowerCase().replace(/^every\s+/, '');
    }

    /* Bulk Buy is not one price — it is a set of multi-tub deals rendered into
       `[data-bundle-slot]`, the active one carrying `.is-on` and a
       `data-deal-qty`. Ian: "if I have bulk buy selected with multiple tubs
       this isn't shown". Without the count the line reads "Bulk Buy" whether
       the shopper picked 2 tubs or 4, and the price beside it then looks
       wrong rather than merely unexplained. */
    if (label.getAttribute('data-tier') === 'bundle') {
      var deal = activeDeal();
      if (deal) {
        var dealTitle = deal.querySelector('.custom-buy__deal-title');
        var qtyText = dealTitle ? dealTitle.textContent.trim() : '';
        if (!qtyText) {
          var q = deal.getAttribute('data-deal-qty');
          if (q) qtyText = q + ' tubs';
        }
        if (qtyText) name += ' — ' + qtyText.toLowerCase();
      }
    }
    return name;
  }

  /* Selecting the Bulk tier does NOT mark a deal `.is-on` — that only happens
     once the shopper clicks one. But a quantity IS in effect from the moment
     the tier is chosen (the first deal), so reading `.is-on` alone left the
     line saying "Bulk Buy" with no count until something was clicked. Fall
     back to the first deal, which is what the buy box charges for. */
  function activeDeal() {
    return document.querySelector('.custom-buy__deal.is-on') ||
           document.querySelector('.custom-buy__deal');
  }

  /* THE WAS-PRICE. The vendor bar renders a compare-at node but leaves it
     EMPTY: Shopify has no compare_at_price on these variants, so there is
     nothing for it to print. The saving is created by the purchase tier —
     subscription discount, or a bulk per-tub rate — and only the buy box
     knows it. `.custom-buy__was` / `.custom-buy__now` on the ACTIVE tier are
     the real figures, so they are mirrored into the bar rather than recomputed
     here; the arithmetic stays in one place.

     Written into the vendor's own price container so the component's variant
     re-render replaces it wholesale rather than leaving a stale figure. */
  function renderPrice() {
    var priceEl = bar.querySelector('.sticky-add-to-cart__price');
    if (!priceEl || !productId) return;

    var checked = document.querySelector('input[name="custom-buy-' + productId + '"]:checked');
    var label = checked && checked.closest('[data-tier]');
    if (!label) return;

    var wasEl = label.querySelector('.custom-buy__was');
    var nowEl = label.querySelector('.custom-buy__now');
    var was = wasEl ? wasEl.textContent.trim() : '';
    var now = nowEl ? nowEl.textContent.trim() : '';

    /* Bulk carries no total anywhere on the page: the card shows a per-tub
       rate ("£8.99 / tub") and the deal shows "£8.99 each", so the bar has to
       multiply. Parsed from the deal's own text rather than a data attribute
       because the app does not expose the figure numerically — if the format
       ever stops matching, the block bails and the vendor price stands rather
       than printing a wrong number. */
    if (label.getAttribute('data-tier') === 'bundle') {
      var deal = activeDeal();
      if (!deal) return;
      var qty = parseInt(deal.getAttribute('data-deal-qty'), 10);
      var eachEl = deal.querySelector('.custom-buy__deal-price');
      var eachTxt = eachEl ? eachEl.textContent : '';
      var m = eachTxt.match(/([£$€])\s*([\d.,]+)/);
      if (!qty || !m) return;

      var unit = parseFloat(m[2].replace(/,/g, ''));
      if (!isFinite(unit)) return;

      var symbol = m[1];
      var total = (unit * qty).toFixed(2);
      // Fixed-price deals carry their exact total (£35.99, not 4 x a rounded
      // £9.00 = £36.00), the same figure Add to bag shows.
      var exact = parseInt(deal.getAttribute('data-deal-total'), 10);
      if (exact > 0) total = (exact / 100).toFixed(2);
      // The saving is the full price times quantity, when there is one.
      var fullEl = label.closest('form, .custom-buy, body').querySelector('[data-tier="once"] .custom-buy__now');
      var fm = fullEl ? fullEl.textContent.match(/[\d.,]+/) : null;
      var full = fm ? parseFloat(fm[0].replace(/,/g, '')) : null;

      var bulkHtml = '';
      if (full && full > unit) {
        bulkHtml += '<s class="custom-mb__was">' + symbol + (full * qty).toFixed(2) + '</s> ';
      }
      bulkHtml += '<span class="custom-mb__now">' + symbol + total + '</span>';

      if (priceEl.getAttribute('data-custom-price') !== bulkHtml) {
        priceEl.setAttribute('data-custom-price', bulkHtml);
        priceEl.innerHTML = bulkHtml;
      }
      return;
    }
    if (!now) return;

    var html = '';
    if (was && was !== now) {
      html += '<s class="custom-mb__was">' + was + '</s> ';
    }
    html += '<span class="custom-mb__now">' + now + '</span>';

    if (priceEl.getAttribute('data-custom-price') === html) return;
    priceEl.setAttribute('data-custom-price', html);
    priceEl.innerHTML = html;
  }

  /* Size and flavour come from the checked variant inputs rather than from the
     bar's own text, so our previous output can never be read back as input —
     the failure mode that made the observer version loop. */
  function variantParts() {
    var out = [];
    var picker = document.querySelector('variant-picker, .variant-picker, [class*="variant-picker"]');
    if (picker) {
      picker.querySelectorAll('input:checked').forEach(function (i) {
        var v = (i.value || '').trim();
        if (v && out.indexOf(v) === -1) out.push(v);
      });
    }
    if (out.length) return out;

    // Fall back to the variant title the component rendered, minus anything we
    // appended (the tier is always last after a middle dot).
    var raw = (variantEl.getAttribute('data-raw') || '').trim();
    if (!raw) return [];
    return raw.split('/').map(function (p) { return p.trim(); }).filter(Boolean);
  }

  function render() {
    queued = false;

    // Cache the component's own text the first time we see a value that is not
    // ours. Ours always contains the middle dot separator.
    var current = variantEl.textContent.trim();
    if (current && current.indexOf(' · ') === -1) {
      variantEl.setAttribute('data-raw', current);
    }

    var parts = variantParts();
    var tier = activeTier();
    if (tier) parts.push(tier);
    if (!parts.length) return;

    var line = parts.join(' · ');
    if (current === line) return;      // nothing to do — and no write, no loop

    variantEl.textContent = line;
    variantEl.style.display = '';      // vendor hides it on single-variant products

    renderPrice();
  }

  /* Coalesce every trigger into one write per frame. */
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(render);
  }

  // Variant changes, purchase tier and frequency all land here.
  document.addEventListener('change', schedule, true);
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('[data-tier], variant-picker, .custom-buy__tier, .custom-buy__deal, .custom-buy__freq-btn')) {
      schedule();
      // The component re-renders asynchronously after a variant change; one
      // late pass catches it without watching the node.
      setTimeout(schedule, 350);
    }
  }, true);

  schedule();
})();

/* ---------------------------------------------------------------------------
   COLLECTION PARALLAX MASTHEAD.

   Enabled by Settings > Warrior collection. The Liquid only renders the hook
   when the setting is on, so this is inert on every other page and theme.

   Ported from the Claude Design spec with two necessary changes:

     1. THE SCROLLER IS NOT THE WINDOW. Horizon scrolls inside `.page-wrapper`
        (`body` is `overflow: hidden`), so the design's `window.scrollY` never
        moves — measured: scrolling the window 600px left the page visually
        still. We bind to the scrolling element and fall back to the window, so
        this keeps working if that ever changes.

     2. FADE DISTANCE IS THE MASTHEAD'S OWN HEIGHT, not a hardcoded 620px. The
        design's number matches its own artboard; a short heading would still be
        half-visible at 620px, and a long one would vanish too early.

   Everything else follows the design: depth 0.35 by default, an opacity floor
   of 0.15 so the block never fully disappears, and one rAF per frame.
   --------------------------------------------------------------------------- */
(function () {
  var DEPTH_FALLBACK = 0.35;
  var OPACITY_FLOOR = 0.15;

  function init() {
    var wrap = document.querySelector('[data-custom-collhead-parallax]');
    if (!wrap) return;

    var inner = wrap.querySelector('.custom-collhead');
    if (!inner) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    /* `.page-wrapper` is the real scroll container; document.scrollingElement is
       the fallback for a theme that scrolls the document itself. */
    var scroller = document.querySelector('.page-wrapper') || document.scrollingElement || document.documentElement;
    var usingWindow = scroller === document.scrollingElement || scroller === document.documentElement;
    var frame = null;

    function depth() {
      var raw = parseFloat(getComputedStyle(wrap).getPropertyValue('--collhead-parallax-depth'));
      return isNaN(raw) ? DEPTH_FALLBACK : raw;
    }

    function apply() {
      frame = null;

      if (reduce.matches) {
        inner.style.transform = '';
        inner.style.opacity = '';
        return;
      }

      var y = usingWindow ? (window.scrollY || 0) : (scroller.scrollTop || 0);

      /* Fade across the distance the masthead is actually VISIBLE for, not its
         own height. The grid starts covering it immediately, so the block is
         only on screen until the gap between the two has closed — that gap is
         the masthead's height plus its own drift. Using the bare height faded
         the heading to 0.22 by 200px, while it was still in full view. */
      var span = (wrap.offsetHeight || 620) * (1 + depth()) + 260;

      inner.style.transform = 'translateY(' + Math.round(y * depth()) + 'px)';
      inner.style.opacity = String(Math.max(OPACITY_FLOOR, 1 - y / span));
    }

    function onScroll() {
      if (frame) return;
      frame = requestAnimationFrame(apply);
    }

    (usingWindow ? window : scroller).addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    if (reduce.addEventListener) reduce.addEventListener('change', apply);
    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ---------------------------------------------------------------------------
   PRESS NEWSROOM — page through announcements, newest first.

   Every row is in the markup; this hides all but the current page, so with no
   JS the full list still reads. Page 1 is the newest. "Newer" steps back
   toward page 1, "Older" steps forward. The pager is only rendered when there
   is more than one page.
   --------------------------------------------------------------------------- */
(function () {
  function init() {
    var lists = document.querySelectorAll('[data-press-news]');
    Array.prototype.forEach.call(lists, function (list) {
      var band = list.parentElement;
      var pager = band && band.querySelector('[data-press-pager]');
      if (!pager) return;
      var rows = list.querySelectorAll('.custom-press__news-row');
      var size = parseInt(list.getAttribute('data-page-size'), 10) || 3;
      var pages = Math.ceil(rows.length / size);
      if (pages < 2) return;
      var prev = pager.querySelector('[data-press-prev]');
      var next = pager.querySelector('[data-press-next]');
      var count = pager.querySelector('[data-press-count]');
      var page = 0;

      function show() {
        for (var i = 0; i < rows.length; i++) {
          rows[i].hidden = Math.floor(i / size) !== page;
        }
        prev.disabled = page === 0;
        next.disabled = page === pages - 1;
        prev.setAttribute('aria-disabled', prev.disabled ? 'true' : 'false');
        next.setAttribute('aria-disabled', next.disabled ? 'true' : 'false');
        if (count) count.textContent = (page + 1) + ' / ' + pages;
      }

      prev.addEventListener('click', function () { if (page > 0) { page--; show(); } });
      next.addEventListener('click', function () { if (page < pages - 1) { page++; show(); } });
      pager.hidden = false;
      show();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ---------------------------------------------------------------------------
   PRESS QUOTES — filter the wall by range.

   The chips ship `hidden`; showing them here means a visitor without JS never
   sees controls that do nothing, and gets the full wall instead.
   --------------------------------------------------------------------------- */
(function () {
  function init() {
    var sections = document.querySelectorAll('[data-press-quotes]');
    Array.prototype.forEach.call(sections, function (section) {
      var chips = section.querySelector('[data-press-chips]');
      if (!chips) return;
      var buttons = chips.querySelectorAll('[data-press-filter]');
      var cards = section.querySelectorAll('.custom-press__qcard');
      chips.hidden = false;
      chips.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-press-filter]');
        if (!btn) return;
        var cat = btn.getAttribute('data-press-filter');
        for (var i = 0; i < buttons.length; i++) {
          buttons[i].setAttribute('aria-pressed', buttons[i] === btn ? 'true' : 'false');
        }
        for (var j = 0; j < cards.length; j++) {
          cards[j].hidden = cat !== 'all' && cards[j].getAttribute('data-cat') !== cat;
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ---------------------------------------------------------------------------
   GUARANTEE — copy the support email.

   Same progressive approach as the press boilerplate: the button is created
   here, so a browser without the Clipboard API shows the address as plain
   selectable text rather than a control that does nothing.

   The design puts a copy control in the hero AND the facts card, sharing one
   state. Only the facts card gets one here — the hero's is a real mailto
   instead, because someone on this page is trying to start a refund, not
   collect an address.
   --------------------------------------------------------------------------- */
(function () {
  var REVERT_MS = 1600;

  function init() {
    var root = document.querySelector('.custom-guar');
    if (!root) return;

    var slot = root.querySelector('[data-guar-email]');
    if (!slot) return;
    if (!navigator.clipboard || !navigator.clipboard.writeText) return;

    var email = root.dataset.supportEmail || slot.textContent.trim();
    var idle = root.dataset.copyLabel || 'Copy';
    var done = root.dataset.copiedLabel || 'Copied';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'custom-guar__copy';
    btn.textContent = idle;
    btn.setAttribute('aria-label', 'Copy ' + email);

    var timer = null;
    btn.addEventListener('click', function () {
      navigator.clipboard.writeText(email).then(function () {
        btn.textContent = done;
        btn.classList.add('is-copied');
        btn.setAttribute('aria-live', 'polite');
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          btn.textContent = idle;
          btn.classList.remove('is-copied');
        }, REVERT_MS);
      }).catch(function () {
        /* Denied permission is not an error state — the address is still
           on the page to select by hand. */
      });
    });

    slot.parentNode.appendChild(btn);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ---------------------------------------------------------------------------
   404 — the excuse generator.

   All excuses are in the markup; this shows one at a time and cycles on click.
   The BUTTON is created here rather than rendered in Liquid, so with JS off a
   visitor sees one excuse and no dead control — which matters more on a 404
   than anywhere else, because it is the page people reach when something has
   already gone wrong.
   --------------------------------------------------------------------------- */
(function () {
  function init() {
    var panel = document.querySelector('[data-e404-excuse]');
    if (!panel) return;

    var items = panel.querySelectorAll('[data-e404-excuse-item]');
    if (items.length < 2) return;

    var label = panel.dataset.buttonLabel || 'Hear another excuse';
    var i = 0;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'custom-e404__excuse-btn';
    btn.textContent = label;

    btn.addEventListener('click', function () {
      items[i].classList.remove('is-on');
      i = (i + 1) % items.length;
      var next = items[i];
      next.classList.add('is-on');
      /* Re-trigger the fade: removing and re-adding the class in the same
         frame is a no-op, so the reflow read is what makes it replay. */
      next.style.animation = 'none';
      void next.offsetWidth;
      next.style.animation = '';
    });

    panel.appendChild(btn);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
   COLLECTION STRIP — SCROLLING ROW (`layout: scroll`)

   Wires the arrow pair and the progress hairline to the native horizontal scroller. The scroller itself
   is plain CSS (overflow + scroll-snap), so touch, trackpad and keyboard scrolling work with or without
   this script; the script only adds the buttons and keeps their state honest.

   - An arrow press moves by as many WHOLE cards as fit, so a page never lands mid-card.
   - Arrows are `disabled` at either end, and the whole control bar hides when every card fits.
   - Re-initialised on `shopify:section:load`, so it keeps working while the section is edited.
   ========================================================================== */
(function () {
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (!root || root.__cstripInit) return;
    var track = root.querySelector('[data-cstrip-track]');
    if (!track) return;
    root.__cstripInit = true;

    var prev = root.querySelector('[data-cstrip-prev]');
    var next = root.querySelector('[data-cstrip-next]');
    var thumb = root.querySelector('[data-cstrip-thumb]');
    var ticking = false;

    // One "page" = the whole cards that fit in view, at least one.
    function pageWidth() {
      var card = track.querySelector('.custom-cstrip__card');
      if (!card) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      var per = card.getBoundingClientRect().width + gap;
      return Math.max(per, Math.floor((track.clientWidth + gap) / per) * per);
    }

    function update() {
      ticking = false;
      var max = track.scrollWidth - track.clientWidth;
      var x = track.scrollLeft;
      root.classList.toggle('custom-cstrip--fits', max <= 1);
      if (prev) prev.disabled = x <= 1;
      if (next) next.disabled = x >= max - 1;
      if (thumb) {
        var ratio = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
        var p = max > 0 ? x / max : 0;
        thumb.style.width = (ratio * 100) + '%';
        // translateX(%) is relative to the thumb's own width, hence the division by ratio.
        thumb.style.transform = 'translateX(' + (ratio > 0 ? (p * (1 - ratio) / ratio) * 100 : 0) + '%)';
      }
    }

    function schedule() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    function go(direction) {
      track.scrollBy({ left: direction * pageWidth(), behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });
    track.addEventListener('scroll', schedule, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(schedule).observe(track);
    else window.addEventListener('resize', schedule);
    update();
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-cstrip]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();

/* ==========================================================================
   FUNCTIONAL CREATINE — NO WAIT WHEN SWITCHING BLENDS

   The five panels are CSS-only (hidden radios + :has()), and every panel's pack shot is `loading="lazy"`.
   A lazy image in a hidden panel does not download until its panel is shown, so each click used to start
   a fresh download and leave an empty box.

   Two fixes, both progressive:
   1. When the section comes within 600px of the viewport, every pack shot is switched to eager so all
      five download in the background before anyone clicks.
   2. A blurred 48px placeholder (in the markup) covers whatever is still in flight; each full image gets
      `is-loaded` when it arrives and fades in over it (CSS, gated on `custom-fc--js`).
   ========================================================================== */
(function () {
  function markLoaded(img) { img.classList.add('is-loaded'); }

  function init(root) {
    if (!root || root.__fcInit) return;
    root.__fcInit = true;
    var imgs = root.querySelectorAll('[data-fc-full]');
    if (!imgs.length) return;

    Array.prototype.forEach.call(imgs, function (img) {
      if (img.complete && img.naturalWidth) {
        markLoaded(img);
      } else {
        img.addEventListener('load', function () { markLoaded(img); }, { once: true });
        // A failed image must not stay invisible behind its blur.
        img.addEventListener('error', function () { markLoaded(img); }, { once: true });
      }
    });
    root.classList.add('custom-fc--js');

    function warm() {
      Array.prototype.forEach.call(imgs, function (img) { img.loading = 'eager'; });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) {
          warm();
          io.disconnect();
        }
      }, { rootMargin: '600px 0px' });
      io.observe(root);
    } else {
      warm();
    }
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('.custom-fc'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();

/* ==========================================================================
   HEADER: USE THE DRAWER UNTIL THE FULL DESKTOP NAV FITS (750–899px)

   Per Ian: rather than squeeze the nav or hide "Support" in Horizon's "More" overflow, show the mobile
   drawer until every item fits, then switch to the desktop bar.

   Horizon already has this mode — `#header-component[data-menu-style='drawer']` — and switches to it when a
   menu's overflow-list reaches its minimum. But it only checks the FIRST (left) menu, with a minimum of 2,
   so the right menu dropping Support into "More" never trips it. Measured 2026-09-30: the right menu
   overflows below 900px and fits from 900px up. So in that band we hold Horizon's own drawer mode on;
   Horizon's CSS and drawer JS do the rest. No vendor file is touched.

   Horizon rewrites the attribute on resize (setHeaderMenuStyle); the observer re-asserts it while the band
   matches, and above the band it leaves Horizon alone.
   ========================================================================== */
(function () {
  var header = document.querySelector('#header-component');
  if (!header || !window.matchMedia) return;
  var band = window.matchMedia('(min-width: 750px) and (max-width: 899px)');

  function apply() {
    if (band.matches && header.dataset.menuStyle !== 'drawer') header.dataset.menuStyle = 'drawer';
  }

  new MutationObserver(apply).observe(header, { attributes: true, attributeFilter: ['data-menu-style'] });
  band.addEventListener('change', function () {
    if (band.matches) apply();
    else window.dispatchEvent(new Event('resize')); // let Horizon recompute its own menu style
  });
  apply();
})();

/* COLLECTION GRID: EACH PANEL SPANS WHAT ITS ROW HAS ROOM FOR (2026-09-30)
   A 2-wide CMS panel that starts in a row's last slot wraps to the next row,
   leaves a hole and pushes a card down. It takes 1 slot there instead, and
   completes the row. Anywhere else it takes 2. On mobile a 1-wide panel that
   starts a row takes the whole row, as before.

   blocks/custom-cms-panel.liquid does this in CSS for the first paint, but CSS
   can only see the panel's own position. The slot also depends on the panels
   BEFORE it (each takes 1 or 2 cells) and on any card that is hidden. So this
   walks the grid in visual order (CSS `order`, then DOM order) and sets each
   panel's span inline, which overrides the CSS. It reruns when the column count
   can change (grid resize) and when filtering, sorting or paging re-renders the
   items (child list change). */
(function () {
  var GRID = '.custom-collection-page .product-grid';
  var mobile = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;

  /* The receipt panel is comfortable from 290px per column (every line fits,
     measured 2026-10-02); narrower, it takes 2 columns. */
  var RECEIPT_MIN = 290;

  function layout(grid) {
    /* Inline orders set by a previous pass (the receipt's one-card shift below)
       are cleared first, so the CSS order is what gets read. */
    Array.prototype.forEach.call(grid.querySelectorAll(':scope > * > .custom-cms-panel--receipt'), function (p) {
      p.parentElement.style.order = '';
    });
    var tracks = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean);
    var cols = tracks.length;
    if (!cols) return;
    var track = parseFloat(tracks[0]) || 0;
    var items = [];
    Array.prototype.forEach.call(grid.children, function (el, i) {
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE') return;
      var cs = getComputedStyle(el);
      if (cs.display === 'none') return;
      items.push({ el: el, i: i, order: parseInt(cs.order, 10) || 0 });
    });
    items.sort(function (a, b) { return a.order - b.order || a.i - b.i; });

    function panelOf(it) { return it && it.el.querySelector(':scope > .custom-cms-panel'); }

    var slot = 0;
    for (var k = 0; k < items.length; k++) {
      var item = items[k];
      var panel = panelOf(item);
      var span = 1;
      if (panel) {
        var col = slot % cols;
        var receipt = panel.classList.contains('custom-cms-panel--receipt');
        if (receipt && mobile && mobile.matches) {
          span = cols;   /* full row; the CSS already moved it to a row start */
        } else if (receipt && track < RECEIPT_MIN && cols >= 2) {
          /* It needs 2 columns. In a row's last slot it would wrap and leave a
             hole, and 1 column is too narrow, so it moves one card later: that
             card fills the slot and the receipt starts the next row. Ties in
             `order` break by DOM order, and panels render after every card. */
          var next = items[k + 1];
          if (col === cols - 1 && next && !panelOf(next)) {
            item.el.style.order = next.order;
            items[k] = next;
            items[k + 1] = item;
            slot += 1;   /* the card */
            k += 1;      /* the receipt is processed next */
          }
          span = 2;
        } else if (panel.classList.contains('custom-cms-panel--span-2')) {
          span = col <= cols - 2 ? 2 : 1;
        } else if (mobile && mobile.matches && col === 0) {
          span = cols;
        }
        var value = 'span ' + span;
        if (item.el.style.gridColumn !== value) item.el.style.gridColumn = value;
      }
      slot += span;
    }
  }

  function watch(grid) {
    if (grid.__customPanelSpan) return;
    grid.__customPanelSpan = true;
    var queued = false;
    function queue() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; layout(grid); });
    }
    if (window.ResizeObserver) new ResizeObserver(queue).observe(grid);
    new MutationObserver(queue).observe(grid, { childList: true });
    /* Crossing 749px changes each panel's CSS `order` (the mobile full-row shift in
       blocks/custom-cms-panel.liquid) without necessarily resizing the grid box
       in the same frame; measured: a mobile -> desktop resize left both panels on
       their mobile spans. Re-run on the breakpoint itself. */
    if (mobile && mobile.addEventListener) mobile.addEventListener('change', queue);
    layout(grid);
  }

  function init() {
    document.querySelectorAll(GRID).forEach(watch);
  }

  /* Filtering morphs the section, which can replace the grid element itself.
     Batched to one check per frame, since this sees every DOM change. */
  if (!document.querySelector('.custom-collection-page')) return;
  var pending = false;
  new MutationObserver(function () {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () { pending = false; init(); });
  }).observe(document.body, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* COLLECTION BAR: RE-MEASURE THE FILTER PILLS (2026-09-30)
   Horizon's <overflow-list> re-measures only when its own box resizes. The pill
   strip is sized to its content (so "Clear all" follows the pills), so it does
   not always resize when room appears, and a measurement taken at the wrong
   moment sticks. Tested case: filtering from the drawer on desktop re-renders
   the bar while the modal is open, the list measures 0px wide and folds all
   three pills, and they stayed folded at 1280px after the drawer closed.

   So the list gets its own `reflow` event, debounced, whenever its room may
   have changed:
     - the window resizes;
     - any dialog closes (the filter drawer), since `close` does not bubble,
       it is caught in the capture phase;
     - the bar's content changes (filtering, sorting and clearing morph it).
   See custom.css, "COLLECTION FILTER BAR". */
(function () {
  if (!document.querySelector('.custom-collection-page')) return;
  var SEL = '.custom-collection-page .facets--horizontal overflow-list';
  var timer;
  function reflowSoon() {
    clearTimeout(timer);
    timer = setTimeout(function () {
      document.querySelectorAll(SEL).forEach(function (list) {
        list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
      });
    }, 150);
  }
  window.addEventListener('resize', reflowSoon);
  document.addEventListener('close', reflowSoon, true);

  var bar = document.querySelector('.custom-collection-page .facets--horizontal');
  if (bar && window.MutationObserver) {
    new MutationObserver(function (records) {
      /* The list's own slot moves are attribute changes on its children, not
         childList changes, so this does not retrigger itself. */
      for (var i = 0; i < records.length; i++) {
        if (records[i].type === 'childList') { reflowSoon(); return; }
      }
    }).observe(bar, { childList: true, subtree: true });
  }
})();

/* FILTER DRAWER: A CLICK ON THE DIMMED PAGE CLOSES IT (2026-09-30)
   On the collection page the filter drawer overlays the page with a dim (see
   custom.css, "THE FILTER DRAWER OVERLAYS THE PAGE"). That dim is the drawer
   element's ::before, so a click on it has the drawer element itself as its
   target. Clicks inside the panel target the dialog or its content, so they
   never match. `close()` is the component's own method, the same one the
   close button calls. */
document.addEventListener('click', function (event) {
  var target = event.target;
  if (target && target.id === 'filters-drawer' && typeof target.close === 'function') {
    target.close();
  }
});

/* MOBILE DRAWER DRILL-DOWN (2026-10-01)
   Markup: snippets/custom.mobile-drawer.liquid. Motion: custom.css,
   "MOBILE DRAWER — drill-down". This only moves classes between levels:
     is-on    the level on screen
     is-past  an ancestor of it (slid left, faded)
     is-next  anything else (parked off to the right)
   Off-screen levels are `inert`, so neither Tab nor a screen reader reaches them.
   Horizon's header-drawer still owns open/close; when it closes, the drill resets
   to the top level once the slide-out has finished, so the next open starts clean.

   Everything is looked up from the drawer at event time and the path lives on the
   element, because Horizon can re-render the header through the Section Rendering
   API — listeners bound to the old node would silently stop working. */
(function () {
  function levelsOf(drawer) {
    var map = {};
    drawer.querySelectorAll('[data-mdrawer-level]').forEach(function (el) {
      map[el.getAttribute('data-mdrawer-level')] = el;
    });
    return map;
  }

  function render(drawer, focusId) {
    var path = drawer._mdPath || [];
    var levels = levelsOf(drawer);
    var c = ['root'].concat(path);
    var current = c[c.length - 1];
    Object.keys(levels).forEach(function (id) {
      var el = levels[id];
      var state = id === current ? 'is-on' : c.indexOf(id) >= 0 ? 'is-past' : 'is-next';
      el.classList.remove('is-on', 'is-past', 'is-next');
      el.classList.add(state);
      if (state === 'is-on') { el.removeAttribute('inert'); } else { el.setAttribute('inert', ''); }
    });
    drawer.querySelectorAll('[data-mdrawer-go]').forEach(function (b) {
      b.setAttribute('aria-expanded', path.indexOf(b.getAttribute('data-mdrawer-go')) >= 0 ? 'true' : 'false');
    });

    var deep = path.length > 0;
    drawer.classList.toggle('is-deep', deep);
    // Back buttons are the last row of each drilled level; the inert attribute on
    // inactive levels already keeps the hidden ones out of the tab order.

    // The trail starts at the first level you drilled into: "Menu" adds nothing.
    var crumbs = drawer.querySelector('[data-mdrawer-crumbs]');
    if (crumbs) {
      crumbs.textContent = '';
      path.forEach(function (id, d) {
        var li = document.createElement('li');
        if (d > 0) {
          var sep = document.createElement('span');
          sep.className = 'custom-mdrawer__crumbsep';
          sep.setAttribute('aria-hidden', 'true');
          sep.textContent = '/';
          li.appendChild(sep);
        }
        var title = (levels[id] && levels[id].getAttribute('data-mdrawer-title')) || '';
        var node;
        if (d < path.length - 1) {
          node = document.createElement('button');
          node.type = 'button';
          node.className = 'custom-mdrawer__crumb';
          node.setAttribute('data-mdrawer-jump', String(d + 1));
        } else {
          node = document.createElement('span');
          node.className = 'custom-mdrawer__crumbnow';
          node.setAttribute('aria-current', 'page');
        }
        node.textContent = title;
        li.appendChild(node);
        crumbs.appendChild(li);
      });
    }

    if (focusId && levels[focusId]) {
      // First menu item, not Back (Back is now the top row of a drilled level).
      var first = levels[focusId].querySelector('.custom-mdrawer__list a, .custom-mdrawer__list button') || levels[focusId].querySelector('a, button');
      if (first) first.focus({ preventScroll: true });
    }
    save(drawer);
  }

  /* REMEMBER POSITION (Theme settings > Warrior mobile menu > "Remember menu
     position", on by default; Ian 2026-10-05). The drawer keeps the level the
     shopper drilled into (e.g. Shop > Shop by category) and its scroll, across
     closing and across page loads, for the browsing session (sessionStorage:
     a convenience, so any storage failure just means the menu starts at the top). */
  var KEY = 'warrior:mdrawer';
  function remembers(drawer) { return drawer.hasAttribute('data-mdrawer-remember'); }
  function save(drawer) {
    if (!remembers(drawer)) return;
    var path = drawer._mdPath || [];
    var lvl = drawer.querySelector('[data-mdrawer-level].is-on');
    try { sessionStorage.setItem(KEY, JSON.stringify({ path: path, top: lvl ? lvl.scrollTop : 0 })); } catch (e) {}
  }
  function restore(drawer) {
    if (!remembers(drawer) || drawer._mdRestored) return;
    drawer._mdRestored = true;
    var saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (e) {}
    if (!saved || !saved.path || !saved.path.length) return;
    var levels = levelsOf(drawer);
    // Keep the path only while every level still exists (the menu can change).
    var path = [];
    for (var i = 0; i < saved.path.length; i++) { if (!levels[saved.path[i]]) break; path.push(saved.path[i]); }
    if (!path.length) return;
    drawer._mdPath = path;
    render(drawer);
    var lvl = levels[path[path.length - 1]];
    if (lvl && saved.top) lvl.scrollTop = saved.top;
  }
  var saveTimer = null;
  document.addEventListener('scroll', function (event) {
    var lvl = event.target && event.target.closest && event.target.closest('[data-mdrawer-level]');
    if (!lvl) return;
    var drawer = lvl.closest('[data-mdrawer]');
    if (!drawer || !remembers(drawer)) return;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(function () { save(drawer); }, 150);
  }, true);
  function restoreAll() { document.querySelectorAll('[data-mdrawer]').forEach(restore); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreAll); else restoreAll();
  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('[data-mdrawer]').forEach(function (d) { d._mdRestored = false; restore(d); });
  });

  document.addEventListener('click', function (event) {
    var drawer = event.target.closest && event.target.closest('[data-mdrawer]');
    if (!drawer) return;
    var path = drawer._mdPath || (drawer._mdPath = []);
    var levels = levelsOf(drawer);

    var go = event.target.closest('[data-mdrawer-go]');
    if (go) {
      var id = go.getAttribute('data-mdrawer-go');
      if (!levels[id]) return;               // no level rendered: let the link navigate
      event.preventDefault();
      levels[id].scrollTop = 0;
      path.push(id);
      render(drawer, id);
      return;
    }
    if (event.target.closest('[data-mdrawer-back]')) {
      var left = path.pop();
      render(drawer);
      var opener = left && drawer.querySelector('[data-mdrawer-go="' + left + '"]');
      if (opener) opener.focus({ preventScroll: true });
      return;
    }
    var jump = event.target.closest('[data-mdrawer-jump]');
    if (jump) {
      drawer._mdPath = path.slice(0, parseInt(jump.getAttribute('data-mdrawer-jump'), 10));
      render(drawer);
    }
  });

  // Reset after the panel has slid away (0.5s in custom.css), never while visible.
  // Watches every `<details>` that holds a drawer, re-checked on header re-render.
  var watched = new WeakSet();
  function watch() {
    if (!window.MutationObserver) return;
    document.querySelectorAll('[data-mdrawer]').forEach(function (drawer) {
      var details = drawer.closest('details');
      if (!details || watched.has(details)) return;
      watched.add(details);
      var timer = null;
      new MutationObserver(function () {
        var d = details.querySelector('[data-mdrawer]');
        clearTimeout(timer);
        // Opening: put the shopper back where they left off (no-op once restored).
        if (d && remembers(d) && (details.classList.contains('menu-open') || details.open)) {
          restore(d);
          // Re-apply on every open: the drawer's markup can be re-rendered after load,
          // which resets the level classes while the saved path survives.
          if (d._mdPath && d._mdPath.length) render(d);
        }
        if (d && !remembers(d) && !details.classList.contains('menu-open') && d._mdPath && d._mdPath.length) {
          timer = setTimeout(function () { d._mdPath = []; render(d); }, 520);
        }
      }).observe(details, { attributes: true, attributeFilter: ['class', 'open'] });
    });
  }
  watch();
  document.addEventListener('shopify:section:load', watch);
})();

/* PDP TOP SECTION — design A2 (2026-10-02)
   Three small behaviours for the new top section:
   1. the heading's flavour (and its colour) follows the selected flavour;
   2. (removed 2026-10-02: the flavour · size · plan chips under the gallery);
   3. "Ships today if ordered within …" counts down to the dispatch cut-off.
   All run off the page's own state; nothing here changes what is added to the cart. */
(function () {
  var SCOPE = "main[data-template^='product']";
  var main = document.querySelector(SCOPE);
  if (!main) return;

  // Multi-tub bundles (2026-10-11): "Mix (…)" -> "Mixed flavours", "4 x Chocolate"
  // -> "Chocolate". Same rule as snippets/custom.pdp-heading.liquid.
  function clean(v) {
    var s = (v || '').split(' -')[0].trim();
    if (/^Mix\b/.test(s)) return 'Mixed flavours';
    return s.replace(/^\d+ x /, '');
  }

  // The Flavour fieldset, not the first radio: on Creatine Monohydrate Size is
  // option 1, so the first checked radio is the size.
  function flavour() {
    var picked = main.querySelector('variant-picker .custom-flavour-tiles input[type="radio"]:checked') ||
      main.querySelector('variant-picker input[type="radio"]:checked');
    return picked ? clean(picked.value) : '';
  }

  function paint() {
    var fl = flavour();
    var h = main.querySelector('[data-pdp-heading-variant]');
    if (h && fl) {
      // With the write-on effect the text lives in a child (the writing layer is
      // its sibling), so only that child's text is replaced.
      var ht = h.querySelector('[data-custom-wall-host]') || h;
      if (ht.textContent !== fl) ht.textContent = fl;
      // Colour = the selected swatch dot in the FIRST flavour row (Ian,
      // 2026-10-02), so the heading always matches what the shopper sees and
      // follows option 1 on bundles. A dot with no colour (Unflavoured) leaves
      // the text ink. The flavour → colour map is the fallback when no dot is
      // found (non-tile pickers).
      var picked = main.querySelector('variant-picker .custom-flavour-tiles input[type="radio"]:checked');
      var tile = picked && picked.closest('label');
      var dot = tile && tile.querySelector('.custom-flavour-dot');
      var dotColour = dot ? dot.style.getPropertyValue('--custom-dot').trim() : '';
      if (dot) {
        if (dotColour) h.style.setProperty('--pdp-flavour', dotColour);
        else h.style.removeProperty('--pdp-flavour');
      } else {
        var map = {};
        try { map = JSON.parse('{' + (h.getAttribute('data-colours') || '') + '}'); } catch (e) {}
        if (map[fl]) h.style.setProperty('--pdp-flavour', map[fl]);
        else h.style.removeProperty('--pdp-flavour');
      }
    }

    // The Flavour / Size / Plan chips under the gallery were removed (Ian,
    // 2026-10-02).
  }

  // Horizon re-renders parts of the form on a variant change, so repaint a few
  // times rather than observing mutations (which would loop on our own writes).
  function soon() { setTimeout(paint, 60); setTimeout(paint, 400); setTimeout(paint, 1000); }
  document.addEventListener('change', soon);
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.custom-buy__freq-btn, .custom-buy__deal, [data-tier]')) soon();
  });
  soon();

  /* Ships today countdown. Cut-off is "HH:MM" UK time, Monday–Friday. */
  var ships = main.querySelector('[data-pdp-ships]');
  if (ships) {
    var parts = (ships.getAttribute('data-cutoff') || '14:00').split(':');
    var ch = parseInt(parts[0], 10) || 14, cm = parseInt(parts[1], 10) || 0;
    var text = ships.querySelector('[data-pdp-ships-text]');
    var tick = function () {
      // Work in UK local time whatever the visitor's timezone.
      var now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
      var day = now.getDay();
      var cut = new Date(now); cut.setHours(ch, cm, 0, 0);
      var weekday = day >= 1 && day <= 5;
      text.textContent = '';
      var b = document.createElement('b');
      if (weekday && now < cut) {
        var ms = cut - now;
        var hh = Math.floor(ms / 3600000), mm = Math.floor(ms % 3600000 / 60000), ss = Math.floor(ms % 60000 / 1000);
        b.textContent = 'Ships today';
        text.appendChild(b);
        text.appendChild(document.createTextNode(' if ordered within '));
        var t = document.createElement('span');
        t.className = 'custom-pdp__ships-time';
        t.textContent = hh + ' hr ' + mm + ' min ' + ss + ' sec';
        text.appendChild(t);
      } else {
        var next = (day === 5 || day === 6) ? 'Monday' : (day === 0 ? 'Monday' : 'tomorrow');
        if (day === 5 && now < cut) next = 'today';
        b.textContent = 'Ships ' + next;
        text.appendChild(b);
        text.appendChild(document.createTextNode(' from Manchester'));
      }
    };
    tick();
    setInterval(tick, 1000);
  }
})();

/* ---------------------------------------------------------------------------
   PDP — never strand the shopper on a combination that does not exist.
   Creatine Monohydrate 1KG comes in Unflavoured only. Picking 1KG while Blue
   Razz is selected left Horizon showing "Unavailable" with Add to bag disabled
   and every other flavour crossed out (verified 2026-10-02). After a choice,
   if the checked value in any fieldset is now unavailable, the first available
   value in that fieldset is selected instead, so the size the shopper asked for
   wins and the button stays live. Runs once per shopper action, after
   Horizon's re-render, so it cannot loop on its own change.
   --------------------------------------------------------------------------- */
(function () {
  var main = document.querySelector("main[data-template^='product']");
  if (!main) return;
  var busy = false;
  function fix() {
    if (busy) return;
    var fieldsets = main.querySelectorAll('variant-picker fieldset');
    for (var i = 0; i < fieldsets.length; i++) {
      var checked = fieldsets[i].querySelector("input[type='radio']:checked");
      if (!checked || checked.getAttribute('aria-disabled') !== 'true') continue;
      var ok = fieldsets[i].querySelector("input[type='radio']:not([aria-disabled='true'])");
      if (!ok) return;
      busy = true;
      var label = ok.closest('label');
      (label || ok).click();
      setTimeout(function () { busy = false; }, 1500);
      return;
    }
  }
  main.addEventListener('change', function (e) {
    if (busy || !e.target.closest || !e.target.closest('variant-picker')) return;
    setTimeout(fix, 500);
    setTimeout(fix, 1200);
  });
})();

/* ---------------------------------------------------------------------------
   PDP GALLERY SPEED (Ian, 2026-10-02) — make a flavour change feel instant.

   Measured before (RAW, 1440, tap Cherry Bakewell): Horizon's section fetch
   took 465ms (102KB) before anything changed, then the 1920w hero downloaded
   in 875ms (225KB): the sharp image arrived 1,480ms after the tap.

   Three layers, none touching vendor code:
   1. PRELOAD. Each variant's featured image URL is in the purchase block's
      JSON (`m`). After the page is idle, a ~2KB LQIP for every flavour is
      fetched, then (only on a fast, non-Save-Data connection) each full hero,
      one at a time at low priority, at the width the browser already chose
      for the current hero — so the later request is a cache hit. Hovering,
      touching or focusing a tile preloads that flavour immediately.
   2. SWAP ON TAP. The hero <img> is pointed at the new flavour in the same
      tick as the tap, with the same srcset widths and sizes Horizon uses, so
      Horizon's own re-render ~0.5s later asks for URLs already in cache.
   3. LOADING STATE. Until the sharp image has decoded, the slide shows the
      flavour's LQIP blurred and scaled, with a shimmer (a skeleton card); the
      full image fades in over it.
   --------------------------------------------------------------------------- */
(function () {
  var main = document.querySelector("main[data-template^='product']");
  if (!main) return;
  var buy = main.querySelector('[data-custom-buy]');
  var vjEl = buy && buy.querySelector('[data-custom-buy-variants]');
  if (!vjEl) return;
  var VJ;
  try { VJ = JSON.parse(vjEl.textContent); } catch (e) { return; }
  if (!VJ || !VJ.variants) return;

  var LQIP_W = 64; // ~2-3KB; big enough to read as the product when upscaled
  var conn = navigator.connection || {};
  var fast = !conn.saveData && !/(^|-)2g$|^3g$/.test(conn.effectiveType || '4g');

  function heroImg() {
    return main.querySelector('.product-information__media slideshow-slide img');
  }
  // Same CDN quality as the gallery (Theme settings > Warrior image quality >
  // Product gallery images, rendered by custom.img). Read off the hero's own URL
  // so a preload is byte-for-byte the URL the gallery later asks for: without it
  // every preload was full quality AND a cache miss on swap (2026-10-06).
  function qualityParam() {
    var img = heroImg();
    var m = img && /[?&]quality=(\d+)/.exec(img.currentSrc || img.src || '');
    return m ? '&quality=' + m[1] : '';
  }
  function withWidth(url, w) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w + qualityParam();
  }
  // The width the browser picked for the current hero (e.g. 1920 on a 2x
  // desktop). Reusing it means a preload is the exact URL later requested.
  function heroWidth() {
    var img = heroImg();
    var m = img && /[?&]width=(\d+)/.exec(img.currentSrc || img.src || '');
    return m ? parseInt(m[1], 10) : 1200;
  }
  function srcWidths() {
    var img = heroImg();
    var ws = [];
    ((img && img.getAttribute('srcset')) || '').split(',').forEach(function (part) {
      var m = /(\d+)w\s*$/.exec(part.trim());
      if (m) ws.push(parseInt(m[1], 10));
    });
    return ws.length ? ws : [352, 832, 1200, 1600, 1920];
  }

  var cache = {};
  var loaded = {}; // url -> true once fully downloaded (checked synchronously on tap)
  function load(url) {
    if (!url) return Promise.resolve();
    if (cache[url]) return cache[url];
    cache[url] = new Promise(function (resolve) {
      var i = new Image();
      i.decoding = 'async';
      try { i.fetchPriority = 'low'; } catch (e) {}
      i.onload = function () { loaded[url] = true; resolve(i); };
      i.onerror = function () { resolve(i); };
      i.src = url;
    });
    return cache[url];
  }
  function lqip(v) { return v && v.m ? withWidth(v.m, LQIP_W) : null; }
  function full(v) { return v && v.m ? withWidth(v.m, heroWidth()) : null; }

  /* 1. Background preload, after the page is idle. */
  function idle(fn) {
    if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 4000 });
    else setTimeout(fn, 2000);
  }
  function backgroundPreload() {
    var seen = {};
    var list = VJ.variants.filter(function (v) {
      if (!v.m || seen[v.m]) return false;
      seen[v.m] = true;
      return true;
    });
    list.forEach(function (v) { load(lqip(v)); });           // tiny, all of them
    if (!fast) return;                                       // full size only on fast links
    var i = 0;
    (function next() {                                       // one at a time
      if (i >= list.length) return;
      load(full(list[i++])).then(function () { idle(next); });
    })();
  }
  if (document.readyState === 'complete') idle(backgroundPreload);
  else window.addEventListener('load', function () { idle(backgroundPreload); });

  /* Which variant would a tap on this tile give? The tile's value in its
     option slot, the current selection everywhere else. */
  function selection() {
    var picker = main.querySelector('variant-picker');
    var want = [];
    for (var i = 0; i < VJ.options.length; i++) {
      var c = picker && picker.querySelector("input[type='radio'][data-option-name='" + VJ.options[i] + "']:checked");
      want.push(c ? c.value : null);
    }
    return want;
  }
  function variantFor(want) {
    for (var j = 0; j < VJ.variants.length; j++) {
      var o = VJ.variants[j].o, ok = true;
      for (var m = 0; m < o.length; m++) if (want[m] !== null && o[m] !== want[m]) { ok = false; break; }
      if (ok) return VJ.variants[j];
    }
    return null;
  }
  function variantForInput(input) {
    var want = selection();
    var idx = VJ.options.indexOf(input.getAttribute('data-option-name'));
    if (idx < 0) return null;
    want[idx] = input.value;
    return variantFor(want);
  }

  /* Intent preload: hover / touch / keyboard focus on a tile. */
  function onIntent(e) {
    var label = e.target.closest && e.target.closest('variant-picker .variant-option__button-label');
    var input = label && label.querySelector("input[type='radio']");
    if (!input) return;
    var v = variantForInput(input);
    if (v) { load(lqip(v)); load(full(v)); }
  }
  main.addEventListener('pointerover', onIntent, { passive: true });
  main.addEventListener('touchstart', onIntent, { passive: true });
  main.addEventListener('focusin', onIntent);

  /* 2 + 3. Swap on tap, with the loading state.
     The LQIP is shown IN THE SAME <img>: it is swapped to the tiny file first
     (same box, same object-fit, so the preview is exactly the size of the real
     image; the earlier separate blurred layer was cover-sized and scaled 4%
     larger, which made the image visibly jump when it landed), blurred by CSS,
     then the full file is set once it has downloaded and the blur eases off. */
  function showLoading(slide) { slide.classList.add('custom-media-loading'); }
  function clearLoading(slide) { slide.classList.remove('custom-media-loading'); }
  var swapToken = 0;
  function setFull(img, v) {
    img.setAttribute('srcset', srcWidths().map(function (w) { return withWidth(v.m, w) + ' ' + w + 'w'; }).join(', '));
    img.src = full(v);
  }
  function swapHero(v) {
    var img = heroImg();
    if (!img || !v || !v.m) return;
    var target = full(v);
    if ((img.currentSrc || img.src) === target) return;
    var slide = img.closest('slideshow-slide') || img.parentElement;
    var token = ++swapToken;
    if (loaded[target]) { setFull(img, v); clearLoading(slide); return; } // preloaded: instant
    showLoading(slide);
    img.removeAttribute('srcset');
    img.src = lqip(v);
    var finish = function () {
      if (token !== swapToken) return;          // a newer tap took over
      var cur = heroImg();
      if (cur) { setFull(cur, v); clearLoading(cur.closest('slideshow-slide') || slide); }
    };
    load(target).then(finish);
    setTimeout(function () { if (token === swapToken) clearLoading(slide); }, 6000);
  }

  /* First paint on a slow connection: until each gallery image has loaded, its
     slide shows the tile ground with a shimmer (a skeleton card) and, as soon as
     it arrives, the image's own ~2KB LQIP as a background of the <img> itself,
     sized exactly like the image (contain, content box). The real image then
     paints straight over it. The LCP request is never restarted. */
  function initialPreview() {
    var imgs = main.querySelectorAll('.product-information__media slideshow-slide img');
    for (var i = 0; i < imgs.length; i++) {
      (function (img) {
        if (img.complete && img.naturalWidth) return;
        var slide = img.closest('slideshow-slide');
        if (!slide) return;
        slide.classList.add('custom-media-pending');
        var src = img.currentSrc || img.src || '';
        if (/[?&]width=\d+/.test(src)) {
          img.style.backgroundImage = 'url("' + src.replace(/([?&])width=\d+/, '$1width=' + LQIP_W) + '")';
        }
        var done = function () { slide.classList.remove('custom-media-pending'); img.style.backgroundImage = ''; };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      })(imgs[i]);
    }
  }
  initialPreview();

  main.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.closest || !input.closest('variant-picker') || input.type !== 'radio') return;
    var v = variantFor(selection());
    if (v) swapHero(v);
  });

  // Horizon re-renders the gallery after its fetch; if the new hero is not
  // loaded yet, give it the same loading state so it never pops in.
  var media = main.querySelector('.product-information__media');
  if (media && 'MutationObserver' in window) {
    new MutationObserver(function () {
      var img = heroImg();
      if (!img || img.complete) return;
      var slide = img.closest('slideshow-slide');
      if (!slide || slide.classList.contains('custom-media-loading') || slide.classList.contains('custom-media-pending')) return;
      initialPreview(); // same treatment as first paint for Horizon's re-rendered slides
    }).observe(media, { childList: true, subtree: true });
  }
})();

/* ---------------------------------------------------------------------------
   PDP — variant changes fetch the product section only, not the whole page.
   Horizon's variant-picker requests `/products/<handle>?option_values=…`, i.e.
   the ENTIRE page (header, editorial, app scripts, JSON-LD: 587KB decoded,
   97KB over the wire on RAW, measured 2026-10-02), then morphs only the main
   product section out of it. Adding `section_id=<main section>` returns just
   that section. Only the main PDP's own requests are touched: same product
   path as this page, `option_values` present, no section_id yet. Combined-
   listing jumps to another product URL are left alone (Horizon morphs `main`
   from those and needs the full document).
   --------------------------------------------------------------------------- */
(function () {
  var main = document.querySelector("main[data-template^='product']");
  if (!main || window.__customSectionFetch) return;
  var section = main.querySelector('product-component, .product-information__grid');
  section = section && section.closest('[id^="shopify-section-"]');
  if (!section) return;
  var sectionId = section.id.replace('shopify-section-', '');
  var path = location.pathname.replace(/\/$/, '');
  window.__customSectionFetch = true;
  var orig = window.fetch;
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (url.indexOf('option_values=') !== -1 && url.indexOf('section_id=') === -1) {
        var u = new URL(url, location.origin);
        if (u.origin === location.origin && u.pathname.replace(/\/$/, '') === path) {
          u.searchParams.set('section_id', sectionId);
          input = typeof input === 'string' ? u.pathname + u.search : new Request(u.pathname + u.search, input);
        }
      }
    } catch (e) {}
    return orig.call(this, input, init);
  };
})();

/* ---------------------------------------------------------------------------
   HEADER — close menus that the layout no longer shows (Ian, 2026-10-02:
   "the entire page has gone white … opening and closing menus and manually
   adjusting browser widths").

   Reproduced: open the desktop mega menu, narrow the window. Horizon swaps the
   nav for the drawer (`data-menu-style` becomes "drawer", the nav is
   display:none) but header-menu stays aria-expanded, so its 587px
   `.header__underlay-open` panel stays painted over the page. The reverse also
   leaves a stale state: the mobile drawer left open when widening to desktop
   keeps the page scroll-locked behind an invisible panel.

   So after every resize, a menu that is open while its own trigger is hidden is
   closed through Horizon's own code paths: Escape on header-menu (its
   force-close) and HeaderDrawer.close().
   --------------------------------------------------------------------------- */
(function () {
  var header = document.getElementById('header-component');
  if (!header) return;
  // `data-menu-style` is NOT the switch: Horizon sets it from touch support and
  // nav overflow, and it stayed "menu" at 500px. The nav and the burger are
  // shown/hidden by CSS breakpoints, so what decides is what is VISIBLE.
  function shown(el) { return !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden'; }
  function sync() {
    var menu = header.querySelector('header-menu');
    var navList = menu && (menu.querySelector('.menu-list') || menu);
    if (menu && menu.querySelector('[aria-expanded="true"]') && !shown(navList)) {
      menu.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    }
    var drawer = header.querySelector('header-drawer');
    var details = drawer && drawer.querySelector('details');
    var burger = details && details.querySelector('summary');
    if (details && details.open && !shown(burger) && typeof drawer.close === 'function') drawer.close();
  }
  var t = null;
  window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(sync, 120); });
})();

/* ---------------------------------------------------------------------------
   PDP CUSTOMER VIDEOS (custom.pdp-ugc) — tap-to-play band, drinksult.com style.
   - Nothing downloads until a card is tapped (preload="none" + data-src); HLS
     where the browser plays it natively, else the MP4.
   - Tapping plays that video WITH sound (a tap is the gesture browsers need)
     and pauses every other; tapping again pauses. `is-playing` hides the icon.
   - A playing video pauses when it scrolls out of view.
   - The bar under the row tracks the scroll: thumb width = visible share,
     position = scrolled share. Hidden when every card fits.
   - Mouse users can drag the row (touch and trackpads scroll natively).
   --------------------------------------------------------------------------- */
(function () {
  var roots = document.querySelectorAll('[data-custom-ugc]');
  if (!roots.length) return;

  function load(v) {
    if (v.getAttribute('src') || !v.dataset.src) return;
    v.src = v.dataset.hls && v.canPlayType('application/vnd.apple.mpegurl') ? v.dataset.hls : v.dataset.src;
  }
  function stopAll(except) {
    document.querySelectorAll('.custom-pugc__video').forEach(function (o) {
      if (o !== except && !o.paused) o.pause();
    });
  }

  roots.forEach(function (root) {
    var track = root.querySelector('[data-ugc-track]');
    var bar = root.querySelector('[data-ugc-bar]');
    var thumb = bar && bar.querySelector('[data-ugc-thumb]');
    if (!track) return;

    root.querySelectorAll('.custom-pugc__video').forEach(function (v) {
      var card = v.closest('.custom-pugc__card');
      var btn = card.querySelector('[data-ugc-play]');
      v.addEventListener('play', function () {
        card.classList.add('is-playing');
        if (btn) btn.setAttribute('aria-label', btn.getAttribute('aria-label').replace(/^Play/, 'Pause'));
      });
      v.addEventListener('pause', function () {
        card.classList.remove('is-playing');
        if (btn) btn.setAttribute('aria-label', btn.getAttribute('aria-label').replace(/^Pause/, 'Play'));
      });
    });

    var dragged = false;
    root.addEventListener('click', function (e) {
      if (dragged) { dragged = false; return; }
      var card = e.target.closest('.custom-pugc__card');
      if (!card) return;
      var v = card.querySelector('video');
      if (!v.paused) { v.pause(); return; }
      stopAll(v);
      load(v);
      v.muted = false;
      var p = v.play();
      if (p && p.catch) p.catch(function () { v.muted = true; v.play().catch(function () {}); });
    });

    /* COVER FRAME (Ian, 2026-10-03): a video with `data-frame` (seconds, from
       warrior.pdp_ugc_frames) shows that frame instead of Shopify's preview.
       Shortly before the card scrolls in, the MP4 loads with preload=metadata
       and a `#t=` fragment, so the browser fetches only enough to paint that
       frame; the poster stays until the frame is ready, so there is no blank
       flash. Browsers that will not paint a frame without a tap (iOS Safari)
       simply keep the poster. Playback still starts from the beginning. */
    var framed = root.querySelectorAll('.custom-pugc__video[data-frame]');
    function cue(v) {
      if (v.getAttribute('src') || !v.dataset.src) return;
      v.preload = 'metadata';
      v.muted = true;
      v.addEventListener('loadeddata', function () { v.removeAttribute('poster'); }, { once: true });
      v.src = v.dataset.src + '#t=' + v.dataset.frame;
      v.dataset.cued = '1';
    }
    if (framed.length) {
      if ('IntersectionObserver' in window) {
        var pre = new IntersectionObserver(function (es) {
          es.forEach(function (en) { if (en.isIntersecting) { cue(en.target); pre.unobserve(en.target); } });
        }, { rootMargin: '0px 400px 300px 400px' });
        framed.forEach(function (v) { pre.observe(v); });
      } else {
        framed.forEach(cue);
      }
      framed.forEach(function (v) {
        v.addEventListener('play', function () {
          if (v.dataset.cued === '1') { v.dataset.cued = '2'; v.currentTime = 0; }
        });
      });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (en) { if (!en.isIntersecting && !en.target.paused) en.target.pause(); });
      }, { threshold: 0 });
      root.querySelectorAll('.custom-pugc__video').forEach(function (v) { io.observe(v); });
    }

    function paintBar() {
      if (!bar || !thumb) return;
      var sw = track.scrollWidth, cw = track.clientWidth;
      bar.hidden = sw <= cw + 2;
      if (bar.hidden) return;
      var bw = bar.clientWidth;
      var tw = Math.max(40, bw * cw / sw);
      thumb.style.width = tw + 'px';
      thumb.style.transform = 'translateX(' + ((bw - tw) * track.scrollLeft / (sw - cw)) + 'px)';
    }
    var raf = 0;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = window.requestAnimationFrame(function () { raf = 0; paintBar(); });
    }, { passive: true });
    window.addEventListener('resize', paintBar, { passive: true });
    paintBar();

    // Drag to scroll for a mouse. Moving more than 5px counts as a drag, so the
    // click that ends it does not also start a video.
    var down = null;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = { x: e.clientX, left: track.scrollLeft, moved: false };
    });
    window.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - down.x;
      if (Math.abs(dx) > 5) { down.moved = true; track.classList.add('is-dragging'); }
      if (down.moved) track.scrollLeft = down.left - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!down) return;
      dragged = down.moved;
      down = null;
      track.classList.remove('is-dragging');
    });
  });
})();

/* ---------------------------------------------------------------------------
   SCROLL HAND-OVER AT 990px (Ian's white-page bug, 2026-10-02).
   From 990px Horizon (assets/base.css:27-56) makes html/body `overflow: hidden`
   at 100dvh and scrolls `.page-wrapper` instead. Below 990 the document
   scrolls. Crossing the breakpoint does not carry the position over, so after
   scrolling on a narrow window and widening it (or rotating an iPad from
   portrait 820 to landscape 1180) the document keeps its offset with
   scrolling now disabled: the whole page-wrapper sits that far above the
   viewport and the screen is white, with no way to scroll back. Measured: doc
   scrollTop 2074 held, page-wrapper top at -2074.
   Fix: on each crossing, move the offset to whichever element now scrolls.
   --------------------------------------------------------------------------- */
(function () {
  if (!window.matchMedia) return;
  var mq = window.matchMedia('(min-width: 990px)');
  function jump(el, y) {
    try { el.scrollTo({ top: y, behavior: 'instant' }); } catch (e) { el.scrollTop = y; }
  }
  function handOver() {
    var pw = document.querySelector('.page-wrapper');
    var doc = document.scrollingElement || document.documentElement;
    if (!pw) return;
    if (mq.matches) {
      var y = doc.scrollTop;
      if (y > 0) { jump(doc, 0); jump(pw, pw.scrollTop + y); }
    } else {
      // The wrapper stops scrolling (and reads 0) before this runs, so use the
      // last position it reported.
      var p = lastPw;
      lastPw = 0;
      if (p > 0) { jump(pw, 0); jump(doc, doc.scrollTop + p); }
    }
  }
  var lastPw = 0;
  document.addEventListener('scroll', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('page-wrapper')) lastPw = e.target.scrollTop;
  }, { capture: true, passive: true });
  if (mq.addEventListener) mq.addEventListener('change', handOver);
  else if (mq.addListener) mq.addListener(handOver);
  // A page loaded mid-scroll (back/forward cache) can start in the bad state.
  window.addEventListener('pageshow', handOver);
})();

/* ---------------------------------------------------------------------------
   HEADER BAG COUNT FOLLOWS EVERY CART CHANGE (Ian, 2026-10-02).
   Horizon's <cart-icon> only redraws on its own `cartLinesUpdate` event, which
   its product form fires after ITS add. UpCart (the cart drawer app) takes over
   adds and drawer quantity changes and posts them itself
   (`/cart/add.js?upcart=1`, `/cart/change.js`…), so that event never fires and
   the bag kept the old count until the next page load. Measured: cart.js
   item_count 1, bag still 0 after 9s.
   Fix: watch fetch for cart writes (any caller), read the resulting count, and
   call the bag's own renderCartBubble(). Change/update/clear responses carry
   the cart; add.js returns only the added lines, so /cart.js is read then.
   --------------------------------------------------------------------------- */
(function () {
  if (!window.fetch) return;
  var WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var orig = window.fetch;
  function paint(n) {
    var icon = document.querySelector('cart-icon');
    if (icon && typeof icon.renderCartBubble === 'function' && typeof n === 'number' && icon.currentCartCount !== n) {
      icon.renderCartBubble(n);
    }
  }
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = orig.apply(this, arguments);
    if (!WRITE.test(url)) return p;
    p.then(function (res) {
      if (!res || !res.ok) return;
      res.clone().json().then(function (data) {
        if (data && typeof data.item_count === 'number') return paint(data.item_count);
        return orig.call(window, '/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { paint(cart.item_count); });
      }).catch(function () {});
    }).catch(function () {});
    return p;
  };
})();

/* ==========================================================================
   FAQ "Show all N questions" / "Show fewer" (snippets/custom.faq-toggle.liquid)
   Product pages: lists longer than the section's limit (default 20) render the
   rest with `custom-faq-extra`; this toggles `.is-all` on the list. Delegated,
   so it also covers PDP sections re-rendered by variant changes.
   ========================================================================== */
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-faq-toggle]');
    if (!btn) return;
    var list = document.getElementById(btn.getAttribute('aria-controls'));
    if (!list) return;
    var open = list.classList.toggle('is-all');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    btn.textContent = btn.getAttribute(open ? 'data-label-less' : 'data-label-more');
    // Collapsing from the bottom of a long list: bring the toggle back into view.
    if (!open) btn.scrollIntoView({ block: 'nearest' });
  });
})();


/* ==========================================================================
   Console easter egg: we're hiring (Ian, 2026-10-03)
   Open devtools on any page: the Warrior wordmark in block characters, then a
   recruitment call to action, all as ONE plain monospace block (Ian's reference:
   no colours, no pills, no backgrounds), plus a tiny `warrior` command set.
     warrior.help()      list the commands
     warrior.apply()     open an email to the careers inbox
     warrior.stack()     what this storefront is built on
     warrior.motivate()  a line from the wall-writing phrase bank
     warrior.outwork()   the Konami wall (below)
   The art is traced from brand/01-logos/primary/logo-wordmark-on-light.svg
   (80 columns, half-block characters, two pixel rows per text row), so the
   helmet A and the tall W and R are the real letterforms.
   Font matters: block glyphs only join up in a system monospace that has them
   (Menlo / Monaco / Consolas). The site's JetBrains Mono subset lacks them, so the
   console fell back per glyph and drew gapped bricks; line-height 1 closes the
   row gaps. No colour is set, so it reads in the console's own light/dark theme.
   Voice per brand/08-voice: recruitment is the one place the Warrior Way runs
   undiluted ("no bullshit" belongs here and nowhere else).
   Runs once per page, never inside the theme editor.
   ========================================================================== */
(function () {
  if (window.__warriorHiring || (window.Shopify && Shopify.designMode)) return;
  window.__warriorHiring = true;
  var EMAIL = 'readytowork@teamwarrior.com';
  // inline-block gives each part its own line boxes, so line-height 1 really closes
  // the art's row gaps (on a plain inline span the console's own line height wins).
  var FONT = 'font-family: Menlo, Monaco, Consolas, "DejaVu Sans Mono", "Courier New", monospace; font-size: 12px;';
  var ART = 'display: inline-block; line-height: 1; margin-bottom: 14px;' + FONT; // blocks join
  var MONO = 'display: inline-block; line-height: 1.6;' + FONT;                   // readable text
  var LOGO = "███    ▄    ██                                                        ███▀▀▀██▄\n███    █    ██    ▄▄██▄      ▄▄▄▄▄▄      ▄▄▄▄▄▄      ▄▄      ▄▄▄▄▄    ███    ███\n███   ▄█    ██   ███████▄    ██   ▀██    ██   ▀██    ██    ▄█▀  ▀██   ███    ███\n███   ███   ██  █████████    ██    ██    ██    ██    ██    ██    ██   ███    ███\n███   ███   ██  █████████    ██    ██    ██    ██    ██    ██    ██   ███    ███\n███   ███   ██  ▀████████    ██    ██    ██    ██    ██    ██    ██   ███    ███\n▀██  ▄███▄  █    █▀▀██▀▀█    ██   ██▀    ██   ██▀    ██    ██    ██   ███    ███\n ██  █████  █    ▀▄ █  ▄█    ██▀▀▀█▄     ██▀▀▀█▄     ██    ██    ██   ███    ███\n ██▄ ██▀██  █    ██ █▄ ██    ██    ██    ██    ██    ██    ██    ██   ███▄▄▄▄█▀\n ███▄█▀ ███ █   ▄██ ▀▀ ██    ██    ██    ██    ██    ██    ██    ██   ███▀▀▀▀██▄\n █████  ███▄█   ███    ██    ██    ██    ██    ██    ██    ██    ██   ███    ███\n █████  ▀████    ██    ██    ██    ██    ██    ██    ██    ██    ██   ███    ███\n ████▀   ████    ██    ██    ██    ██    ██    ██    ██    ██    ██   ███    ███\n ████    ████    ▀▀    ▀▀    ▀▀    ▀▀    ▀▀    ▀▀    ▀▀     ▀▀█▄█▀    ███    ███\n ▀███    ███                                                          ███    ███\n  ███     ██                                                          ███    ███\n  ██      ██                                                          ███    ███\n  ██      ██                                                          ███    ███\n  ██      ▀█                                                          ███    ███";
  var PHRASES = ["Turn up tune out", "Finish what you started", "Raise the floor", "Earned not given", "Outwork outlast", "You said tomorrow", "Nobody’s watching", "Same time tomorrow", "Small reps long years", "Ten years of Tuesdays", "Boring works", "Keep the streak", "One more set", "Less talk more reps", "The bar doesn’t care", "Nobody claps at 6am", "Simple not easy", "Good enough isn’t", "No shortcuts", "Measure it beat it", "Slow is still forward", "Show up anyway"];
  function say(text) { try { console.log('%c' + text, MONO); } catch (e) {} }

  try {
    console.log('%c' + LOGO + '%c\n' + [
      'WE’RE HIRING.',
      'You opened the console. That’s the kind of curiosity we hire for.',
      'No bullshit. No excuses. Just people who outwork, outlast and ship.',
      'SEND YOUR CV → ' + EMAIL,
      'or type warrior.apply() · warrior.help() for more'
    ].join('\n'), ART, MONO);
  } catch (e) {}

  window.warrior = {
    help: function () {
      say([
        'warrior.apply()     email your CV to ' + EMAIL,
        'warrior.stack()     what this site is built on',
        'warrior.motivate()  something for the next set',
        'warrior.outwork()   write it on the wall (or try ↑↑↓↓←→←→BA)'
      ].join('\n'));
      return 'Outwork. Outlast.';
    },
    apply: function () {
      var subject = encodeURIComponent('Found you in the console');
      var body = encodeURIComponent('Hi Warrior,\n\nI found you in the console on ' + location.hostname + '.\n\n[Who you are, what you do, and why Warrior. Attach your CV.]\n');
      window.location.href = 'mailto:' + EMAIL + '?subject=' + subject + '&body=' + body;
      return 'Opening your email: ' + EMAIL;
    },
    stack: function () {
      say('Shopify Horizon · Liquid · vanilla Web Components · hand-written CSS on DESIGN.md tokens.\nNo React. No Tailwind in the theme. Made in Manchester.');
      return 'Like the look of it? warrior.apply()';
    },
    motivate: function () {
      var p = PHRASES[Math.floor(Math.random() * PHRASES.length)];
      say(p.toUpperCase() + '.');
      return p;
    },
    outwork: function () { if (window.WarriorOutwork) window.WarriorOutwork(); return 'Outwork. Outlast.'; },
    toString: function () { return 'Type warrior.help()'; }
  };
})();

/* ==========================================================================
   Konami code easter egg (Ian, 2026-10-03)
   ↑ ↑ ↓ ↓ ← → ← → B A on any page (or warrior.outwork() in the console) draws
   "OUTWORK. OUTLAST." across a full-screen dark wall with the ROAM wall-writing
   engine from the collection mastheads (assets/custom.wall-writing.js): the
   same hand-written strokes and paint drips, in Neon.
   The engine and glyphs load only when triggered (URLs from custom.globals).
   Esc, a click, or a few seconds after the drips settle closes it.
   Ignored while typing in a field, and never in the theme editor.
   ========================================================================== */
(function () {
  if (window.Shopify && Shopify.designMode) return;
  var SEQ = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
  var PHRASE = 'OUTWORK. OUTLAST.';
  var pos = 0, open = null, engine = null;

  function loadEngine() {
    if (window.WarriorWallInit) return Promise.resolve();
    if (engine) return engine;
    var src = window.WarriorWall && window.WarriorWall.js;
    if (!src) return Promise.reject();
    engine = new Promise(function (res, rej) {
      var sc = document.createElement('script');
      sc.src = src; sc.onload = res; sc.onerror = rej;
      document.head.appendChild(sc);
    });
    return engine;
  }

  function close() {
    if (!open) return;
    var el = open; open = null;
    el.classList.remove('is-in');
    document.removeEventListener('keydown', onEsc, true);
    setTimeout(function () { el.remove(); }, 400);
  }
  function onEsc(e) { if (e.key === 'Escape') close(); }

  /* SECRET CODE (Ian, 2026-10-07). Below the wall, once the writing is done:
     "You found the secret code", the code in a dashed Neon ticket with a Copy
     button, the terms, and a line of motivation (Theme settings > Warrior
     easter egg). Before the code's start date the panel says when it unlocks
     instead, so nobody copies a code checkout would reject. With a code the
     overlay no longer closes itself: Esc, the close button or a click outside
     the panel. Text goes in through textContent, never innerHTML. */
  var K = window.WarriorKonami || {};
  function codeState() {
    var code = (K.code || '').trim();
    if (!code) return null;
    var t = K.starts ? Date.parse(K.starts) : NaN;
    // Expired: no panel at all (wall only, closes itself), so nobody copies a dead code.
    var end = K.ends ? Date.parse(K.ends) : NaN;
    if (!isNaN(end) && Date.now() > end) return null;
    return { code: code, live: isNaN(t) || Date.now() >= t, starts: isNaN(t) ? null : new Date(t) };
  }
  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }
  function panel(st) {
    var pnl = el('div', 'custom-konami__panel');
    pnl.appendChild(el('p', 'custom-konami__eyebrow', 'Up, up, down, down. You know the rest.'));
    pnl.appendChild(el('h2', 'custom-konami__title', st.live ? 'You found the secret code' : 'You found the secret'));
    if (st.live) {
      var ticket = el('div', 'custom-konami__ticket');
      ticket.appendChild(el('span', 'custom-konami__code', st.code));
      var btn = el('button', 'custom-konami__copy', 'Copy code');
      btn.type = 'button';
      btn.setAttribute('aria-live', 'polite');
      btn.addEventListener('click', function () {
        var done = function () { btn.textContent = 'Copied ✓'; btn.classList.add('is-copied'); };
        var fallback = function () {
          var ta = el('textarea'); ta.value = st.code; ta.setAttribute('readonly', '');
          ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta);
          ta.select(); try { document.execCommand('copy'); } catch (e) {} ta.remove(); done();
        };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(st.code).then(done, fallback);
        else fallback();
      });
      ticket.appendChild(btn);
      pnl.appendChild(ticket);
      if (K.terms) pnl.appendChild(el('p', 'custom-konami__terms', K.terms));
    } else {
      var d = st.starts.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
      pnl.appendChild(el('p', 'custom-konami__terms', 'The code unlocks on ' + d + '. Come back and try again.'));
    }
    if (K.message) pnl.appendChild(el('p', 'custom-konami__message', K.message));
    return pnl;
  }

  function outwork() {
    if (open || !(window.WarriorWall && window.WarriorWall.glyphs)) return;
    var st = codeState();
    var o = document.createElement('div');
    o.className = 'custom-konami' + (st ? ' custom-konami--code' : '');
    o.setAttribute('role', 'dialog');
    o.setAttribute('aria-modal', 'true');
    o.setAttribute('aria-label', st ? 'You found the secret code' : 'Outwork. Outlast.');
    o.innerHTML =
      '<div class="custom-wall custom-wall--fill-neon custom-konami__wall" data-custom-wall data-min-width="0"' +
      ' data-angle-min="4" data-angle-max="8" data-drips="true" data-cycle="false" data-style="fill" data-thin="8" aria-hidden="true" hidden>' +
      '<svg class="custom-wall__svg" focusable="false"></svg>' +
      '<script type="application/json" data-custom-wall-phrases>' + JSON.stringify([PHRASE]) + '<\/script></div>' +
      '<p class="custom-konami__hint">We’re hiring: readytowork@teamwarrior.com · Esc to close</p>';
    o.querySelector('[data-custom-wall]').setAttribute('data-glyphs', window.WarriorWall.glyphs);
    var wall = o.querySelector('[data-custom-wall]');
    // The panel is in the layout from the start (hidden), so the wall is drawn
    // in its final position and nothing jumps when the panel animates in.
    var pn = st ? panel(st) : null;
    if (pn) { pn.setAttribute('aria-hidden', 'true'); wall.insertAdjacentElement('afterend', pn); }
    var reveal = function () { if (!pn || pn.classList.contains('is-in')) return; pn.removeAttribute('aria-hidden'); pn.classList.add('is-in'); };
    if (st) {
      var x = el('button', 'custom-konami__close');
      x.type = 'button'; x.setAttribute('aria-label', 'Close');
      x.innerHTML = '<svg width="20" height="20" viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M8 8 L24 24"/><path d="M24 8 L8 24"/></svg>';
      x.addEventListener('click', close);
      o.appendChild(x);
      o.addEventListener('click', function (e) { if (!e.target.closest('.custom-konami__panel, .custom-konami__close')) close(); });
    } else {
      o.addEventListener('click', close);
    }
    document.body.appendChild(o);
    open = o;
    document.addEventListener('keydown', onEsc, true);
    requestAnimationFrame(function () { o.classList.add('is-in'); });
    wall.addEventListener('wall:done', function () {
      if (st) reveal();
      else setTimeout(function () { if (open === o) close(); }, 3500);
    });
    if (st) {
      // Reduced motion draws instantly; otherwise reveal anyway if the pen is slow.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) reveal();
      setTimeout(reveal, 6000);
    }
    loadEngine().then(function () { window.WarriorWallInit(wall); }).catch(function () { if (st) reveal(); else close(); });
  }
  window.WarriorOutwork = outwork;

  document.addEventListener('keydown', function (e) {
    var t = e.target;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) { pos = 0; return; }
    var k = e.key && e.key.length === 1 ? e.key.toLowerCase() : e.key;
    if (k === SEQ[pos]) {
      pos++;
      if (pos === SEQ.length) { pos = 0; outwork(); }
    } else {
      pos = k === SEQ[0] ? 1 : 0;
    }
  });
})();

/* Collection intro: read more (sections/custom-collection-head.liquid).
   Toggles the mobile two-line clamp. Delegated, so it survives Horizon's
   section re-renders. */
(function () {
  'use strict';
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-collhead-more]');
    if (!btn) return;
    var box = btn.closest('[data-collhead-intro]');
    if (!box) return;
    var open = box.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    btn.textContent = open ? 'Read less' : 'Read more';
  });
})();

/* Card hover shader loader (assets/custom.card-shader.js, motion plan #11).
   Fetches the shader on the first hover of a card that has a second image,
   and only on fine-pointer desktops 990px+ with motion allowed. */
(function () {
  'use strict';
  var ok = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches && window.WarriorShaderUrl && window.WarriorShaderEffect;
  if (!ok) return;
  var loading = false;
  function onOver(e) {
    // Bestsellers cards stretch the title link over the whole card, so the hover
    // lands on the link: find the card first, then its image.
    var img = e.target.closest && e.target.closest('.custom-card-gallery, .custom-bs__card');
    img = img && img.querySelector('img[data-custom-hover-src]:not([data-custom-hover-src=""])');
    if (!img) return;
    if (window.WarriorShader) { window.WarriorShader.attach(img); return; }
    if (loading) return;
    loading = true;
    var s = document.createElement('script');
    s.src = window.WarriorShaderUrl;
    s.onload = function () {
      // The first hover happened before the script existed: replay it on this card.
      var card = img.closest('product-card, .product-card, .card-gallery, .custom-bs__card');
      if (card && card.matches(':hover')) card.dispatchEvent(new PointerEvent('pointerenter', { pointerType: 'mouse' }));
    };
    document.head.appendChild(s);
  }
  document.addEventListener('pointerover', onOver, { passive: true });
})();



/* PDP flavour glitch (Theme settings > Warrior motion > "RGB glitch when the
   flavour changes"). Picking another flavour glitches the main product image
   from the old flavour into the new one, using the card shader's one-shot
   play(). The old image is captured on the click, before Horizon re-renders the
   gallery; the new one is caught as soon as it is in the DOM and hidden until
   the glitch runs, so the swap never flashes. Skipped under reduced motion. */
(function () {
  'use strict';
  if (!window.WarriorFlavourGlitch || !window.WarriorShaderUrl) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  var main = document.querySelector('main[data-template^="product"]');
  if (!main) return;
  function visibleImg() {
    var g = main.querySelector('media-gallery');
    if (!g) return null;
    var box = (g.querySelector('slideshow-container') || g).getBoundingClientRect();
    var imgs = g.querySelectorAll('slideshow-slide .product-media img');
    for (var i = 0; i < imgs.length; i++) {
      var r = imgs[i].getBoundingClientRect();
      if (r.width > 100 && Math.abs(r.left - box.left) < 24) return imgs[i];
    }
    return imgs[0] || null;
  }
  var loading = null;
  function engine() {
    if (window.WarriorShader) return Promise.resolve();
    if (loading) return loading;
    loading = new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = window.WarriorShaderUrl; s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
    return loading;
  }
  // Fetch the engine when a shopper first reaches for the flavour picker (press,
  // or keyboard focus), not on idle: most PDP visits never change flavour, and
  // the idle fetch showed up as a long task on mobile (2026-10-17). The press
  // comes ~100 ms before the change event, which covers the 4 KB fetch.
  var warm = function (e) {
    if (!e.target.closest || !e.target.closest('variant-picker')) return;
    main.removeEventListener('pointerdown', warm, true);
    main.removeEventListener('focusin', warm, true);
    engine().catch(function () {});
  };
  main.addEventListener('pointerdown', warm, true);
  main.addEventListener('focusin', warm, true);
  var pending = null;
  main.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.type !== 'radio' || !input.closest('variant-picker')) return;
    var fs = input.closest('fieldset');
    var name = input.getAttribute('data-option-name') || ((fs && fs.querySelector('legend')) || {}).textContent || '';
    if (!/flavo/i.test(name)) return; // flavour changes only, not size or plan
    var old = visibleImg();
    if (!old) return;
    var fromUrl = old.currentSrc || old.src;
    // The overlay follows the live visible image (its own box, where its
    // object-fit applies); Horizon's re-render can scroll or resize it.
    var box = old.getBoundingClientRect();
    if (box.bottom < 0 || box.top > window.innerHeight) return; // gallery off screen: just swap
    var liveRect = function () { var i = visibleImg(); var r = i && i.getBoundingClientRect(); return r && r.width ? r : null; };
    var my = {};
    pending = my;
    engine().then(function () {
      if (pending !== my) return false;
      return window.WarriorShader.hold(liveRect, fromUrl, getComputedStyle(old).objectFit);
    }).then(function (ok) {
      if (!ok || pending !== my) return;
      // Wait for the new image to land and the gallery to stop re-rendering
      // (Horizon can morph it more than once), then glitch into it.
      var started = performance.now(), lastChange = performance.now(), seen = fromUrl;
      var g = main.querySelector('media-gallery');
      var mo = g ? new MutationObserver(function () { lastChange = performance.now(); }) : null;
      if (mo) mo.observe(g, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
      (function watch() {
        if (pending !== my) { if (mo) mo.disconnect(); return; }
        var now = visibleImg();
        var src = now && (now.currentSrc || now.src);
        if (src && src !== seen) { seen = src; lastChange = performance.now(); }
        var settled = performance.now() - lastChange > 160;
        if (src && src !== fromUrl && settled && now.complete && now.naturalWidth) {
          if (mo) mo.disconnect();
          window.WarriorShader.release(src);
          return;
        }
        if (performance.now() - started > 3000) { if (mo) mo.disconnect(); window.WarriorShader.release(null); return; }
        requestAnimationFrame(watch);
      })();
    }).catch(function () {});
  }, true);
})();

/* ── PDP gallery ARIA (WAI-ARIA APG "tabbed carousel") ─────────────────────
   Horizon's gallery ships with no carousel semantics. Its thumbnail/dot buttons
   carry aria-selected, which is not a valid attribute on a button, and the
   vendor CSS styles the active thumbnail by [aria-selected='true']. Giving the
   buttons role="tab" makes that attribute valid without touching vendor JS or CSS:
     slideshow-component  role=region, aria-roledescription=carousel, labelled
     slideshow-controls   role=tablist ("Choose product image")
     thumbnail / dot      role=tab, aria-controls=its slide, roving tabindex,
                          ←/→/Home/End move between images
     slideshow-slide      role=tabpanel, aria-roledescription=slide, "N of M"
     prev / next          aria-controls the slides wrapper
   The zoom dialog's thumbnail strip gets the same tab treatment.
   Horizon morphs the gallery on a variant change, which strips attributes it
   didn't render, so a debounced observer re-applies. Every write is
   compare-then-set, so the observer settles after one extra no-op pass. */
(function () {
  if (!document.querySelector('main[data-template^="product"]')) return;
  var uid = 0;
  function set(el, name, val) { if (el && el.getAttribute(name) !== val) el.setAttribute(name, val); }
  function idFor(el, prefix) { if (!el.id) el.id = prefix + '-' + (++uid); return el.id; }
  function productTitle() {
    // The h1 carries hidden variant JSON in its textContent; og:title is clean.
    var m = document.querySelector('meta[property="og:title"]');
    return m ? m.getAttribute('content').trim() : '';
  }

  function tablist(list, tabs, panels, label) {
    if (tabs.length < 2) return;
    set(list, 'role', 'tablist');
    set(list, 'aria-label', label);
    var any = tabs.some(function (t) { return t.getAttribute('aria-selected') === 'true'; });
    tabs.forEach(function (tab, i) {
      var on = any ? tab.getAttribute('aria-selected') === 'true' : i === 0;
      set(tab, 'role', 'tab');
      set(tab, 'aria-selected', on ? 'true' : 'false');
      set(tab, 'tabindex', on ? '0' : '-1');
      if (panels[i]) set(tab, 'aria-controls', idFor(panels[i], 'pdp-media'));
      // Name the tab after the image it shows, not just its position.
      var img = tab.querySelector('img');
      var alt = img && (img.dataset.alt || img.getAttribute('alt'));
      var pos = 'image ' + (i + 1) + ' of ' + tabs.length;
      set(tab, 'aria-label', alt ? alt + ' (' + pos + ')' : pos.charAt(0).toUpperCase() + pos.slice(1));
      // Keep the img alt (SEO audits count alt="" as missing). The tab's
      // aria-label overrides its content name, so it is not read twice. Restore
      // any alt an earlier build of this script cleared.
      if (img && !img.getAttribute('alt') && img.dataset.alt) set(img, 'alt', img.dataset.alt);
    });
    if (!list.dataset.ariaKeys) {
      list.dataset.ariaKeys = '1';
      list.addEventListener('keydown', function (e) {
        var keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1, Home: 'first', End: 'last' };
        if (!(e.key in keys)) return;
        var all = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
        var at = all.indexOf(document.activeElement);
        if (at < 0) return;
        e.preventDefault();
        var k = keys[e.key];
        var to = k === 'first' ? 0 : k === 'last' ? all.length - 1 : (at + k + all.length) % all.length;
        all[to].focus();
        all[to].click();
      });
    }
  }

  function apply() {
    document.querySelectorAll('.product-recommendations__skeleton-item[aria-label]').forEach(function (el) {
      el.removeAttribute('aria-label');
      set(el, 'aria-hidden', 'true');
    });
    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      var title = productTitle();
      var show = gallery.querySelector('slideshow-component');
      if (show) {
        var wrap = show.querySelector('slideshow-slides');
        var slides = wrap ? Array.prototype.slice.call(wrap.querySelectorAll(':scope > slideshow-slide')) : [];
        if (slides.length > 1) {
          set(show, 'role', 'region');
          set(show, 'aria-roledescription', 'carousel');
          set(show, 'aria-label', title ? 'Product images: ' + title : 'Product images');
          set(wrap, 'aria-live', 'polite');
          // Vendor renders the scroller tabindex=-1; a scrollable region must be
          // keyboard-reachable (axe scrollable-region-focusable).
          set(wrap, 'tabindex', '0');
          set(wrap, 'role', 'group');
          set(wrap, 'aria-label', 'Product image slides');
          var wrapId = idFor(wrap, 'pdp-slides');
          show.querySelectorAll('slideshow-arrows button, .slideshow-control--previous, .slideshow-control--next').forEach(function (b) {
            set(b, 'aria-controls', wrapId);
          });
          slides.forEach(function (s, i) {
            set(s, 'role', 'tabpanel');
            set(s, 'aria-roledescription', 'slide');
            set(s, 'aria-label', (i + 1) + ' of ' + slides.length);
          });
          show.querySelectorAll('slideshow-controls').forEach(function (ctl) {
            var tabs = Array.prototype.slice.call(ctl.querySelectorAll('button.slideshow-control:not(.slideshow-control--previous):not(.slideshow-control--next)'))
              .filter(function (b) { return b.hasAttribute('aria-selected') || b.closest('.slideshow-controls__thumbnails, .slideshow-controls__dots'); });
            var list = tabs.length ? tabs[0].parentElement : null;
            if (list) tablist(list, tabs, slides, 'Choose product image');
          });
        }
      }
      // Zoom dialog thumbnail strip → the zoomed images it scrolls to.
      var dlgList = gallery.querySelector('.dialog-thumbnails-list');
      if (dlgList) {
        var dTabs = Array.prototype.slice.call(dlgList.querySelectorAll('.dialog-thumbnails-list__thumbnail'));
        var dPanels = Array.prototype.slice.call(gallery.querySelectorAll('.dialog-zoomed-gallery > *'));
        dPanels.forEach(function (p, i) {
          set(p, 'role', 'tabpanel');
          set(p, 'aria-label', 'Zoomed image ' + (i + 1) + ' of ' + dPanels.length);
        });
        tablist(dlgList, dTabs, dPanels, 'Choose zoomed image');
      }
    });
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; apply(); });
  }
  function start() {
    apply();
    var main = document.querySelector('main') || document.body;
    // childList catches a re-rendered gallery; the attribute filter catches the
    // vendor moving aria-selected and morph stripping role. We only ever write
    // attributes, never nodes, inside this subtree.
    new MutationObserver(schedule).observe(main, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['aria-selected', 'role']
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* ── WebMCP form annotations for AI agents (Chrome origin trial, 2026) ──────
   https://developer.chrome.com/docs/ai/webmcp — lets an in-browser agent call
   named tools instead of scraping the page. Plan + rationale: theme/docs/webmcp.md.

   Shopify already registers its storefront tools on every page (search_catalog,
   get_product, show_variant, add_to_cart, get_cart, update_cart_lines,
   cancel_cart, proceed_to_checkout, browse_store, manage_orders,
   search_shop_policies_and_faqs, get_ucp_profile). Same-named theme tools REPLACE
   Shopify's (verified 2026-10-05), so the theme adds only what Shopify lacks:
     filter_products          registered here with registerTool() (collection/search)
     subscribe_to_newsletter  declarative attributes in snippets/custom.footer.liquid
   Attributes added from JS fail Lighthouse "WebMCP schemas are valid": the form is
   briefly half-annotated, and Chrome reports it (2026-10-17). So no JS-annotated
   forms. Shopify's sign-in form sits in a shadow root and is left to Shopify.
   Quick-add forms on cards are left alone: one tool per card would register 20+
   identically named tools, and Shopify's add_to_cart covers the job. */
(function () {
  /* filter_products is registered in script, not as form attributes. A form
     annotated after load always passes through a half-annotated state (name
     without description, or the reverse), which Chrome reports and Lighthouse
     scores as an invalid schema; its checkboxes are vendor markup (checkbox.liquid)
     that cannot carry descriptions. registerTool() hands Chrome the whole schema
     at once. The schema lists the filters on offer at load; execute() works on the
     live form, so it still applies after Horizon re-renders it. */
  function registerFilters() {
    var mc = document.modelContext || navigator.modelContext;
    var form = document.querySelector('form.facets__form[id$="-desktop"]');
    if (!mc || typeof mc.registerTool !== 'function' || !form) return;
    var id = form.id;
    var live = function () { return document.getElementById(id); };
    var slug = function (t) { return t.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'filter'; };
    var props = {}, groups = {};
    form.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach(function (el) {
      var fs = el.closest('details, fieldset, .facets__filters-wrapper, li.facets__item');
      var title = fs && fs.querySelector('summary, legend, .facets__summary, .facets__label');
      var label = (title ? title.textContent : el.name.split('.').pop()).replace(/\s+/g, ' ').replace(/\(\d+\)/g, '').trim();
      var key = slug(label);
      if (groups[key] && groups[key].name !== el.name) key = slug(el.name.split('.').pop());
      if (!groups[key]) { groups[key] = { name: el.name, values: [] }; props[key] = { type: 'array', description: label + '. Pick one or more; an empty list clears it.', items: { type: 'string', enum: [] } }; }
      if (props[key].items.enum.indexOf(el.value) < 0) props[key].items.enum.push(el.value);
    });
    if (form.querySelector('input[name="filter.v.price.gte"]')) {
      props.min_price = { type: 'number', description: 'Minimum price in pounds.' };
      props.max_price = { type: 'number', description: 'Maximum price in pounds.' };
    }
    var sorts = document.querySelectorAll('input[name="sort_by"][form="' + id + '"]');
    if (sorts.length) {
      props.sort_by = { type: 'string', description: 'Sort order: ' + Array.prototype.map.call(sorts, function (r) {
        return r.value + ' = ' + (r.getAttribute('data-option-name') || r.value);
      }).join(', ') + '.', enum: Array.prototype.map.call(sorts, function (r) { return r.value; }) };
    }
    if (!Object.keys(props).length) return;
    try {
      mc.registerTool({
        name: 'filter_products',
        description: 'Narrows and sorts the products listed on this page (range, form, protein source, price in GBP), then reloads the list. Read-only: nothing is added to the basket.',
        inputSchema: { type: 'object', properties: props },
        annotations: { readOnlyHint: true },
        execute: function (args) {
          var f = live();
          if (!f) return { content: [{ type: 'text', text: 'The filters are not available on this page.' }] };
          args = args || {};
          Object.keys(groups).forEach(function (key) {
            if (!Array.isArray(args[key])) return;
            f.querySelectorAll('input[type="checkbox"]').forEach(function (el) {
              if (el.name === groups[key].name) el.checked = args[key].indexOf(el.value) >= 0;
            });
          });
          var setNum = function (sel, v) { var el = f.querySelector(sel); if (el && v != null) el.value = String(v); };
          setNum('input[name="filter.v.price.gte"]', args.min_price);
          setNum('input[name="filter.v.price.lte"]', args.max_price);
          if (args.sort_by) {
            var r = document.querySelector('input[name="sort_by"][form="' + id + '"][value="' + String(args.sort_by).replace(/"/g, '') + '"]');
            if (r) r.checked = true;
          }
          f.requestSubmit ? f.requestSubmit() : f.submit();
          return { content: [{ type: 'text', text: 'Filters applied; the product list is reloading.' }] };
        }
      });
    } catch (e) { /* API shape changed in a later Chrome: the page works as before */ }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', registerFilters);
  else registerFilters();
})();

/* ── Alt text for Kaching Bundles images (SEO audit 2026-10-05) ────────────
   Kaching renders its bundle-bar flavour thumbnails and free-gift icons with
   alt="" (15 + 2 on a Creatine Plus PDP). They sit beside visible text, so the
   alt is built from that text: "<product>, <flavour>" for a variant thumbnail
   (kept in step with its <select>), and the gift line ("FREE Shaker") for a
   gift. Kaching re-renders on every bar change, hence the observer; writes are
   compare-then-set on alt only, so it settles. */
(function () {
  if (!document.querySelector('main[data-template^="product"]')) return;
  var og = document.querySelector('meta[property="og:title"]');
  var title = og ? og.getAttribute('content').trim() : '';
  function set(img, alt) { if (alt && img.getAttribute('alt') !== alt) img.setAttribute('alt', alt); }
  function apply() {
    document.querySelectorAll('img.kaching-bundles__bar-variant-image').forEach(function (img) {
      var row = img.closest('.kaching-bundles__bar-variant');
      var sel = row && row.querySelector('select');
      var opt = sel && sel.options[sel.selectedIndex];
      set(img, title + (opt ? ', ' + opt.text.trim() : ''));
    });
    document.querySelectorAll('img.kaching-bundles__free-gift__image').forEach(function (img) {
      var main = img.closest('.kaching-bundles__free-gift__main');
      var t = main && main.querySelector('.kaching-bundles__free-gift__text');
      set(img, t ? t.textContent.replace(/^\s*\+\s*/, '').trim() : 'Free gift');
    });
  }
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; apply(); });
  }
  function start() {
    apply();
    var root = document.querySelector('main') || document.body;
    new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
    root.addEventListener('change', function (e) { if (e.target.matches && e.target.matches('.kaching-bundles__bar-variant-select')) schedule(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* ── Journal: load more, reading progress, contents (blog redesign 2026-10-05) ──
   sections/custom-journal.liquid + sections/custom-article.liquid.
   - Load more: the button is a real ?page=N link (crawlable, works without JS).
     With JS it fetches that page and appends its grid items in place, then
     points itself at the next page or removes itself.
   - Progress: the 4px bar's scaleX follows how far through the article body you are.
   - Contents: built from the body's H2s (H1s too: some older posts use a second H1
     as a section heading). Shown only when there are 3+, as the brief says, and the
     link for the section in view is marked active. */
(function () {
  var more = document.querySelector('[data-journal-more]');
  if (more) {
    more.addEventListener('click', function (e) {
      var grid = document.querySelector('[data-journal-grid]');
      if (!grid || !window.fetch || !window.DOMParser) return;
      e.preventDefault();
      more.setAttribute('aria-busy', 'true');
      fetch(more.href, { credentials: 'same-origin' }).then(function (r) { return r.text(); }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var g = doc.querySelector('[data-journal-grid]');
        var first = null;
        if (g) Array.prototype.forEach.call(g.children, function (li) { var n = document.importNode(li, true); if (!first) first = n; grid.appendChild(n); });
        var next = doc.querySelector('[data-journal-more]');
        if (next) { more.href = next.href; more.removeAttribute('aria-busy'); }
        else more.parentNode.removeChild(more);
        var a = first && first.querySelector('a');
        if (a) a.focus({ preventScroll: true });
      }).catch(function () { window.location.href = more.href; });
    });
  }

  var body = document.querySelector('[data-article-content]');
  if (!body) return;

  var bar = document.querySelector('[data-article-progress]');
  if (bar) {
    var ticking = false;
    var update = function () {
      ticking = false;
      var r = body.getBoundingClientRect();
      var total = r.height - window.innerHeight * 0.6;
      var p = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 1;
      bar.style.transform = 'scaleX(' + p.toFixed(3) + ')';
    };
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  }

  /* Older posts open with their own copy of the title as a heading (demoted from H1 to
     H2 by the section). The page already shows the title, so that copy is hidden and
     kept out of the contents list. */
  var norm = function (t) { return t.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); };
  var titleEl = document.querySelector('.custom-art__h1');
  var firstHead = body.querySelector('h1, h2');
  if (titleEl && firstHead && norm(firstHead.textContent) === norm(titleEl.textContent)) firstHead.hidden = true;
  var heads = Array.prototype.filter.call(body.querySelectorAll('h1, h2'), function (h) { return !h.hidden && h.textContent.trim().length > 1; });
  if (heads.length < 3) return;
  var used = {};
  heads.forEach(function (h, i) {
    if (!h.id) {
      var id = h.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'section-' + (i + 1);
      while (used[id] || document.getElementById(id)) id += '-' + (i + 1);
      h.id = id;
    }
    used[h.id] = true;
  });
  var lists = document.querySelectorAll('[data-article-toc-list]');
  Array.prototype.forEach.call(lists, function (ol) {
    heads.forEach(function (h) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent.trim();
      li.appendChild(a);
      ol.appendChild(li);
    });
  });
  var toc = document.querySelector('[data-article-toc]');
  var tocSm = document.querySelector('[data-article-toc-sm]');
  if (toc) toc.hidden = false;
  if (tocSm) tocSm.hidden = false;
  if (toc && 'IntersectionObserver' in window) {
    var links = toc.querySelectorAll('a');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var i = heads.indexOf(en.target);
        Array.prototype.forEach.call(links, function (l, j) { l.classList.toggle('is-active', j === i); if (j === i) l.setAttribute('aria-current', 'location'); else l.removeAttribute('aria-current'); });
      });
    }, { rootMargin: '0px 0px -70% 0px' });
    heads.forEach(function (h) { io.observe(h); });
  }
})();

/* ── PDP gallery video player (2026-10-05) ────────────────────────────────────
   Horizon renders gallery videos (product media, or a video in "Gallery images 2")
   as <deferred-media>: a poster button that injects a muted, control-less <video>,
   plus a centred play/pause hint. Ian: the hint should not sit in the middle, and
   the player should be black and look finished. So once a gallery video is
   revealed, a black pill bar is added along its bottom (play/pause, seek, time,
   mute, full screen) and the hint is hidden in CSS.
   Mobile (< 750px): tapping the poster opens the video full screen (iOS uses its
   native player via webkitEnterFullscreen), and while a video slide is the current
   slide the carousel drops its 22px peek so no neighbouring image shows. */
(function () {
  if (!document.querySelector('main[data-template^="product"]')) return;
  var mobile = window.matchMedia('(max-width: 749px)');
  var ICON = {
    play: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4.5v15l13-7.5z"/></svg>',
    pause: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 4h4v16H6zM14 4h4v16h-4z"/></svg>',
    sound: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9h4l5-4v14l-5-4H4z"/><path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12"/></svg>',
    muted: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9h4l5-4v14l-5-4H4z"/><path d="M17 9l5 6M22 9l-5 6"/></svg>',
    full: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>'
  };
  function fmt(s) { s = Math.max(0, Math.floor(s || 0)); return Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2); }
  function btn(label, html) {
    var b = document.createElement('button');
    b.type = 'button'; b.className = 'custom-vplayer__btn'; b.setAttribute('aria-label', label); b.innerHTML = html;
    return b;
  }
  /* Leaving full screen can reset the carousel's scroll, landing on another slide;
     put the video's slide back where it was. */
  var fsSlide = null;
  document.addEventListener('fullscreenchange', function () {
    if (document.fullscreenElement || !fsSlide) return;
    var slides = fsSlide.parentElement, el = fsSlide;
    fsSlide = null;
    window.requestAnimationFrame(function () { if (slides) slides.scrollLeft = el.offsetLeft - slides.offsetLeft; });
  });
  function goFull(host, video) {
    fsSlide = host.closest('slideshow-slide');
    if (video.webkitEnterFullscreen && !host.requestFullscreen) { try { video.webkitEnterFullscreen(); } catch (e) {} return; }
    var req = host.requestFullscreen || host.webkitRequestFullscreen;
    if (req) { try { var p = req.call(host); if (p && p.catch) p.catch(function () { if (video.webkitEnterFullscreen) video.webkitEnterFullscreen(); }); } catch (e) {} }
    else if (video.webkitEnterFullscreen) { try { video.webkitEnterFullscreen(); } catch (e) {} }
  }

  function build(host, video) {
    if (host.querySelector(':scope > .custom-vplayer')) return;
    host.classList.add('custom-vplayer-host');
    video.setAttribute('playsinline', '');
    var bar = document.createElement('div');
    bar.className = 'custom-vplayer custom-on-dark';
    bar.setAttribute('role', 'group');
    bar.setAttribute('aria-label', 'Video controls');
    var play = btn('Pause video', '<span data-on>' + ICON.pause + '</span><span data-off>' + ICON.play + '</span>');
    var track = document.createElement('div');
    track.className = 'custom-vplayer__track';
    track.tabIndex = 0;
    track.setAttribute('role', 'slider');
    track.setAttribute('aria-label', 'Seek');
    track.setAttribute('aria-valuemin', '0');
    var fill = document.createElement('span'); fill.className = 'custom-vplayer__fill'; track.appendChild(fill);
    var time = document.createElement('span'); time.className = 'custom-vplayer__time'; time.setAttribute('aria-hidden', 'true');
    var mute = btn('Unmute video', '<span data-on>' + ICON.muted + '</span><span data-off>' + ICON.sound + '</span>');
    var full = btn('Full screen', ICON.full);
    bar.appendChild(play); bar.appendChild(track); bar.appendChild(time); bar.appendChild(mute); bar.appendChild(full);
    host.appendChild(bar);

    function sync() {
      var d = video.duration || 0, t = video.currentTime || 0;
      play.setAttribute('aria-pressed', video.paused ? 'true' : 'false');
      play.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      mute.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
      mute.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      fill.style.width = d ? (t / d * 100).toFixed(2) + '%' : '0';
      time.textContent = fmt(t) + ' / ' + fmt(d);
      track.setAttribute('aria-valuemax', String(Math.round(d)));
      track.setAttribute('aria-valuenow', String(Math.round(t)));
      track.setAttribute('aria-valuetext', fmt(t) + ' of ' + fmt(d));
    }
    function toggle() { if (video.paused) { var p = video.play(); if (p && p.catch) p.catch(function () {}); } else video.pause(); }
    ['play', 'pause', 'timeupdate', 'durationchange', 'loadedmetadata', 'volumechange'].forEach(function (ev) { video.addEventListener(ev, sync); });
    play.addEventListener('click', function (e) { e.stopPropagation(); toggle(); });
    video.addEventListener('click', function (e) { e.stopPropagation(); toggle(); });
    mute.addEventListener('click', function (e) { e.stopPropagation(); video.muted = !video.muted; });
    full.addEventListener('click', function (e) { e.stopPropagation(); goFull(host, video); });
    function seekTo(clientX) {
      var r = track.getBoundingClientRect();
      if (video.duration) video.currentTime = Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * video.duration;
    }
    var dragging = false;
    track.addEventListener('pointerdown', function (e) { e.stopPropagation(); dragging = true; track.setPointerCapture(e.pointerId); seekTo(e.clientX); });
    track.addEventListener('pointermove', function (e) { if (dragging) seekTo(e.clientX); });
    track.addEventListener('pointerup', function () { dragging = false; });
    track.addEventListener('click', function (e) { e.stopPropagation(); });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { video.currentTime = Math.min(video.duration || 0, video.currentTime + 5); e.preventDefault(); }
      if (e.key === 'ArrowLeft') { video.currentTime = Math.max(0, video.currentTime - 5); e.preventDefault(); }
    });

    
    video.addEventListener('webkitendfullscreen', sync);
    sync();
  }

  function wire(dm) {
    if (dm.__customVp) return;
    dm.__customVp = true;
    var poster = dm.querySelector('.deferred-media__poster-button:not(.video-interaction-hint)');
    var wantFull = false;
    /* pointerdown, not click: Horizon's delegated on:click handler stops the click
       before a listener here would see it. The tap is still the user gesture that
       full screen needs, as the video is injected within the same activation. */
    if (poster) poster.addEventListener('pointerdown', function () { wantFull = mobile.matches; });
    function check() {
      var v = dm.querySelector('video');
      if (!v) return false;
      build(dm, v);
      if (wantFull) { wantFull = false; goFull(dm, v); }
      return true;
    }
    if (check()) return;
    var mo = new MutationObserver(function () { if (check()) mo.disconnect(); });
    mo.observe(dm, { childList: true, subtree: true });
  }

  /* Drop the mobile peek while a video slide is the current one. */
  function watchSlides(slides) {
    if (slides.__customVa || !('IntersectionObserver' in window)) return;
    slides.__customVa = true;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.intersectionRatio >= 0.6) slides.setAttribute('data-custom-video-active', '');
        else if (en.intersectionRatio < 0.4) slides.removeAttribute('data-custom-video-active');
      });
    }, { root: slides, threshold: [0, 0.4, 0.6, 1] });
    slides.querySelectorAll('.product-media-container--video').forEach(function (s) { io.observe(s); });
  }

  function scan() {
    document.querySelectorAll('main[data-template^="product"] .product-media-container--video deferred-media').forEach(function (dm) {
      if (!dm.closest('.dialog-zoomed-gallery')) wire(dm);
    });
    document.querySelectorAll('main[data-template^="product"] .product-information__media slideshow-slides').forEach(function (s) {
      if (s.querySelector('.product-media-container--video')) watchSlides(s);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  /* Variant changes re-render the gallery section. */
  new MutationObserver(function () { window.clearTimeout(scan.t); scan.t = window.setTimeout(scan, 120); })
    .observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
})();


/* ==========================================================================
   PDP SKU: follow the selected variant
   ==========================================================================
   snippets/custom.pdp-sku.liquid prints the SKU and a variant id → SKU map.
   The variant id is read from the product form's `id` input (and the URL as a
   fallback), so this does not depend on Horizon's event payload. Re-applied on
   Horizon's variant signals and after picker changes, once the re-render lands.
   ========================================================================== */
(function () {
  var box = document.querySelector('[data-custom-sku]');
  if (!box) return;
  var code = box.querySelector('[data-custom-sku-code]');
  var map = {};
  try {
    map = JSON.parse(box.querySelector('[data-custom-sku-map]').textContent);
  } catch (e) {
    return;
  }

  function currentId() {
    var input = document.querySelector(
      'main product-form-component input[name="id"], main form[action*="/cart/add"] input[name="id"]'
    );
    if (input && input.value) return input.value;
    return new URLSearchParams(location.search).get('variant');
  }

  function sync() {
    var id = currentId();
    if (!id || !(id in map)) return;
    var sku = map[id];
    if (code.textContent !== sku) code.textContent = sku;
    box.hidden = !sku;
  }

  document.addEventListener('variant:update', sync);
  document.addEventListener('variantChange', sync);
  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.closest || !e.target.closest('variant-picker')) return;
    setTimeout(sync, 120);
    setTimeout(sync, 500);
  });
  window.addEventListener('popstate', sync);
  sync();
})();

/* ==========================================================================
   PDP: keep the Add to bag label steady across a variant change
   ==========================================================================
   Horizon re-renders the button on every variant change, which strips our
   "— £17.99" price (and any bundle gift tag). The blocks above put them back on
   timers, so for ~300–600ms the label read "ADD TO BAG" and the centred text
   jumped left and back (measured 2026-10-06 on Clear Whey).

   This watches each button and, the moment a re-render removes our nodes,
   re-inserts the last ones it saw. MutationObserver callbacks run before the
   next paint, so the shopper never sees the short label. The timed repaints
   still run and correct the figure if the new variant costs something else;
   that change fades in rather than snapping.

   No feedback loop: re-inserting fires the observer again, but by then nothing
   is missing, so it only takes a fresh snapshot. Attributes are not observed,
   so toggling the fade class does not retrigger it.
   ========================================================================== */
(function () {
  var SEL = "main[data-template^='product'] .add-to-cart-button";
  var KEEP = ['.custom-pdp__atc-price', '.custom-pdp__atc-gifts'];
  var cache = new WeakMap();
  var watched = new WeakSet();

  function place(btn, node) {
    var added = btn.querySelector('.add-to-cart__added');
    if (added && added.parentNode === btn) btn.insertBefore(node, added);
    else btn.appendChild(node);
  }

  function settle(btn) {
    var last = cache.get(btn) || {};
    var next = {};
    var restored = false;
    for (var i = 0; i < KEEP.length; i++) {
      var k = KEEP[i];
      var live = btn.querySelector(k);
      if (!live && last[k]) {
        place(btn, last[k].cloneNode(true));
        restored = true;
      } else if (live) {
        next[k] = live.cloneNode(true);
      }
    }
    if (restored) return; // the re-insert fires the observer again; snapshot then

    var price = btn.querySelector(KEEP[0]);
    var was = last[KEEP[0]] && last[KEEP[0]].getAttribute('data-price');
    if (price && was && price.getAttribute('data-price') !== was) {
      price.classList.remove('is-fresh');
      void price.offsetWidth; // restart the fade if two changes land close together
      price.classList.add('is-fresh');
    }
    cache.set(btn, next);
  }

  function watch() {
    var btns = document.querySelectorAll(SEL);
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      if (watched.has(btn)) continue;
      watched.add(btn);
      settle(btn);
      new MutationObserver(settle.bind(null, btn))
        .observe(btn, { childList: true, subtree: true, characterData: true });
    }
  }

  // Buttons can be swapped wholesale (and the sticky bar mounts late), so look
  // for unwatched ones on Horizon's signals and after picker changes.
  document.addEventListener('variant:update', watch);
  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.closest || !e.target.closest('variant-picker')) return;
    setTimeout(watch, 0);
    setTimeout(watch, 600);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
  setTimeout(watch, 1500);
})();

/* ── Gift stub copy (Gift 5, Ian 2026-10-20) ───────────────────────────────
   Kite's free-gift widget is restyled as a stub under Add to bag (custom.css,
   "Gift 5"). Its wording is Kite's ("£41.91 away to unlock!", "Free gift ready
   to claim", "The gift is now in your cart!"); this swaps it for the design's,
   using only the £ amount Kite printed. Kite re-renders on every cart change,
   so an observer re-applies it; each write is compare-then-set, so it settles. */
(function () {
  var main = document.querySelector('main[data-template^="product"]');
  if (!main) return;
  function setHTML(el, html) { if (el && el.getAttribute('data-w-copy') !== html) { el.innerHTML = html; el.setAttribute('data-w-copy', html); } }
  function apply() {
    main.querySelectorAll('.gfgPageWrapper .gfgFreeGiftSingleOfferSelectDiv').forEach(function (row) {
      var sub = row.querySelector('.gfgFreeGiftMsgSubTitle');
      if (!sub) return;
      var raw = sub.getAttribute('data-w-copy') === sub.innerHTML ? sub.getAttribute('data-w-raw') : sub.textContent;
      if (sub.getAttribute('data-w-copy') !== sub.innerHTML) sub.setAttribute('data-w-raw', raw);
      var ready = row.querySelector('.gfgFreeGiftClaimButton:not(.gfgFreeGiftInvalidProductClaimButton)');
      var amt = (raw || '').match(/£\s?[\d,]+(?:\.\d{2})?/);
      if (ready) setHTML(sub, '<b>Free shaker unlocked</b> · ready to claim');
      else if (amt) setHTML(sub, 'Spend <b>' + amt[0].replace(/\s/g, '') + '</b> more and we’ll add a free shaker');
    });
    main.querySelectorAll('.gfgPageWrapper .gfgFreeGiftSelectDiv .gfgFreeGiftMsgTitle').forEach(function (t) {
      setHTML(t, '<b>Free shaker</b> is in your bag');
    });
  }
  var queued = false;
  function schedule() { if (queued) return; queued = true; requestAnimationFrame(function () { queued = false; apply(); }); }
  function start() {
    apply();
    var box = main.querySelector('.product-details') || main;
    new MutationObserver(schedule).observe(box, { childList: true, subtree: true, characterData: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
